import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import axios from "axios";
import { formatDate, formatDateTime } from "../lib/date";
import { TOKEN_KEY, isTokenExpired, applyAuthToken } from "../lib/authToken";
import { toast } from "sonner";
import {
  LayoutDashboard,
  LayoutGrid,
  Ticket,
  Bell,
  HeartPulse,
  Users,
  Settings,
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Activity,
  TrendingUp,
  LifeBuoy,
  Megaphone,
  Package,
  Shield,
  Layers,

  TrendingDown,
  Filter,
  ExternalLink,
  Monitor,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  ClipboardCheck,
  KeyRound,
  LogOut,
  Building,
  Building2,
  Mic,
  Upload,
  FileAudio,
  Loader2,
  Brain,
  Star,
  ThumbsUp,
  ThumbsDown,
  Minus,
  Sparkles,
  BarChart3,
  Timer,
  UserCheck,
  Zap,
  CircleDot,
  Database,
  Server,
  HardDrive,
  Eye,
  Wifi,
  WifiOff,
  Plus,
  Mail,
  CalendarClock,
  CalendarDays,
  Send,
  X,
  Trash2,
  Target,
  Compass,
  CreditCard,
  Crosshair,
  ListChecks,
  FileCode,
  Play,
  Download,
  Edit,
  Code,
  UserCog,
  FileText,
  BookOpen,
  Pencil,
  UserPlus,
  Globe,
  Share2,
  Radar,
  Network,
  Crown,
  FileCheck,
  Route,
  Inbox,
  ArrowUpRight,
  ArrowDownLeft,
  MessageSquare,
  GraduationCap,
  Lightbulb,
  Link2,
  Copy,
  History,
  Sprout,
  ClipboardList,
  Rows2,
  Rows3,
  Plug,
  ExternalLink as ExternalLinkIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { QueueHealthGauge } from "../components/QueueHealthGauge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import TimesheetsTab from "../components/dashboard/TimesheetsTab";
import AgentWorkHoursConfig from "../components/dashboard/AgentWorkHoursConfig";
import TicketReportsView from "../components/dashboard/TicketReportsView";
import ClientsRegister from "../components/dashboard/clients/ClientsRegister";
import JourneyPage from "../components/dashboard/journey/JourneyPage";
import { CallInsights } from "../components/dashboard/CallInsights";
import CustomerSuccessAtRisk from "../components/dashboard/CustomerSuccessAtRisk";
import SalesProspects from "../components/dashboard/SalesProspects";
import SalesOverview from "../components/dashboard/SalesOverview";
import ProspectEmailTemplates from "../components/dashboard/ProspectEmailTemplates";
import ManagementMeetings from "../components/dashboard/ManagementMeetings";
import CompanyTodos from "../components/dashboard/todos/TodosPage";
import CompanyOverview from "../components/dashboard/CompanyOverview";
import CompanyList from "../components/dashboard/CompanyList";
import DocumentRegistry from "../components/dashboard/DocumentRegistry";
import AcademyManager from "../components/dashboard/AcademyManager";
import ProductLibrary from "../components/dashboard/quotes/ProductLibrary";
import QuoteSettings from "../components/dashboard/quotes/QuoteSettings";
import QuotesOverview from "../components/dashboard/quotes/QuotesOverview";
import NurtureSequences from "../components/dashboard/NurtureSequences";
import MarketingModule from "../components/dashboard/MarketingModule";
import SocialCalendar from "../components/dashboard/social/SocialCalendar";
import OutreachInbox from "../components/dashboard/outreach/OutreachInbox";
import BillingOverview from "../components/dashboard/billing/BillingOverview";
import AIModelSettings from "../components/settings/AIModelSettings";
import HeartbeatSettings from "../components/settings/HeartbeatSettings";
import DigestSendLog from "../components/settings/DigestSendLog";
import IntegrationsKeys from "../components/settings/IntegrationsKeys";
import HubSpotExport from "../components/settings/HubSpotExport";
import AdminUsers from "../components/dashboard/AdminUsers";
import AdminRoles from "../components/dashboard/AdminRoles";
import AdminDataManagement from "../components/dashboard/AdminDataManagement";
import AdminIntegrations from "../components/dashboard/AdminIntegrations";
import RecordView from "./RecordView";
import ClientRecordPage from "../components/dashboard/clients/record/ClientRecordPage";
import HealthChecksPage from "../components/dashboard/healthchecks/HealthChecksPage";
import HealthCheckStaffPage from "../components/dashboard/healthchecks/HealthCheckStaffPage";
import HealthCheckSubmissionPage from "../components/dashboard/healthchecks/HealthCheckSubmissionPage";
import QuoteBuilderPage from "./QuoteBuilderPage";
import QuoteRecordView from "./QuoteRecordView";
import EmailComposePage from "./EmailComposePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const JIRA_URL = "https://equiprsoftware.atlassian.net/browse";

const PRIORITY_COLORS = {
  Highest: "#C23A00",
  High: "#6B5600",
  Medium: "#3B82F6",
  Low: "#0E6B3F",
  Lowest: "#8A857A",
  None: "#8A857A"
};

const STATUS_COLORS = {
  Open: "#3B82F6",
  "In Progress": "#6B5600",
  "Waiting for customer": "#8A857A",
  Resolved: "#0E6B3F",
  Closed: "#B5AFA3"
};

// Agent Mapping Card Component with Persona Profiles - Defined outside Dashboard to prevent remounting on parent re-renders
const AgentMappingCard = () => {
  const [agentMappings, setAgentMappings] = useState([]);
  const [editingAgent, setEditingAgent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedPersona, setExpandedPersona] = useState(null);

  useEffect(() => {
    fetchAgentMappings();
  }, []);

  const fetchAgentMappings = async () => {
    try {
      const res = await axios.get(`${API}/agents/mapping`);
      setAgentMappings(res.data.agents || []);
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to fetch agent mappings:", error);
      setIsLoading(false);
    }
  };

  const handleSaveAgent = async (agent) => {
    try {
      await axios.put(`${API}/agents/mapping/${agent.id}`, agent);
      toast.success(`${agent.name} updated`);
      setEditingAgent(null);
      fetchAgentMappings();
    } catch (error) {
      toast.error("Failed to save agent");
    }
  };

  // Default persona profile template
  const defaultPersona = {
    communication_style: "balanced", // direct, supportive, balanced
    message_length: "moderate", // brief, moderate, detailed
    tone: "professional", // formal, professional, casual
    motivation_triggers: [], // competition, recognition, goals, team_spirit
    prefers_positive_reinforcement: true,
    stress_response: "practical", // encouragement, space, practical
    learning_style: "step_by_step", // step_by_step, big_picture, examples
    feedback_preference: "private", // public, private, written
    custom_notes: ""
  };

  if (isLoading) {
    return (
      <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
        <CardContent className="p-4">
          <RefreshCw className="w-6 h-6 animate-spin text-[#8A6D00] mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
      <CardHeader>
        <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
          <Users className="w-5 h-5" /> Agent Mapping & Personas
        </CardTitle>
        <p className="text-sm text-[#59554D]">Configure agent details and AI persona profiles for personalized motivation</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {agentMappings.map((agent) => (
            <div key={agent.id} className="bg-[#F4F2ED] rounded-lg p-4">
              {editingAgent?.id === agent.id ? (
                <div className="space-y-4">
                  {/* Basic Agent Info */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-[#8A6D00]">Basic Information</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-[#59554D]">Name</Label>
                        <Input
                          value={editingAgent.name}
                          onChange={(e) => setEditingAgent({ ...editingAgent, name: e.target.value })}
                          className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#59554D]">Extension</Label>
                        <Input
                          value={editingAgent.extension}
                          onChange={(e) => setEditingAgent({ ...editingAgent, extension: e.target.value })}
                          className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#59554D]">Email</Label>
                        <Input
                          value={editingAgent.email}
                          onChange={(e) => setEditingAgent({ ...editingAgent, email: e.target.value })}
                          className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#59554D]">Teams Handle</Label>
                        <Input
                          value={editingAgent.teams_handle}
                          onChange={(e) => setEditingAgent({ ...editingAgent, teams_handle: e.target.value })}
                          className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#59554D]">JIRA Display Name</Label>
                        <Input
                          value={editingAgent.jira_display_name}
                          onChange={(e) => setEditingAgent({ ...editingAgent, jira_display_name: e.target.value })}
                          className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#59554D]">Support Dedication %</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="10"
                            value={Math.round((editingAgent.support_weight || 1) * 100)}
                            onChange={(e) => setEditingAgent({ ...editingAgent, support_weight: parseFloat(e.target.value) / 100 })}
                            className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm"
                          />
                          <span className="text-xs text-[#59554D]">%</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-[#59554D]">
                          Role
                          <span className="ml-1 text-[10px] text-[#8A857A]" title="Managers see the full Dashboard + Settings. Agents see the Agent queue.">
                            (ⓘ)
                          </span>
                        </Label>
                        <select
                          value={editingAgent.role || "agent"}
                          onChange={(e) => setEditingAgent({ ...editingAgent, role: e.target.value })}
                          className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-md px-3 py-2 text-[#1A1A1A] text-sm h-9"
                          data-testid={`role-select-${editingAgent.id}`}
                        >
                          <option value="agent">Agent</option>
                          <option value="manager">Manager</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Persona Profile Section */}
                  <div className="space-y-3 pt-3 border-t border-[#DAD5CA]">
                    <h4 className="text-sm font-medium text-[#1D4ED8] flex items-center gap-2">
                      <Brain className="w-4 h-4" /> AI Persona Profile
                    </h4>
                    <p className="text-xs text-[#8A857A]">Configure how AI communicates with this agent</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-[#59554D]">Communication Style</Label>
                        <select
                          value={editingAgent.persona?.communication_style || "balanced"}
                          onChange={(e) => setEditingAgent({ 
                            ...editingAgent, 
                            persona: { ...editingAgent.persona, communication_style: e.target.value }
                          })}
                          className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-md px-3 py-2 text-[#1A1A1A] text-sm"
                        >
                          <option value="direct">Direct & Blunt</option>
                          <option value="supportive">Supportive & Encouraging</option>
                          <option value="balanced">Balanced</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs text-[#59554D]">Message Length</Label>
                        <select
                          value={editingAgent.persona?.message_length || "moderate"}
                          onChange={(e) => setEditingAgent({ 
                            ...editingAgent, 
                            persona: { ...editingAgent.persona, message_length: e.target.value }
                          })}
                          className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-md px-3 py-2 text-[#1A1A1A] text-sm"
                        >
                          <option value="brief">Brief & Concise</option>
                          <option value="moderate">Moderate</option>
                          <option value="detailed">Detailed Explanations</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs text-[#59554D]">Tone</Label>
                        <select
                          value={editingAgent.persona?.tone || "professional"}
                          onChange={(e) => setEditingAgent({ 
                            ...editingAgent, 
                            persona: { ...editingAgent.persona, tone: e.target.value }
                          })}
                          className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-md px-3 py-2 text-[#1A1A1A] text-sm"
                        >
                          <option value="formal">Formal</option>
                          <option value="professional">Professional</option>
                          <option value="casual">Casual & Friendly</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs text-[#59554D]">Stress Response</Label>
                        <select
                          value={editingAgent.persona?.stress_response || "practical"}
                          onChange={(e) => setEditingAgent({ 
                            ...editingAgent, 
                            persona: { ...editingAgent.persona, stress_response: e.target.value }
                          })}
                          className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-md px-3 py-2 text-[#1A1A1A] text-sm"
                        >
                          <option value="encouragement">Extra Encouragement</option>
                          <option value="space">Give Space</option>
                          <option value="practical">Practical Tips</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs text-[#59554D]">Learning Style</Label>
                        <select
                          value={editingAgent.persona?.learning_style || "step_by_step"}
                          onChange={(e) => setEditingAgent({ 
                            ...editingAgent, 
                            persona: { ...editingAgent.persona, learning_style: e.target.value }
                          })}
                          className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-md px-3 py-2 text-[#1A1A1A] text-sm"
                        >
                          <option value="step_by_step">Step-by-Step</option>
                          <option value="big_picture">Big Picture First</option>
                          <option value="examples">Learn by Examples</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs text-[#59554D]">Feedback Preference</Label>
                        <select
                          value={editingAgent.persona?.feedback_preference || "private"}
                          onChange={(e) => setEditingAgent({ 
                            ...editingAgent, 
                            persona: { ...editingAgent.persona, feedback_preference: e.target.value }
                          })}
                          className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-md px-3 py-2 text-[#1A1A1A] text-sm"
                        >
                          <option value="public">Public Recognition</option>
                          <option value="private">Private Acknowledgment</option>
                          <option value="written">Written Feedback</option>
                        </select>
                      </div>
                    </div>

                    {/* Motivation Triggers */}
                    <div>
                      <Label className="text-xs text-[#59554D]">Motivation Triggers (select all that apply)</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {["competition", "recognition", "goals", "team_spirit"].map(trigger => (
                          <button
                            key={trigger}
                            onClick={() => {
                              const currentTriggers = editingAgent.persona?.motivation_triggers || [];
                              const newTriggers = currentTriggers.includes(trigger)
                                ? currentTriggers.filter(t => t !== trigger)
                                : [...currentTriggers, trigger];
                              setEditingAgent({
                                ...editingAgent,
                                persona: { ...editingAgent.persona, motivation_triggers: newTriggers }
                              });
                            }}
                            className={`px-3 py-1 rounded-full text-xs transition-colors ${
                              (editingAgent.persona?.motivation_triggers || []).includes(trigger)
                                ? "bg-[#1D4ED8] text-[#1A1A1A]"
                                : "bg-[#DAD5CA] text-[#59554D] hover:bg-[#C9C3B6]"
                            }`}
                          >
                            {trigger === "competition" ? "🏆 Competition" :
                             trigger === "recognition" ? "⭐ Recognition" :
                             trigger === "goals" ? "🎯 Goals" : "🤝 Team Spirit"}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Preferences */}
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingAgent.persona?.prefers_positive_reinforcement ?? true}
                          onChange={(e) => setEditingAgent({
                            ...editingAgent,
                            persona: { ...editingAgent.persona, prefers_positive_reinforcement: e.target.checked }
                          })}
                          className="rounded border-[#DAD5CA] bg-[#FFFFFF] text-[#8A6D00]"
                        />
                        <span className="text-xs text-[#59554D]">Prefers positive reinforcement over challenges</span>
                      </label>
                    </div>

                    {/* Custom Notes */}
                    <div>
                      <Label className="text-xs text-[#59554D]">Custom Notes for AI</Label>
                      <textarea
                        value={editingAgent.persona?.custom_notes || ""}
                        onChange={(e) => setEditingAgent({
                          ...editingAgent,
                          persona: { ...editingAgent.persona, custom_notes: e.target.value }
                        })}
                        placeholder="Any specific notes about this agent's preferences, sensitivities, or communication needs..."
                        className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-md px-3 py-2 text-[#1A1A1A] text-sm h-20 resize-none"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingAgent(null)}
                      className="border-[#DAD5CA] bg-transparent text-[#1A1A1A]"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleSaveAgent(editingAgent)}
                      className="bg-[#0E6B3F] hover:bg-[#E3F3E9] text-[#1A1A1A]"
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#FFF8D6] flex items-center justify-center text-[#8A6D00] font-medium">
                      {agent.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-[#1A1A1A]">{agent.name}</p>
                        {agent.role === "manager" && (
                          <span
                            className="text-xs bg-[#FFF7D1] px-1.5 py-0.5 rounded text-[#6B5600] font-medium"
                            title="Has manager-level access (Dashboard, Settings, Reports)"
                            data-testid={`role-badge-${agent.id}`}
                          >
                            Manager
                          </span>
                        )}
                        <span className="text-xs bg-[#DAD5CA] px-1.5 py-0.5 rounded text-[#59554D]" title="Support dedication">
                          {Math.round((agent.support_weight || 1) * 100)}%
                        </span>
                        {agent.persona && (
                          <span className="text-xs bg-[#EFF6FF] px-1.5 py-0.5 rounded text-[#1D4ED8]" title="Persona configured">
                            <Brain className="w-3 h-3 inline" /> Persona
                          </span>
                        )}
                      </div>
                      <div className="flex gap-4 text-xs text-[#59554D]">
                        <span>Ext: {agent.extension}</span>
                        <span>{agent.email}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingAgent({ ...agent, persona: agent.persona || {} })}
                    className="border-[#DAD5CA] bg-transparent hover:bg-[#DAD5CA] text-[#1A1A1A]"
                    data-testid={`edit-agent-${agent.id}`}
                  >
                    Edit
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default function Dashboard({ initialTab = "company_overview", recordType = null }) {
  const navigate = useNavigate();
  
  // Check authentication on mount
  useEffect(() => {
    const session = localStorage.getItem("equipr_session");
    const token = localStorage.getItem(TOKEN_KEY);
    if (!session || !token || isTokenExpired(token)) {
      localStorage.removeItem("equipr_session");
      applyAuthToken(null);
      navigate("/login");
      return;
    }
    try {
      const sessionData = JSON.parse(session);
      // Check if session is expired (8 hours)
      if (sessionData.expires && new Date(sessionData.expires) < new Date()) {
        localStorage.removeItem("equipr_session");
        navigate("/login");
        return;
      }
      // Check if user can access the manager dashboard (manager or admin/super user)
      if (sessionData.role !== "manager" && sessionData.role !== "admin") {
        navigate("/agent");
        return;
      }
      setCurrentUser(sessionData);
    } catch (e) {
      localStorage.removeItem("equipr_session");
      navigate("/login");
    }
  }, [navigate]);
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const routeId = useParams().id;
  // When navigating away from a record deep-link via the sidebar, leave the
  // record route so RecordView unmounts and the selected tab renders.
  const selectTab = (tab) => {
    if (recordType) navigate("/");
    setActiveTab(tab);
  };
  const [currentUser, setCurrentUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [rules, setRules] = useState(null);
  const [priorityDist, setPriorityDist] = useState([]);
  const [statusDist, setStatusDist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [ticketFilter, setTicketFilter] = useState("all");
  const [ticketDensity, setTicketDensity] = useState(() => {
    try { return localStorage.getItem("equipr_ticket_density") || "comfortable"; } catch { return "comfortable"; }
  });
  const toggleTicketDensity = (mode) => {
    setTicketDensity(mode);
    try { localStorage.setItem("equipr_ticket_density", mode); } catch {}
  };
  const [callsData, setCallsData] = useState({ calls: [], metrics: {} });
  const [complianceData, setComplianceData] = useState(null);
  const [companyHealth, setCompanyHealth] = useState(null);
  // Customer Success queue counts — same source as the Clients subtitle (Clients directive §4)
  const [csQueue, setCsQueue] = useState(null);
  useEffect(() => {
    if (!["cs_clients", "cs_at_risk", "cs_journey"].includes(activeTab)) return;
    axios.get(`${API}/customer-success/clients/register`, { params: { include_inactive: true } })
      .then((r) => setCsQueue(r.data.summary))
      .catch(() => {});
  }, [activeTab]);

  const [companyPeriod, setCompanyPeriod] = useState("all");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [issuesTab, setIssuesTab] = useState("open"); // "open" | "closed"
  const [hubspotLoading, setHubspotLoading] = useState(false);

  // Lazy-load HubSpot snapshot when a company row is clicked. Keeps the
  // Companies list fast (no upfront enrichment) while still giving managers
  // CRM context in the detail dialog.
  const openCompanyDialog = async (company) => {
    setIssuesTab("open"); // always start on the Open tab
    setSelectedCompany(company);
    if (company.hubspot !== undefined) return;
    setHubspotLoading(true);
    try {
      const res = await axios.get(
        `${API}/integrations/hubspot/company?org=${encodeURIComponent(company.company)}`
      );
      setSelectedCompany((prev) =>
        prev && prev.company === company.company
          ? { ...prev, hubspot: res.data?.company || null }
          : prev
      );
    } catch (e) {
      console.warn("HubSpot lookup failed:", e);
      setSelectedCompany((prev) =>
        prev && prev.company === company.company
          ? { ...prev, hubspot: null }
          : prev
      );
    } finally {
      setHubspotLoading(false);
    }
  };
  const [recordings, setRecordings] = useState([]);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  // Call recording upload dialog state
  const [selectedCall, setSelectedCall] = useState(null);
  const [callUploadDialogOpen, setCallUploadDialogOpen] = useState(false);
  const [uploadingCallRecording, setUploadingCallRecording] = useState(false);
  // Track expanded recordings (for transcription collapse/expand)
  const [expandedRecordings, setExpandedRecordings] = useState({});
  // Edit recording dialog state
  const [editRecordingDialogOpen, setEditRecordingDialogOpen] = useState(false);
  const [editingRecording, setEditingRecording] = useState(null);
  const [savingRecording, setSavingRecording] = useState(false);
  // Analytics state
  const [callVolumeData, setCallVolumeData] = useState(null);
  const [agentPerformance, setAgentPerformance] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [customerIntel, setCustomerIntel] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState("today");
  const [analyticsSubtab, setAnalyticsSubtab] = useState("calls"); // calls | tickets
  // AI Suggestion Feedback state
  const [aiFeedbackData, setAiFeedbackData] = useState(null);
  const [loadingAiFeedback, setLoadingAiFeedback] = useState(false);
  const [aiFeedbackDays, setAiFeedbackDays] = useState(7);
  // Database health monitoring state
  const [databaseHealth, setDatabaseHealth] = useState(null);
  const [databaseLoading, setDatabaseLoading] = useState(false);
  const [addDbDialogOpen, setAddDbDialogOpen] = useState(false);
  const [editDbDialogOpen, setEditDbDialogOpen] = useState(false);
  const [editingDb, setEditingDb] = useState(null);
  const [newDbConfig, setNewDbConfig] = useState({
    client_id: "",
    client_name: "",
    host: "",
    port: 1433,
    database: "",
    username: "",
    password: "",
    company_name: ""
  });
  // Infrastructure Health state (WCF services and Servers)
  const [infrastructureTab, setInfrastructureTab] = useState("databases");
  const [wcfHealth, setWcfHealth] = useState(null);
  const [serverHealth, setServerHealth] = useState(null);
  const [wcfLoading, setWcfLoading] = useState(false);
  const [serverLoading, setServerLoading] = useState(false);
  const [addWcfDialogOpen, setAddWcfDialogOpen] = useState(false);
  const [addServerDialogOpen, setAddServerDialogOpen] = useState(false);
  const [newWcfConfig, setNewWcfConfig] = useState({
    service_id: "",
    client_name: "",
    service_url: "",
    company_name: "",
    auth_required: false,
    timeout_seconds: 10
  });
  const [newServerConfig, setNewServerConfig] = useState({
    server_id: "",
    server_name: "",
    hostname: "",
    server_type: "database",
    company_name: ""
  });
  // Settings Tab state
  const [settingsTab, setSettingsTab] = useState("assignment");
  // Auto-Assignment Rules state
  const [assignmentRules, setAssignmentRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  // Full agent_mapping records (with support_weight + role) — fetched
  // separately because the main `agents` state pulls from
  // /api/agents/workload which doesn't carry those fields.
  const [agentMappingsForCapacity, setAgentMappingsForCapacity] = useState([]);
  // Per-agent inline-edit state for the Support Capacity panel
  const [savingAgentId, setSavingAgentId] = useState(null);
  const [capacityDrafts, setCapacityDrafts] = useState({}); // {agentId: "85"}
  const [editRuleDialogOpen, setEditRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({
    rule_id: "",
    name: "",
    description: "",
    conditions: [{ field: "organization", operator: "equals", value: "" }],
    assign_to: "",
    priority: 50,
    enabled: true
  });
  const [autoAssignRunning, setAutoAssignRunning] = useState(false);
  // Auto-assign Preview (dry-run) state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResults, setPreviewResults] = useState([]);
  const [applyingPreview, setApplyingPreview] = useState(false);
  // Unassigned tickets with suggestions
  const [unassignedWithSuggestions, setUnassignedWithSuggestions] = useState([]);
  const [loadingUnassignedSuggestions, setLoadingUnassignedSuggestions] = useState(false);
  // Escalations state
  const [escalations, setEscalations] = useState([]);
  const [escalationStats, setEscalationStats] = useState(null);
  const [loadingEscalations, setLoadingEscalations] = useState(false);
  const [escalationAgentFilter, setEscalationAgentFilter] = useState("");
  const [escActiveOnly, setEscActiveOnly] = useState(true);
  // Reply-to-escalation dialog (#12)
  const [replyEsc, setReplyEsc] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [replyInternal, setReplyInternal] = useState(false);
  const [replySending, setReplySending] = useState(false);
  // De-escalate dialog (#12)
  const [deEsc, setDeEsc] = useState(null);
  const [deEscStatus, setDeEscStatus] = useState("In Progress");
  const [deEscNote, setDeEscNote] = useState("");
  const [deEscSending, setDeEscSending] = useState(false);
  // Team Workload — Queue Health breakdown + nudge
  const [qhAgent, setQhAgent] = useState(null);        // agent whose breakdown dialog is open
  const [nudgingAgent, setNudgingAgent] = useState(null); // agent.name currently being nudged
  // Reports — manager email recipients
  const [reportRecipients, setReportRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState("");
  const [slaClients, setSlaClients] = useState([]);
  const [newSlaClient, setNewSlaClient] = useState("");
  const [nonSlaClients, setNonSlaClients] = useState([]);
  const [newNonSlaClient, setNewNonSlaClient] = useState("");
  const [reportSettingsLoaded, setReportSettingsLoaded] = useState(false);
  const [savingReportSettings, setSavingReportSettings] = useState(false);
  const [sendingTestReport, setSendingTestReport] = useState(false);
  const [devReportLoading, setDevReportLoading] = useState(false);
  const [devReportSending, setDevReportSending] = useState(false);
  // Script Library state
  const [scripts, setScripts] = useState([]);
  const [scriptCategories, setScriptCategories] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [runScriptDialogOpen, setRunScriptDialogOpen] = useState(false);
  const [scriptResults, setScriptResults] = useState(null);
  const [runningScript, setRunningScript] = useState(false);
  const [selectedDbForScript, setSelectedDbForScript] = useState("");
  const [newScript, setNewScript] = useState({
    script_id: "",
    name: "",
    description: "",
    sql: "",
    permission: "read_only",
    category: "General",
    use_custom_credentials: false,
    custom_username: "",
    custom_password: ""
  });
  // Global elevated SQL credentials
  const [elevatedCredentials, setElevatedCredentials] = useState({
    elevated_sql_username: "",
    has_elevated_credentials: false,
    use_elevated_for_health_checks: false
  });
  const [elevatedPassword, setElevatedPassword] = useState("");
  const [savingCredentials, setSavingCredentials] = useState(false);

  // Client Management state
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);
  const [editClientDialogOpen, setEditClientDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [quickLoginDialogOpen, setQuickLoginDialogOpen] = useState(false);
  const [quickLoginData, setQuickLoginData] = useState(null);
  const [generatingQuickLogin, setGeneratingQuickLogin] = useState(false);
  // VIP client lookup for ticket badges
  const [vipLookup, setVipLookup] = useState({});
  const [newClient, setNewClient] = useState({
    client_id: "",
    company_name: "",
    email: "",
    contact_name: "",
    phone: "",
    jira_org_name: ""
  });
  const [jiraOrganizations, setJiraOrganizations] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, ticketsRes, alertsRes, agentsRes, priorityRes, statusRes, rulesRes, callsRes, complianceRes, companyHealthRes, recordingsRes, dbHealthRes, vipRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/tickets`),
        axios.get(`${API}/alerts?limit=20`),
        axios.get(`${API}/agents/workload`),
        axios.get(`${API}/priority-distribution`),
        axios.get(`${API}/status-distribution`),
        axios.get(`${API}/rules`),
        axios.get(`${API}/calls`),
        axios.get(`${API}/compliance/status`),
        axios.get(`${API}/companies/health?period=${companyPeriod}`),
        axios.get(`${API}/recordings`),
        axios.get(`${API}/databases/health`),
        axios.get(`${API}/clients/vip-lookup`)
      ]);

      setStats(statsRes.data);
      setTickets(ticketsRes.data.tickets || []);
      setAlerts(alertsRes.data.alerts || []);
      setAgents(agentsRes.data.agents || []);
      setRules(rulesRes.data);
      setCallsData(callsRes.data);
      setComplianceData(complianceRes.data);
      setCompanyHealth(companyHealthRes.data);
      setRecordings(recordingsRes.data.recordings || []);
      setDatabaseHealth(dbHealthRes.data);
      setVipLookup(vipRes.data.vip_lookup || {});

      // Transform priority distribution for chart
      const priorityData = Object.entries(priorityRes.data.distribution || {})
        .filter(([_, value]) => value > 0)
        .map(([name, value]) => ({ name, value, fill: PRIORITY_COLORS[name] || "#59554D" }));
      setPriorityDist(priorityData);

      // Transform status distribution for chart
      const statusData = Object.entries(statusRes.data.distribution || {})
        .filter(([_, value]) => value > 0)
        .map(([name, value]) => ({ name, value, fill: STATUS_COLORS[name] || "#59554D" }));
      setStatusDist(statusData);

      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      toast.error("Failed to load dashboard data");
      setLoading(false);
    }
  }, [companyPeriod]);

  useEffect(() => {
    fetchDashboardData();
    fetchScripts();
    fetchElevatedCredentials();
    const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/sync`);
      toast.success("Sync started");
      setTimeout(fetchDashboardData, 3000);
    } catch (error) {
      toast.error("Failed to start sync");
    } finally {
      setSyncing(false);
    }
  };

  // Fetch elevated credentials settings
  const fetchElevatedCredentials = async () => {
    try {
      const res = await axios.get(`${API}/settings/elevated-credentials`);
      setElevatedCredentials(res.data);
    } catch (error) {
      console.error("Failed to fetch elevated credentials:", error);
    }
  };

  // Save elevated credentials
  const saveElevatedCredentials = async () => {
    setSavingCredentials(true);
    try {
      const res = await axios.post(`${API}/settings/elevated-credentials`, {
        username: elevatedCredentials.elevated_sql_username,
        password: elevatedPassword || undefined,
        use_for_health_checks: elevatedCredentials.use_elevated_for_health_checks
      });
      setElevatedCredentials(res.data.settings);
      setElevatedPassword("");
      toast.success("Elevated credentials saved");
    } catch (error) {
      toast.error("Failed to save credentials");
    } finally {
      setSavingCredentials(false);
    }
  };

  // Clear elevated credentials
  const clearElevatedCredentials = async () => {
    try {
      await axios.delete(`${API}/settings/elevated-credentials`);
      setElevatedCredentials({
        elevated_sql_username: "",
        has_elevated_credentials: false,
        use_elevated_for_health_checks: false
      });
      setElevatedPassword("");
      toast.success("Elevated credentials cleared");
    } catch (error) {
      toast.error("Failed to clear credentials");
    }
  };

  // Fetch analytics data
  const fetchAnalytics = useCallback(async (period = analyticsPeriod) => {
    try {
      const [volumeRes, perfRes, customerRes] = await Promise.all([
        axios.get(`${API}/analytics/call-volume?period=${period}`),
        axios.get(`${API}/analytics/agent-performance?period=${period}`),
        axios.get(`${API}/analytics/customer-intelligence?period=${period}`)
      ]);
      
      setCallVolumeData(volumeRes.data);
      setAgentPerformance(perfRes.data);
      setCustomerIntel(customerRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    }
  }, [analyticsPeriod]);

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    setAnalyticsPeriod(newPeriod);
    fetchAnalytics(newPeriod);
  };

  // Fetch AI suggestion feedback data
  const fetchAiFeedback = useCallback(async (days = aiFeedbackDays) => {
    setLoadingAiFeedback(true);
    try {
      const res = await axios.get(`${API}/ai/suggestions/feedback?days=${days}`);
      setAiFeedbackData(res.data);
    } catch (error) {
      console.error("Failed to fetch AI feedback:", error);
    } finally {
      setLoadingAiFeedback(false);
    }
  }, [aiFeedbackDays]);

  // Fetch database health data
  const fetchDatabaseHealth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/databases/health`);
      setDatabaseHealth(res.data);
    } catch (error) {
      console.error("Failed to fetch database health:", error);
    }
  }, []);

  // Fetch WCF service health data
  const fetchWcfHealth = useCallback(async () => {
    setWcfLoading(true);
    try {
      const res = await axios.get(`${API}/wcf/health`);
      setWcfHealth(res.data);
    } catch (error) {
      console.error("Failed to fetch WCF health:", error);
    } finally {
      setWcfLoading(false);
    }
  }, []);

  // Fetch Server health data
  const fetchServerHealth = useCallback(async () => {
    setServerLoading(true);
    try {
      const res = await axios.get(`${API}/servers/health`);
      setServerHealth(res.data);
    } catch (error) {
      console.error("Failed to fetch server health:", error);
    } finally {
      setServerLoading(false);
    }
  }, []);

  // Fetch all infrastructure health
  const fetchInfrastructureHealth = useCallback(async () => {
    await Promise.all([
      fetchDatabaseHealth(),
      fetchWcfHealth(),
      fetchServerHealth()
    ]);
  }, [fetchDatabaseHealth, fetchWcfHealth, fetchServerHealth]);

  // Check all WCF services
  const checkAllWcfServices = async () => {
    setWcfLoading(true);
    try {
      const res = await axios.post(`${API}/wcf/check`);
      setWcfHealth(res.data);
      toast.success(`Checked ${res.data.services?.length || 0} WCF services`);
    } catch (error) {
      toast.error("Failed to check WCF services");
    } finally {
      setWcfLoading(false);
    }
  };

  // Check all servers
  const checkAllServers = async () => {
    setServerLoading(true);
    try {
      const res = await axios.post(`${API}/servers/check`);
      setServerHealth(res.data);
      toast.success(`Checked ${res.data.servers?.length || 0} servers`);
    } catch (error) {
      toast.error("Failed to check servers");
    } finally {
      setServerLoading(false);
    }
  };

  // Add WCF service
  const addWcfService = async () => {
    try {
      await axios.post(`${API}/wcf/configs`, newWcfConfig);
      toast.success("WCF service added successfully");
      setAddWcfDialogOpen(false);
      setNewWcfConfig({ service_id: "", client_name: "", service_url: "", company_name: "", auth_required: false, timeout_seconds: 10 });
      fetchWcfHealth();
    } catch (error) {
      toast.error("Failed to add WCF service");
    }
  };

  // Delete WCF service
  const deleteWcfService = async (serviceId) => {
    if (!confirm("Are you sure you want to remove this WCF service?")) return;
    try {
      await axios.delete(`${API}/wcf/configs/${serviceId}`);
      toast.success("WCF service removed");
      fetchWcfHealth();
    } catch (error) {
      toast.error("Failed to remove WCF service");
    }
  };

  // Add Server
  const addServer = async () => {
    try {
      await axios.post(`${API}/servers/configs`, newServerConfig);
      toast.success("Server added successfully");
      setAddServerDialogOpen(false);
      setNewServerConfig({ server_id: "", server_name: "", hostname: "", server_type: "database", company_name: "" });
      fetchServerHealth();
    } catch (error) {
      toast.error("Failed to add server");
    }
  };

  // Delete Server
  const deleteServer = async (serverId) => {
    if (!confirm("Are you sure you want to remove this server?")) return;
    try {
      await axios.delete(`${API}/servers/configs/${serverId}`);
      toast.success("Server removed");
      fetchServerHealth();
    } catch (error) {
      toast.error("Failed to remove server");
    }
  };

  // Check all databases
  const checkAllDatabases = async () => {
    setDatabaseLoading(true);
    try {
      const res = await axios.post(`${API}/databases/check`);
      setDatabaseHealth(res.data);
      toast.success(`Checked ${res.data.checked} databases`);
    } catch (error) {
      toast.error("Failed to check databases");
    } finally {
      setDatabaseLoading(false);
    }
  };

  // Add new database configuration
  const handleAddDatabase = async () => {
    if (!newDbConfig.client_id || !newDbConfig.host || !newDbConfig.username) {
      toast.error("Please fill in required fields");
      return;
    }
    
    try {
      const res = await axios.post(`${API}/databases/configs`, newDbConfig);
      toast.success(res.data.message);
      setAddDbDialogOpen(false);
      setNewDbConfig({
        client_id: "",
        client_name: "",
        host: "",
        port: 1433,
        database: "",
        username: "",
        password: "",
        company_name: ""
      });
      fetchDatabaseHealth();
    } catch (error) {
      toast.error("Failed to add database");
    }
  };

  // Remove database configuration
  const handleRemoveDatabase = async (clientId) => {
    try {
      await axios.delete(`${API}/databases/configs/${clientId}`);
      toast.success("Database removed");
      fetchDatabaseHealth();
    } catch (error) {
      toast.error("Failed to remove database");
    }
  };

  // Edit database configuration
  const handleEditDatabase = async () => {
    if (!editingDb) return;
    
    try {
      const updates = {};
      if (editingDb.client_name) updates.client_name = editingDb.client_name;
      if (editingDb.host) updates.host = editingDb.host;
      if (editingDb.port) updates.port = editingDb.port;
      if (editingDb.database) updates.database = editingDb.database;
      if (editingDb.username) updates.username = editingDb.username;
      if (editingDb.password) updates.password = editingDb.password;
      updates.company_name = editingDb.company_name || null;
      
      await axios.put(`${API}/databases/configs/${editingDb.client_id}`, updates);
      toast.success("Database updated");
      setEditDbDialogOpen(false);
      setEditingDb(null);
      fetchDatabaseHealth();
    } catch (error) {
      toast.error("Failed to update database");
    }
  };

  // Fetch scripts
  const fetchScripts = async () => {
    try {
      const res = await axios.get(`${API}/scripts`);
      setScripts(res.data.scripts || []);
      setScriptCategories(res.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch scripts:", error);
    }
  };

  // Save script
  const handleSaveScript = async () => {
    if (!newScript.script_id || !newScript.name || !newScript.sql) {
      toast.error("Please fill in required fields");
      return;
    }
    
    try {
      if (selectedScript) {
        await axios.put(`${API}/scripts/${newScript.script_id}`, newScript);
        toast.success("Script updated");
      } else {
        await axios.post(`${API}/scripts`, newScript);
        toast.success("Script created");
      }
      setScriptDialogOpen(false);
      setSelectedScript(null);
      setNewScript({ script_id: "", name: "", description: "", sql: "", permission: "read_only", category: "General", use_custom_credentials: false, custom_username: "", custom_password: "" });
      fetchScripts();
    } catch (error) {
      toast.error("Failed to save script");
    }
  };
  /* eslint-enable */

  // Delete script
  const handleDeleteScript = async (scriptId) => {
    try {
      await axios.delete(`${API}/scripts/${scriptId}`);
      toast.success("Script deleted");
      fetchScripts();
    } catch (error) {
      toast.error("Failed to delete script");
    }
  };

  // Run script
  const handleRunScript = async () => {
    if (!selectedDbForScript || !selectedScript) {
      toast.error("Please select a database and script");
      return;
    }
    
    setRunningScript(true);
    setScriptResults(null);
    
    try {
      const res = await axios.post(`${API}/scripts/execute`, {
        database_id: selectedDbForScript,
        script_id: selectedScript.script_id
      });
      setScriptResults(res.data);
      if (res.data.success) {
        toast.success(`Query completed in ${res.data.execution_time_seconds}s`);
      } else {
        toast.error(res.data.error);
      }
    } catch (error) {
      toast.error("Failed to execute script");
      setScriptResults({ success: false, error: error.message });
    } finally {
      setRunningScript(false);
    }
  };

  // Export results to CSV
  const exportToCsv = () => {
    if (!scriptResults?.rows?.length) return;
    
    const headers = scriptResults.columns.join(",");
    const rows = scriptResults.rows.map(row => 
      scriptResults.columns.map(col => {
        const val = row[col];
        if (val === null || val === undefined) return "";
        const str = String(val);
        return str.includes(",") || str.includes('"') || str.includes("\n") 
          ? `"${str.replace(/"/g, '""')}"` 
          : str;
      }).join(",")
    );
    
    const csv = [headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${scriptResults.script_name}_${scriptResults.database_name}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const generateAiInsights = async () => {
    setLoadingInsights(true);
    try {
      const res = await axios.post(`${API}/analytics/ai-insights`);
      setAiInsights(res.data);
      toast.success("AI insights generated");
    } catch (error) {
      toast.error("Failed to generate AI insights");
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleTestTeams = async () => {
    try {
      const res = await axios.post(`${API}/test-teams`);
      if (res.data.success) {
        toast.success("Test notification sent to Teams");
      } else {
        toast.error("Failed to send test notification");
      }
    } catch (error) {
      toast.error("Failed to send test notification");
    }
  };

  const handleSaveRules = async () => {
    try {
      await axios.put(`${API}/rules`, rules);
      toast.success("Rules saved successfully");
    } catch (error) {
      toast.error("Failed to save rules");
    }
  };

  // Client Management Functions
  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data.clients || []);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    } finally {
      setLoadingClients(false);
    }
  };

  // Fetch JIRA organizations directly from JIRA API
  const fetchJiraOrganizations = async () => {
    try {
      const response = await axios.get(`${API}/organizations`);
      const orgs = (response.data.organizations || []).map(org => org.name).filter(Boolean);
      setJiraOrganizations(orgs);
    } catch (error) {
      console.error("Failed to fetch JIRA organizations:", error);
      // Fallback to company health data if API fails
      if (companyHealth?.companies) {
        const orgs = companyHealth.companies
          .map(c => c.company)
          .filter(Boolean)
          .sort();
        setJiraOrganizations([...new Set(orgs)]);
      }
    }
  };

  useEffect(() => {
    fetchJiraOrganizations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Also update from company health as fallback when it loads
  useEffect(() => {
    if (jiraOrganizations.length === 0 && companyHealth?.companies) {
      const orgs = companyHealth.companies
        .map(c => c.company)
        .filter(Boolean)
        .sort();
      setJiraOrganizations([...new Set(orgs)]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyHealth]);

  const handleCreateClient = async () => {
    if (!newClient.client_id || !newClient.company_name || !newClient.email) {
      toast.error("Client ID, Company Name, and Email are required");
      return;
    }
    
    try {
      await axios.post(`${API}/clients`, newClient);
      toast.success(`Client ${newClient.client_id} created successfully`);
      setAddClientDialogOpen(false);
      setNewClient({
        client_id: "",
        company_name: "",
        email: "",
        contact_name: "",
        phone: "",
        jira_org_name: ""
      });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create client");
    }
  };

  const handleUpdateClient = async () => {
    if (!editingClient) return;
    
    try {
      await axios.put(`${API}/clients/${editingClient.client_id}`, {
        company_name: editingClient.company_name,
        email: editingClient.email,
        contact_name: editingClient.contact_name,
        phone: editingClient.phone,
        jira_org_name: editingClient.jira_org_name,
        is_vip: editingClient.is_vip || false,
        has_sla: editingClient.has_sla || false,
        sla_notes: editingClient.sla_notes || ""
      });
      toast.success(`Client ${editingClient.client_id} updated`);
      setEditClientDialogOpen(false);
      setEditingClient(null);
      fetchClients();
    } catch (error) {
      toast.error("Failed to update client");
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm(`Are you sure you want to delete client ${clientId}?`)) return;
    
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      toast.success(`Client ${clientId} deleted`);
      fetchClients();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const handleGenerateQuickLogin = async (client) => {
    setGeneratingQuickLogin(true);
    try {
      const response = await axios.post(`${API}/client-dashboards/quick-login/generate`, {
        client_id: client.client_id,
        expiry_hours: 24
      });
      
      const baseUrl = window.location.origin;
      const quickLoginUrl = `${baseUrl}/dashboards?token=${response.data.token}`;
      
      setQuickLoginData({
        ...response.data,
        url: quickLoginUrl,
        client: client
      });
      setQuickLoginDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate quick login link");
    } finally {
      setGeneratingQuickLogin(false);
    }
  };

  const copyQuickLoginLink = () => {
    if (quickLoginData?.url) {
      navigator.clipboard.writeText(quickLoginData.url);
      toast.success("Link copied to clipboard!");
    }
  };

  // Fetch clients when Companies tab is viewed
  useEffect(() => {
    if (activeTab === "companies") {
      fetchClients();
    }
  }, [activeTab]);

  // Fetch clients when Settings > Clients tab is viewed
  useEffect(() => {
    if (activeTab === "settings" && settingsTab === "clients") {
      fetchClients();
    }
  }, [activeTab, settingsTab]);

  // Auto-Assignment Rules Functions
  const fetchAssignmentRules = async () => {
    setLoadingRules(true);
    try {
      const response = await axios.get(`${API}/assignment-rules`);
      setAssignmentRules(response.data.rules || []);
    } catch (error) {
      console.error("Failed to fetch assignment rules:", error);
    } finally {
      setLoadingRules(false);
    }
  };

  // Save a single agent's support_weight (called from the Agent Support
  // Capacity panel on the Auto-Assignment tab). Reuses the existing
  // PUT /api/agents/mapping/{id} endpoint so we send the full agent
  // payload, just with support_weight overridden.
  const saveAgentSupportWeight = async (agent, draftPct) => {
    const pct = Math.max(0, Math.min(100, parseInt(draftPct, 10)));
    if (isNaN(pct)) {
      toast.error("Enter a value between 0 and 100");
      return;
    }
    const newWeight = pct / 100;
    // No-op if unchanged (avoids needless PUT)
    if (Math.round((agent.support_weight || 0) * 100) === pct) {
      setCapacityDrafts(prev => {
        const next = { ...prev };
        delete next[agent.id];
        return next;
      });
      return;
    }
    setSavingAgentId(agent.id);
    try {
      await axios.put(`${API}/agents/mapping/${agent.id}`, {
        ...agent,
        support_weight: newWeight,
      });
      // Patch the local capacity-panel state so the % refreshes immediately
      // without a full dashboard refetch.
      setAgentMappingsForCapacity(prev =>
        prev.map(a => (a.id === agent.id ? { ...a, support_weight: newWeight } : a))
      );
      setCapacityDrafts(prev => {
        const next = { ...prev };
        delete next[agent.id];
        return next;
      });
      toast.success(`${agent.name}'s support capacity set to ${pct}%`);
    } catch (error) {
      console.error("Failed to update support weight:", error);
      toast.error("Failed to save support capacity");
    } finally {
      setSavingAgentId(null);
    }
  };

  const handleCreateRule = async () => {
    if (!newRule.rule_id || !newRule.name || !newRule.assign_to) {
      toast.error("Rule ID, Name, and Assign To are required");
      return;
    }
    
    // Filter out empty conditions
    const validConditions = newRule.conditions.filter(c => c.value.trim());
    if (validConditions.length === 0) {
      toast.error("At least one condition is required");
      return;
    }
    
    try {
      await axios.post(`${API}/assignment-rules`, {
        ...newRule,
        conditions: validConditions
      });
      toast.success(`Rule "${newRule.name}" created`);
      setEditRuleDialogOpen(false);
      setNewRule({
        rule_id: "",
        name: "",
        description: "",
        conditions: [{ field: "organization", operator: "equals", value: "" }],
        assign_to: "",
        priority: 50,
        enabled: true
      });
      fetchAssignmentRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create rule");
    }
  };

  const handleUpdateRule = async () => {
    if (!editingRule) return;
    
    // Filter out empty conditions
    const validConditions = editingRule.conditions.filter(c => c.value.trim());
    if (validConditions.length === 0) {
      toast.error("At least one condition is required");
      return;
    }
    
    try {
      await axios.put(`${API}/assignment-rules/${editingRule.rule_id}`, {
        ...editingRule,
        conditions: validConditions
      });
      toast.success(`Rule "${editingRule.name}" updated`);
      setEditRuleDialogOpen(false);
      setEditingRule(null);
      fetchAssignmentRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update rule");
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!confirm("Are you sure you want to delete this rule?")) return;
    
    try {
      await axios.delete(`${API}/assignment-rules/${ruleId}`);
      toast.success("Rule deleted");
      fetchAssignmentRules();
    } catch (error) {
      toast.error("Failed to delete rule");
    }
  };

  const handleRunAutoAssign = async () => {
    setAutoAssignRunning(true);
    try {
      const response = await axios.post(`${API}/tickets/auto-assign`);
      const { assigned_count, results } = response.data;
      
      if (assigned_count > 0) {
        toast.success(`Auto-assigned ${assigned_count} ticket(s)`);
        fetchDashboardData(); // Refresh tickets
        fetchUnassignedWithSuggestions(); // Refresh suggestions
      } else {
        toast.info("No unassigned tickets to process");
      }
    } catch (error) {
      toast.error("Failed to run auto-assignment");
    } finally {
      setAutoAssignRunning(false);
    }
  };

  // Preview (dry-run) auto-assign: show what would happen, no JIRA mutation.
  const handlePreviewAutoAssign = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const response = await axios.post(`${API}/tickets/auto-assign?dry_run=true`);
      setPreviewResults(response.data.results || []);
    } catch (error) {
      toast.error("Failed to preview auto-assignment");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Apply the previewed assignments by running the live (non-dry-run) endpoint.
  const handleApplyPreview = async () => {
    setApplyingPreview(true);
    try {
      const response = await axios.post(`${API}/tickets/auto-assign`);
      const { assigned_count } = response.data;
      toast.success(`Applied — ${assigned_count} ticket(s) assigned in JIRA`);
      setPreviewOpen(false);
      setPreviewResults([]);
      fetchDashboardData();
      fetchUnassignedWithSuggestions();
    } catch (error) {
      toast.error("Failed to apply auto-assignment");
    } finally {
      setApplyingPreview(false);
    }
  };

  // Fetch unassigned tickets with assignment suggestions
  const fetchUnassignedWithSuggestions = async () => {
    setLoadingUnassignedSuggestions(true);
    try {
      const response = await axios.get(`${API}/tickets/unassigned-with-suggestions`);
      setUnassignedWithSuggestions(response.data.tickets || []);
    } catch (error) {
      console.error("Failed to fetch unassigned tickets:", error);
    } finally {
      setLoadingUnassignedSuggestions(false);
    }
  };

  // Fetch unassigned tickets on overview or tickets tab
  useEffect(() => {
    if (activeTab === "overview" || activeTab === "tickets") {
      fetchUnassignedWithSuggestions();
    }
  }, [activeTab]);

  // Escalations functions
  const fetchEscalations = async () => {
    setLoadingEscalations(true);
    try {
      const [escalationsRes, statsRes] = await Promise.all([
        axios.get(`${API}/escalations`, { params: { limit: 1000, days: 3650, agent: escalationAgentFilter || undefined, active_only: escActiveOnly } }),
        axios.get(`${API}/escalations/stats`, { params: { days: 3650 } })
      ]);
      setEscalations(escalationsRes.data.escalations || []);
      setEscalationStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch escalations:", error);
    } finally {
      setLoadingEscalations(false);
    }
  };

  const deleteEscalation = async (escalationId) => {
    if (!window.confirm("Are you sure you want to delete this escalation record?")) return;
    try {
      await axios.delete(`${API}/escalations/${escalationId}`);
      toast.success("Escalation deleted");
      fetchEscalations();
    } catch (error) {
      console.error("Failed to delete escalation:", error);
      toast.error("Failed to delete escalation");
    }
  };

  const getReviewerName = () => {
    try {
      return JSON.parse(localStorage.getItem("equipr_session") || "{}").name || "Manager";
    } catch (e) {
      return "Manager";
    }
  };

  // Nudge an agent about their decaying queue health (Teams message)
  const nudgeAgent = async (agent) => {
    setNudgingAgent(agent.name);
    try {
      await axios.post(`${API}/agents/nudge`, {
        agent_name: agent.name,
        agent_email: agent.email || undefined,
        manager_name: getReviewerName(),
      });
      toast.success(`Nudge sent to ${agent.name} via Teams`);
    } catch (error) {
      console.error("Failed to nudge agent:", error);
      toast.error(error.response?.data?.detail || "Failed to send nudge");
    } finally {
      setNudgingAgent(null);
    }
  };

  // #12 — Direct reply on an escalated ticket (public to customer or internal note)
  const submitEscalationReply = async () => {
    if (!replyEsc || !replyMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }
    setReplySending(true);
    try {
      await axios.post(`${API}/tickets/${replyEsc.ticket_key}/escalation/reply`, {
        reviewer_name: getReviewerName(),
        message: replyMessage.trim(),
        internal: replyInternal,
      });
      toast.success(replyInternal ? "Internal note added" : "Reply sent to customer");
      setReplyEsc(null);
      setReplyMessage("");
      setReplyInternal(false);
      fetchEscalations();
    } catch (error) {
      console.error("Failed to reply to escalation:", error);
      toast.error(error.response?.data?.detail || "Failed to send reply");
    } finally {
      setReplySending(false);
    }
  };

  // #12 — Move a ticket out of the escalated status
  const submitDeEscalate = async () => {
    if (!deEsc) return;
    setDeEscSending(true);
    try {
      const res = await axios.post(`${API}/tickets/${deEsc.ticket_key}/de-escalate`, {
        reviewer_name: getReviewerName(),
        target_status: deEscStatus || undefined,
        note: deEscNote.trim() || undefined,
      });
      if (res.data.success) {
        toast.success(`${deEsc.ticket_key} moved to ${res.data.new_status}`);
      } else {
        toast.warning(res.data.error || "Marked reviewed; please change status in JIRA manually.");
      }
      setDeEsc(null);
      setDeEscNote("");
      setDeEscStatus("In Progress");
      fetchEscalations();
    } catch (error) {
      console.error("Failed to de-escalate:", error);
      toast.error(error.response?.data?.detail || "Failed to move ticket out of escalated");
    } finally {
      setDeEscSending(false);
    }
  };

  // Fetch escalations when viewing escalations tab
  useEffect(() => {
    if (activeTab === "escalations") {
      fetchEscalations();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, escalationAgentFilter, escActiveOnly]);

  // Fetch rules when Settings > Auto-Assignment tab is viewed
  useEffect(() => {
    if (activeTab === "settings" && settingsTab === "assignment") {
      fetchAssignmentRules();
      // Load full agent mappings so the Capacity panel has support_weight + role
      axios.get(`${API}/agents/mapping`)
        .then(res => setAgentMappingsForCapacity(res.data.agents || []))
        .catch(err => console.error("Failed to fetch agent mappings for capacity:", err));
    }
  }, [activeTab, settingsTab]);

  // Load report recipients when Settings > Reports tab is viewed
  const fetchReportSettings = async () => {
    try {
      const res = await axios.get(`${API}/reports/settings`);
      setReportRecipients(res.data.manager_emails || []);
      setSlaClients(res.data.sla_clients || []);
      setNonSlaClients(res.data.non_sla_clients || []);
      setReportSettingsLoaded(true);
    } catch (error) {
      console.error("Failed to load report settings:", error);
    }
  };

  const saveReportSettings = async (recipients) => {
    setSavingReportSettings(true);
    try {
      const res = await axios.post(`${API}/reports/settings`, {
        manager_emails: recipients,
        enabled: true,
        send_day: "monday",
        send_hour: 7,
      });
      setReportRecipients(res.data.settings.manager_emails || []);
      toast.success("Report recipients saved");
    } catch (error) {
      console.error("Failed to save report settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save recipients");
    } finally {
      setSavingReportSettings(false);
    }
  };

  const addRecipient = () => {
    const email = newRecipient.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (reportRecipients.includes(email)) {
      toast.error("That email is already a recipient");
      return;
    }
    const updated = [...reportRecipients, email];
    setNewRecipient("");
    saveReportSettings(updated);
  };

  const removeRecipient = (email) => {
    saveReportSettings(reportRecipients.filter((e) => e !== email));
  };

  const saveSlaClients = async (clients) => {
    setSavingReportSettings(true);
    try {
      const res = await axios.post(`${API}/reports/settings`, {
        manager_emails: reportRecipients,
        enabled: true,
        send_day: "monday",
        send_hour: 7,
        sla_clients: clients,
      });
      setSlaClients(res.data.settings.sla_clients || []);
      toast.success("SLA clients saved");
    } catch (error) {
      console.error("Failed to save SLA clients:", error);
      toast.error(error.response?.data?.detail || "Failed to save SLA clients");
    } finally {
      setSavingReportSettings(false);
    }
  };

  const addSlaClient = () => {
    const name = newSlaClient.trim();
    if (!name) return;
    if (slaClients.some((c) => c.toLowerCase() === name.toLowerCase())) {
      toast.error("That client is already in the SLA list");
      return;
    }
    setNewSlaClient("");
    saveSlaClients([...slaClients, name]);
  };

  const removeSlaClient = (name) => {
    saveSlaClients(slaClients.filter((c) => c !== name));
  };

  const saveNonSlaClients = async (clients) => {
    setSavingReportSettings(true);
    try {
      const res = await axios.post(`${API}/reports/settings`, {
        manager_emails: reportRecipients,
        enabled: true,
        send_day: "monday",
        send_hour: 7,
        non_sla_clients: clients,
      });
      setNonSlaClients(res.data.settings.non_sla_clients || []);
      toast.success("Non-SLA clients saved");
    } catch (error) {
      console.error("Failed to save Non-SLA clients:", error);
      toast.error(error.response?.data?.detail || "Failed to save Non-SLA clients");
    } finally {
      setSavingReportSettings(false);
    }
  };

  const addNonSlaClient = () => {
    const name = newNonSlaClient.trim();
    if (!name) return;
    if (nonSlaClients.some((c) => c.toLowerCase() === name.toLowerCase())) {
      toast.error("That client is already in the Non-SLA list");
      return;
    }
    setNewNonSlaClient("");
    saveNonSlaClients([...nonSlaClients, name]);
  };

  const removeNonSlaClient = (name) => {
    saveNonSlaClients(nonSlaClients.filter((c) => c !== name));
  };

  // Send the weekly manager report to the saved recipients right now (on demand)
  const sendTestReport = async () => {
    if (reportRecipients.length === 0) {
      toast.error("Add at least one recipient first");
      return;
    }
    setSendingTestReport(true);
    try {
      const res = await axios.post(`${API}/reports/manager-weekly`, { send_email: true });
      const results = res.data.results || [];
      const sent = results.filter((r) => r.status === "success").length;
      const skipped = results.filter((r) => r.status === "skipped").length;
      const failed = results.filter((r) => r.status === "error").length;
      if (sent > 0) {
        toast.success(`Weekly report emailed to ${sent} recipient${sent > 1 ? "s" : ""}`);
      } else if (skipped > 0) {
        toast.warning("Report generated, but email isn't configured (RESEND_API_KEY missing on the backend).");
      } else if (failed > 0) {
        toast.error(`Failed to send to ${failed} recipient${failed > 1 ? "s" : ""}.`);
      } else {
        toast.success("Report send triggered");
      }
    } catch (error) {
      console.error("Failed to send test report:", error);
      toast.error(error.response?.data?.detail || "Failed to send report");
    } finally {
      setSendingTestReport(false);
    }
  };

  useEffect(() => {
    if (activeTab === "settings" && settingsTab === "reports" && !reportSettingsLoaded) {
      fetchReportSettings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, settingsTab, reportSettingsLoaded]);

  const handleLogout = () => {
    localStorage.removeItem("equipr_session");
    toast.success("Logged out successfully");
    navigate("/login");
  };

  // Impersonate an agent - allows manager to view agent dashboard as that agent
  const impersonateAgent = (agent) => {
    console.log("impersonateAgent called with:", agent);
    
    // Get current manager session
    const currentSession = JSON.parse(localStorage.getItem("equipr_session") || "{}");
    console.log("Current session:", currentSession);
    
    // Create impersonation session
    const impersonationSession = {
      id: agent.name.toLowerCase().replace(/\s+/g, '_'),
      name: agent.name,
      email: agent.email || "",
      extension: "",
      jira_display_name: agent.name,
      teams_handle: "",
      role: "agent",
      // Store original manager session for returning
      impersonating: true,
      originalManager: {
        id: currentSession.id,
        name: currentSession.name,
        email: currentSession.email,
        role: currentSession.role,
        expiresAt: currentSession.expiresAt
      },
      expires: currentSession.expires,
      expiresAt: currentSession.expiresAt
    };
    
    console.log("Impersonation session:", impersonationSession);
    localStorage.setItem("equipr_session", JSON.stringify(impersonationSession));
    toast.success(`Viewing as ${agent.name}`);
    navigate("/agent");
  };

  // Statuses to exclude from ticket list (resolved tickets)
  const resolvedStatuses = ["resolved", "closed", "done", "cancelled", "canceled", "resolved without email", "resolved with email"];

  const filteredTickets = tickets.filter(t => {
    // First, exclude resolved tickets
    const status = (t.status || "").toLowerCase();
    if (resolvedStatuses.includes(status)) return false;
    
    // Then apply user filter
    if (ticketFilter === "all") return true;
    if (ticketFilter === "idle") return t.idle_hours >= (rules?.idle_warning_hours || 4);
    if (ticketFilter === "sla") return t.sla_risk || t.sla_breached;
    if (ticketFilter === "high") return t.priority === "High" || t.priority === "Highest";
    if (ticketFilter === "unassigned") return !t.assignee;
    return true;
  });

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const MetricCard = ({ label, value, icon: Icon, color = "#1A1A1A", subtext, onClick }) => (
    <Card 
      className="metric-card bg-[#FFFFFF] border-[#DAD5CA] cursor-pointer hover:border-[#C9C3B6] transition-all duration-150"
      onClick={onClick}
      data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="metric-label mb-2">{label}</p>
            <p className="metric-value" style={{ color }}>{value}</p>
            {subtext && <p className="text-xs text-[#59554D] mt-1">{subtext}</p>}
          </div>
          {Icon && <Icon className="w-5 h-5 text-[#59554D]" />}
        </div>
      </CardContent>
    </Card>
  );

  const PriorityBadge = ({ priority }) => {
    const colors = {
      Highest: "bg-[#FFE9E0] text-[#C23A00]",
      High: "bg-[#FFF7D1] text-[#6B5600]",
      Medium: "bg-[#FFF8D6] text-[#8A6D00]",
      Low: "bg-[#E3F3E9] text-[#0E6B3F]",
      Lowest: "bg-[#EFF6FF] text-[#3B82F6]"
    };
    return (
      <span className={`priority-badge ${colors[priority] || "bg-[#DAD5CA] text-[#59554D]"}`}>
        {priority || "None"}
      </span>
    );
  };

  const SLABadge = ({ ticket }) => {
    if (ticket.sla_breached) {
      return (
        <span className="sla-badge sla-breached pulse-danger">
          <span className="status-dot danger" /> Breached
        </span>
      );
    }
    if (ticket.sla_risk) {
      return (
        <span className="sla-badge sla-at-risk">
          <span className="status-dot warning" /> At Risk
        </span>
      );
    }
    if (ticket.due_date) {
      return (
        <span className="inline-flex items-center gap-1.5 text-[11px] text-[#8A857A]" title="Within SLA">
          <span className="status-dot success" /> On track
        </span>
      );
    }
    return <span className="text-xs text-[#59554D]">No Due Date</span>;
  };

  // On record-view deep-links (/clients/:id, /prospects/:id) don't block the shell
  // behind the heavy dashboard boot fetch — mount the shell + RecordView immediately
  // while dashboard stats (sidebar badges, etc.) keep loading in the background.
  if (loading && !recordType) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#EDEAE3]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-[#8A6D00] animate-spin mx-auto mb-4" />
          <p className="text-[#59554D]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EDEAE3]">
      {/* Header */}
      <header className="app-header flex items-center justify-between overflow-x-auto" data-testid="header">
        <div className="flex items-center gap-4 shrink-0">
          <h1 className="heading text-2xl tracking-tight"><span className="text-[#F5F5F5]">EQUIPR</span> <span className="text-[#FFD108]">INTELLIGENCE</span></h1>
          <Badge variant="outline" className="border-[#333333] text-[#8A8A8A] text-xs">
            MVP
          </Badge>
        </div>
        <div className="flex items-center gap-3 shrink-0 pl-4">
          <Popover>
            <PopoverTrigger asChild>
              <button className="relative p-2 rounded hover:bg-[#1E1E1E] text-[#B8B4AC] hover:text-[#FFFFFF] transition-colors" data-testid="notifications-btn">
                <Bell className="w-5 h-5" />
                {alerts.filter(a => !a.acknowledged).length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#FF4F00] ring-[1.5px] ring-[#121212]" data-testid="notifications-badge" />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96 p-0 bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]" data-testid="notifications-panel">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#DAD5CA]">
                <span className="font-medium text-[#1A1A1A] flex items-center gap-2"><Bell className="w-4 h-4" /> Notifications</span>
                <Badge variant="outline" className="border-[#DAD5CA] text-[#59554D] text-xs">{alerts.length}</Badge>
              </div>
              <ScrollArea className="h-[360px]">
                {alerts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-14 text-[#8A857A]">
                    <Bell className="w-8 h-8 mb-2" />
                    <p className="text-sm">No notifications</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#F4F2ED]">
                    {alerts.map((alert, idx) => (
                      <div key={idx} className="px-4 py-3 hover:bg-[#F4F2ED] transition-colors" data-testid={`notification-item-${idx}`}>
                        <div className="flex items-start gap-3">
                          <span className={`status-dot mt-1 ${alert.severity === 'critical' ? 'danger' : alert.severity === 'warning' ? 'warning' : 'info'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              {alert.ticket_key && (
                                <a href={`${JIRA_URL}/${alert.ticket_key}`} target="_blank" rel="noopener noreferrer" className="mono text-xs text-[#8A6D00] hover:underline">{alert.ticket_key}</a>
                              )}
                              <Badge className={`text-[10px] ${alert.severity === 'critical' ? 'bg-[#FFE9E0] text-[#C23A00]' : alert.severity === 'warning' ? 'bg-[#FFF7D1] text-[#6B5600]' : 'bg-[#FFF8D6] text-[#8A6D00]'}`}>{alert.alert_type}</Badge>
                            </div>
                            <p className="text-sm text-[#1A1A1A] break-words">{alert.message}</p>
                            <p className="text-xs text-[#8A857A] mt-0.5">{formatTimeAgo(alert.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </PopoverContent>
          </Popover>
          {/* Segmented control — Agent View / Command Centre */}
          <a
            href="/my-work"
            target="equipr-my-work"
            rel="noopener"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors bg-[#1C1C1C] text-[#B8B4AC] hover:text-[#FFFFFF]"
            data-testid="my-work-btn"
            title="Opens My Work in its own tab"
          >
            <ClipboardCheck className="w-4 h-4" />
            My Work
          </a>
          <div className="flex items-center bg-[#1C1C1C] rounded-full p-0.5" data-testid="view-segment">
            <Link
              to="/agent"
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[#B8B4AC] hover:text-[#FFFFFF] text-sm font-medium transition-colors"
              data-testid="switch-to-agent-btn"
            >
              <UserCog className="w-4 h-4" />
              Agent View
            </Link>
            <Link
              to="/command-centre"
              className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFD108] text-[#000000] text-sm font-bold transition-colors"
              data-testid="command-centre-btn"
            >
              <Monitor className="w-4 h-4" />
              Command Centre
            </Link>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#8A8A8A]">
            <span className={`status-dot ${stats?.sync_status === 'success' ? 'success' : stats?.sync_status === 'syncing' ? 'info' : 'warning'}`} />
            {stats?.last_sync ? `Last sync: ${formatTimeAgo(stats.last_sync)}` : "Not synced"}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={syncing || stats?.sync_status === 'syncing'}
            className="border-[#333333] bg-transparent hover:bg-[#1E1E1E] text-[#B8B4AC] hover:text-[#FFFFFF]"
            data-testid="sync-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing || stats?.sync_status === 'syncing' ? 'animate-spin' : ''}`} />
            Sync Now
          </Button>
          <div className="w-[30px] h-[30px] rounded-full bg-[#FFD108] flex items-center justify-center text-[#000000] text-xs font-bold" title={currentUser?.name} data-testid="header-avatar">
            {(currentUser?.name || "A").split(' ').map(n => n[0]).join('').slice(0, 2)}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="border-[#333333] bg-transparent hover:bg-[#1E1E1E] text-[#8A8A8A] hover:text-[#FFFFFF]"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="sidebar hidden md:block" data-testid="sidebar">
          <nav className="py-4">
            {/* Platform modules (EQUIPR Intelligence) */}
            <div className="px-4 pt-1 pb-2 text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#6E6E6E]">Platform</div>
            {(() => {
              const CS_TABS = ["cs_clients", "cs_at_risk", "cs_journey", "cs_health_checks"];
              const COMPANY_TABS = ["company_overview", "company_meetings", "company_todos", "company_strategy", "company_goals", "company_focus", "company_documents", "company_billing"];
              const SALES_TABS = ["sales_overview", "sales_prospects", "sales_outreach", "sales_nurture", "sales_templates", "sales_quotes", "sales_products", "sales_quote_settings"];
              const MARKETING_TABS = ["marketing", "marketing_calendar", "marketing_social", "marketing_ai", "marketing_spy"];
              const ADMIN_TABS = ["administration", "admin_users", "admin_roles", "admin_data", "admin_integrations", "timesheets", "analytics", "settings"];
              const activeModule = ADMIN_TABS.includes(activeTab)
                ? "administration"
                : activeTab === "academy" ? "academy"
                : COMPANY_TABS.includes(activeTab) ? "company"
                : CS_TABS.includes(activeTab) ? "customer_success"
                : SALES_TABS.includes(activeTab) ? "sales"
                : MARKETING_TABS.includes(activeTab) ? "marketing" : "support";
              return [
                { id: "company", label: "Company", icon: Building2, tab: "company_overview" },
                { id: "customer_success", label: "Customer Success", icon: HeartPulse, tab: "cs_clients" },
                { id: "support", label: "Support", icon: LifeBuoy, tab: "overview" },
                { id: "sales", label: "Sales", icon: TrendingUp, tab: "sales_overview" },
                { id: "marketing", label: "Marketing", icon: Megaphone, tab: "marketing_calendar" },
                ...(currentUser?.role === "admin" ? [{ id: "academy", label: "Academy", icon: GraduationCap, tab: "academy" }] : []),
                { id: "product", label: "Product", icon: Package, soon: true },
                { id: "development", label: "Development", icon: Code, soon: true },
                { id: "administration", label: "Administration", icon: Shield, tab: "admin_users" },
              ].map(m => {
                const isActive = !m.soon && m.id === activeModule;
                return (
                  <div
                    key={m.id}
                    className={`sidebar-item ${isActive ? 'active' : ''} ${m.soon ? 'opacity-40 cursor-not-allowed' : ''}`}
                    onClick={() => { if (m.soon) return; selectTab(m.tab); }}
                    data-testid={`module-${m.id}`}
                  >
                    <m.icon className="w-5 h-5" />
                    <span className="flex-1">{m.label}</span>
                    {m.soon && (
                      <Badge variant="outline" className="border-[#DAD5CA] text-[#8A857A] text-[9px] px-1 py-0">Soon</Badge>
                    )}
                  </div>
                );
              });
            })()}

            {/* Customer Success module sub-navigation */}
            {["cs_clients", "cs_at_risk", "cs_journey", "cs_health_checks"].includes(activeTab) && (
              <>
                <div className="px-4 pt-4 pb-2 text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#6E6E6E]">Customer Success</div>
                {[
                  { id: "cs_clients", label: "Clients", icon: Users,
                    count: csQueue?.checks_overdue, tone: { color: "#FF7A3D", background: "#3A1405" } },
                  { id: "cs_at_risk", label: "At-Risk", icon: AlertTriangle,
                    count: csQueue?.below_7, tone: { color: "#FFD108", background: "#3A3006" } },
                  { id: "cs_journey", label: "Journey", icon: Route },
                  { id: "cs_health_checks", label: "Health Checks", icon: HeartPulse },
                ].map(item => (
                  <div
                    key={item.id}
                    className={`sidebar-item ${activeTab === item.id ? 'active' : ''}`}
                    onClick={() => selectTab(item.id)}
                    data-testid={`nav-${item.id}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1">{item.label}</span>
                    {item.count > 0 && (
                      <span className="mono text-[10.5px] font-bold px-1.5 rounded" style={item.tone}
                            data-testid={`nav-${item.id}-count`}>{item.count}</span>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* Company (EQUIPR) module sub-navigation */}
            {["company_overview", "company_meetings", "company_todos", "company_strategy", "company_goals", "company_focus", "company_documents", "company_billing"].includes(activeTab) && (
              <>
                <div className="px-4 pt-4 pb-2 text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#6E6E6E]">Company</div>
                {[
                  { id: "company_overview", label: "Overview", icon: LayoutGrid },
                  { id: "company_todos", label: "To-dos", icon: ListChecks },
                  { id: "company_meetings", label: "Meetings", icon: ClipboardList },
                  { id: "company_documents", label: "Documents", icon: FileText },
                  { id: "company_billing", label: "Billing", icon: CreditCard },
                  { id: "company_strategy", label: "Strategy", icon: Compass },
                  { id: "company_goals", label: "Goals", icon: Target },
                  { id: "company_focus", label: "Focus", icon: Crosshair },
                ].map(item => (
                  <div
                    key={item.id}
                    className={`sidebar-item ${activeTab === item.id ? 'active' : ''}`}
                    onClick={() => selectTab(item.id)}
                    data-testid={`nav-${item.id}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1">{item.label}</span>
                  </div>
                ))}
              </>
            )}

            {/* Sales module sub-navigation */}
            {["sales_overview", "sales_prospects", "sales_outreach", "sales_nurture", "sales_templates", "sales_quotes", "sales_products", "sales_quote_settings"].includes(activeTab) && (
              <>
                <div className="px-4 pt-4 pb-2 text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#6E6E6E]">Sales</div>
                {[
                  { id: "sales_overview", label: "Overview", icon: BarChart3 },
                  { id: "sales_prospects", label: "Prospects", icon: TrendingUp },
                  { id: "sales_outreach", label: "Outreach", icon: Mail },
                  { id: "sales_quotes", label: "Quotes", icon: FileText },
                  { id: "sales_nurture", label: "Nurture", icon: Sprout },
                  { id: "sales_templates", label: "Email Templates", icon: Mail },
                  { id: "sales_products", label: "Product Library", icon: Package },
                  { id: "sales_quote_settings", label: "Quote Settings", icon: Settings },
                ].map(item => (
                  <div
                    key={item.id}
                    className={`sidebar-item ${activeTab === item.id ? 'active' : ''}`}
                    onClick={() => selectTab(item.id)}
                    data-testid={`nav-${item.id}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1">{item.label}</span>
                    {item.badge > 0 && (
                      <Badge className="bg-[#C23A00] text-[#1A1A1A] text-xs px-1.5">{item.badge}</Badge>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* Marketing module sub-navigation */}
            {["marketing", "marketing_calendar", "marketing_social", "marketing_ai", "marketing_spy"].includes(activeTab) && (
              <>
                <div className="px-4 pt-4 pb-2 text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#6E6E6E]">Marketing</div>
                {[
                  { id: "marketing_calendar", label: "Social Calendar", icon: CalendarDays },
                  { id: "marketing", label: "Overview", icon: BarChart3 },
                  { id: "marketing_social", label: "Social & Ads", icon: Share2 },
                  { id: "marketing_ai", label: "AI Visibility", icon: Sparkles },
                  { id: "marketing_spy", label: "Competitor Spy", icon: Radar },
                ].map(item => (
                  <div
                    key={item.id}
                    className={`sidebar-item ${activeTab === item.id ? 'active' : ''}`}
                    onClick={() => selectTab(item.id)}
                    data-testid={`nav-${item.id}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1">{item.label}</span>
                  </div>
                ))}
              </>
            )}

            {/* Administration module sub-navigation */}
            {["administration", "admin_users", "admin_roles", "admin_data", "admin_integrations", "timesheets", "analytics", "settings"].includes(activeTab) && (
              <>
                <div className="px-4 pt-4 pb-2 text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#6E6E6E]">Administration</div>
                {[
                  { id: "admin_users", label: "Users", icon: Users },
                  { id: "admin_roles", label: "Roles & Permissions", icon: KeyRound },
                  { id: "admin_data", label: "Data Management", icon: Database },
                  { id: "admin_integrations", label: "Integrations & Keys", icon: Plug },
                  { id: "timesheets", label: "Timesheets", icon: Clock },
                  { id: "analytics", label: "Analytics", icon: BarChart3 },
                  { id: "settings", label: "Settings", icon: Settings },
                ].map(item => (
                  <div
                    key={item.id}
                    className={`sidebar-item ${activeTab === item.id ? 'active' : ''}`}
                    onClick={() => {
                      selectTab(item.id);
                      if (item.id === 'analytics') fetchAnalytics();
                    }}
                    data-testid={`nav-${item.id}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1">{item.label}</span>
                  </div>
                ))}
              </>
            )}

            {/* Support module sub-navigation */}
            {!["cs_clients", "cs_at_risk", "cs_journey", "cs_health_checks", "company_overview", "company_meetings", "company_todos", "company_strategy", "company_goals", "company_focus", "company_documents", "company_billing", "academy", "sales_overview", "sales_prospects", "sales_outreach", "sales_nurture", "sales_templates", "sales_quotes", "sales_products", "sales_quote_settings", "marketing", "marketing_calendar", "marketing_social", "marketing_ai", "marketing_spy", "administration", "admin_users", "admin_roles", "admin_data", "admin_integrations", "timesheets", "analytics", "settings"].includes(activeTab) && (
            <>
            <div className="px-4 pt-4 pb-2 text-[9.5px] font-bold uppercase tracking-[0.16em] text-[#6E6E6E]">Support</div>
            {[
              { id: "overview", label: "Dashboard", icon: LayoutDashboard },
              { id: "tickets", label: "Tickets", icon: Ticket },
              { id: "escalations", label: "Escalations", icon: ArrowUpRight, badge: escalationStats?.total_escalations || 0 },
              { id: "calls", label: "Calls", icon: Phone, badge: callsData.metrics?.callbacks_overdue },
              { id: "infrastructure", label: "Infrastructure", icon: Network, badge: (databaseHealth?.summary?.critical || 0) + (databaseHealth?.summary?.warning || 0) + (wcfHealth?.summary?.critical || 0) + (serverHealth?.summary?.critical || 0) },
              { id: "scripts", label: "Scripts", icon: FileCode },
              { id: "recordings", label: "Recordings", icon: Mic, badge: recordings.filter(r => r.status === "processing").length },
              { id: "agents", label: "Agents", icon: Users },
            ].map(item => (
              <div
                key={item.id}
                className={`sidebar-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => {
                  if (item.route) { navigate(item.route); return; }
                  selectTab(item.id);
                  if (item.id === 'infrastructure') fetchInfrastructureHealth();
                  if (item.id === 'scripts') { fetchScripts(); fetchDatabaseHealth(); }
                }}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <Badge className="bg-[#C23A00] text-[#1A1A1A] text-xs px-1.5">{item.badge}</Badge>
                )}
              </div>
            ))}
            </>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 p-6" data-testid="main-content">
          {recordType ? (
            recordType === "quote_new" || recordType === "quote_edit"
              ? <QuoteBuilderPage editId={recordType === "quote_edit" ? routeId : null} />
              : recordType === "quote_view"
                ? <QuoteRecordView recordId={routeId} />
                : recordType === "prospect_email" || recordType === "client_email"
                  ? <EmailComposePage entity={recordType === "client_email" ? "client" : "prospect"} recordId={routeId} />
                  : recordType === "client"
                    ? <ClientRecordPage recordId={routeId} />
                    : recordType === "health_check_staff"
                      ? <HealthCheckStaffPage recordId={routeId} />
                      : recordType === "health_check_submission"
                        ? <HealthCheckSubmissionPage recordId={routeId} />
                        : <RecordView recordType={recordType} recordId={routeId} />
          ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="md:hidden mb-4 bg-[#FFFFFF] border border-[#DAD5CA] w-full max-w-full h-auto flex flex-nowrap overflow-x-auto justify-start">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="tickets" data-testid="tab-tickets">Tickets</TabsTrigger>
              <TabsTrigger value="escalations" data-testid="tab-escalations">Escalations</TabsTrigger>
              <TabsTrigger value="calls" data-testid="tab-calls">Calls</TabsTrigger>
              <TabsTrigger value="timesheets" data-testid="tab-timesheets">Timesheets</TabsTrigger>
              <TabsTrigger value="companies" data-testid="tab-companies">Companies</TabsTrigger>
              <TabsTrigger value="infrastructure" data-testid="tab-infrastructure">Infrastructure</TabsTrigger>
              <TabsTrigger value="scripts" data-testid="tab-scripts">Scripts</TabsTrigger>
              <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
              <TabsTrigger value="recordings" data-testid="tab-recordings">Recordings</TabsTrigger>
              <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
              <TabsTrigger value="alerts" data-testid="tab-alerts">Alerts</TabsTrigger>
              <TabsTrigger value="agents" data-testid="tab-agents">Agents</TabsTrigger>
              <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
            </TabsList>

            {/* Customer Success — Clients list */}

            <TabsContent value="cs_at_risk" className="space-y-6" data-testid="cs-at-risk-content">
              <CustomerSuccessAtRisk />
            </TabsContent>

            <TabsContent value="cs_clients" className="space-y-6" data-testid="cs-clients-content">
              <ClientsRegister onOpenClient={(c, section) => navigate(`/clients/${encodeURIComponent(c.client_id)}${section ? `#${section}` : ""}`)} />
            </TabsContent>

            {/* Customer Success — Journey (onboarding & Go Live) */}
            <TabsContent value="cs_journey" className="space-y-6" data-testid="cs-journey-content">
              <JourneyPage />
            </TabsContent>

            {/* Customer Success — Health Checks */}
            <TabsContent value="cs_health_checks" className="space-y-6" data-testid="cs-health-checks-content">
              <HealthChecksPage />
            </TabsContent>

            {/* Sales — Overview (demo outcomes) */}
            <TabsContent value="sales_overview" className="space-y-6" data-testid="sales-overview-content">
              <SalesOverview />
            </TabsContent>

            {/* Sales — Prospects pipeline */}
            <TabsContent value="sales_prospects" className="space-y-6" data-testid="sales-prospects-content">
              <SalesProspects />
            </TabsContent>

            {/* Sales — Outreach approvals */}
            <TabsContent value="sales_outreach" className="space-y-6" data-testid="sales-outreach-content">
              <OutreachInbox />
            </TabsContent>

            {/* Sales — Email Templates */}
            <TabsContent value="sales_templates" className="space-y-6" data-testid="sales-templates-content">
              <ProspectEmailTemplates />
            </TabsContent>

            <TabsContent value="sales_quotes" className="space-y-6" data-testid="sales-quotes-content">
              <QuotesOverview />
            </TabsContent>

            <TabsContent value="sales_products" className="space-y-6" data-testid="sales-products-content">
              <ProductLibrary />
            </TabsContent>

            <TabsContent value="sales_quote_settings" className="space-y-6" data-testid="sales-quote-settings-content">
              <QuoteSettings />
            </TabsContent>

            {/* Sales — Nurture sequences */}
            <TabsContent value="sales_nurture" className="space-y-6" data-testid="sales-nurture-content">
              <NurtureSequences />
            </TabsContent>

            {/* Company (EQUIPR) module */}
            <TabsContent value="company_overview" className="space-y-6" data-testid="company-overview-content">
              <CompanyOverview onNavigate={selectTab} />
            </TabsContent>
            <TabsContent value="company_meetings" className="space-y-6" data-testid="company-meetings-content">
              <ManagementMeetings />
            </TabsContent>
            <TabsContent value="company_todos" className="space-y-6" data-testid="company-todos-content">
              <CompanyTodos />
            </TabsContent>
            <TabsContent value="company_strategy" className="space-y-6" data-testid="company-strategy-content">
              <CompanyList kind="strategy" />
            </TabsContent>
            <TabsContent value="company_goals" className="space-y-6" data-testid="company-goals-content">
              <CompanyList kind="goals" />
            </TabsContent>
            <TabsContent value="company_focus" className="space-y-6" data-testid="company-focus-content">
              <CompanyList kind="focus" />
            </TabsContent>
            <TabsContent value="company_documents" className="space-y-6" data-testid="company-documents-content">
              <DocumentRegistry />
            </TabsContent>

            <TabsContent value="academy" className="space-y-6" data-testid="academy-tab-content">
              <AcademyManager />
            </TabsContent>

            <TabsContent value="company_billing" className="space-y-6" data-testid="company-billing-content">
              <BillingOverview />
            </TabsContent>

            {/* Marketing module */}
            <TabsContent value="marketing_calendar" className="space-y-6" data-testid="marketing-calendar-content">
              <SocialCalendar />
            </TabsContent>
            <TabsContent value="marketing" className="space-y-6" data-testid="marketing-content">
              <MarketingModule view="main" />
            </TabsContent>
            <TabsContent value="marketing_social" className="space-y-6" data-testid="marketing-social-content">
              <MarketingModule view="social" />
            </TabsContent>
            <TabsContent value="marketing_ai" className="space-y-6" data-testid="marketing-ai-content">
              <MarketingModule view="ai" />
            </TabsContent>
            <TabsContent value="marketing_spy" className="space-y-6" data-testid="marketing-spy-content">
              <MarketingModule view="spy" />
            </TabsContent>

            {/* Administration Module (placeholder — full area lands in the next slice) */}
            <TabsContent value="administration" className="space-y-6" data-testid="administration-content">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#FFF8D6] flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#8A6D00]" />
                </div>
                <div>
                  <h2 className="heading text-xl text-[#1A1A1A] tracking-tight">Administration</h2>
                  <p className="text-sm text-[#59554D]">Platform management for EQUIPR Intelligence</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { key: "users", label: "Users", icon: Users, desc: "Manage staff accounts and access" },
                  { key: "roles", label: "Roles & Permissions", icon: Shield, desc: "Role-based access control" },
                  { key: "feature-flags", label: "Feature Flags", icon: Layers, desc: "Toggle platform capabilities" },
                  { key: "integrations", label: "Integrations", icon: Network, desc: "GitHub, Jira, HubSpot, Teams, OpenAI" },
                  { key: "audit", label: "Audit Log", icon: ClipboardCheck, desc: "Track significant platform actions" },
                  { key: "org", label: "Organisation Settings", icon: Settings, desc: "Branding and platform configuration" },
                ].map(card => (
                  <Card key={card.key} className="bg-[#FFFFFF] border-[#DAD5CA]" data-testid={`admin-card-${card.key}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <card.icon className="w-5 h-5 text-[#8A6D00]" />
                        <span className="text-[#1A1A1A] font-medium">{card.label}</span>
                        <Badge variant="outline" className="ml-auto border-[#DAD5CA] text-[#8A857A] text-[9px] px-1 py-0">Soon</Badge>
                      </div>
                      <p className="text-sm text-[#8A857A]">{card.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-xs text-[#8A857A]">The Administration area is being built as part of the EQUIPR Intelligence platform foundation. These controls will be enabled in an upcoming release.</p>
            </TabsContent>

            {/* Administration — Users */}
            <TabsContent value="admin_users" className="space-y-6" data-testid="admin-users-content">
              <AdminUsers />
            </TabsContent>

            {/* Administration — Roles & Permissions */}
            <TabsContent value="admin_roles" className="space-y-6" data-testid="admin-roles-content">
              <AdminRoles />
            </TabsContent>

            <TabsContent value="admin_data" className="space-y-6" data-testid="admin-data-content">
              <AdminDataManagement />
            </TabsContent>

            <TabsContent value="admin_integrations" className="space-y-6" data-testid="admin-integrations-content">
              <AdminIntegrations />
            </TabsContent>


            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Metrics Grid */}
              <div className="bento-grid">
                <MetricCard
                  label="Open Tickets"
                  value={stats?.total_open || 0}
                  icon={Ticket}
                  color="#1A1A1A"
                />
                <MetricCard
                  label="In Progress"
                  value={stats?.total_in_progress || 0}
                  icon={Activity}
                  color="#1A1A1A"
                />
                <MetricCard
                  label="High Priority"
                  value={stats?.high_priority || 0}
                  icon={AlertTriangle}
                  color="#C23A00"
                />
                <MetricCard
                  label="Unassigned"
                  value={stats?.unassigned || 0}
                  icon={Users}
                  color="#3B82F6"
                />
                <MetricCard
                  label="SLA At Risk"
                  value={stats?.sla_at_risk || 0}
                  icon={Clock}
                  color="#6B5600"
                  onClick={() => { setActiveTab("tickets"); setTicketFilter("sla"); }}
                />
                <MetricCard
                  label="SLA Breached"
                  value={stats?.sla_breached || 0}
                  icon={XCircle}
                  color="#C23A00"
                  onClick={() => { setActiveTab("tickets"); setTicketFilter("sla"); }}
                />
                <MetricCard
                  label="Idle Tickets"
                  value={stats?.idle_tickets || 0}
                  icon={Clock}
                  color="#6B5600"
                  subtext={`> ${rules?.idle_warning_hours || 4}h idle`}
                  onClick={() => { setActiveTab("tickets"); setTicketFilter("idle"); }}
                />
                <MetricCard
                  label="Waiting"
                  value={stats?.total_waiting || 0}
                  icon={CheckCircle}
                  color="#3B82F6"
                />
              </div>

              {/* Unassigned Tickets Section - Top of Overview */}
              {unassignedWithSuggestions.length > 0 && (
                <Card className="bg-[#FFFFFF] border-[#FFF7D1]">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="heading text-lg text-[#6B5600] flex items-center gap-2">
                      <Inbox className="w-5 h-5" />
                      Unassigned Tickets
                      <Badge className="bg-[#FFF7D1] text-[#6B5600]">{unassignedWithSuggestions.length}</Badge>
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={fetchUnassignedWithSuggestions}
                        disabled={loadingUnassignedSuggestions}
                        className="text-[#59554D] hover:text-[#1A1A1A] h-7 px-2"
                      >
                        <RefreshCw className={`w-3 h-3 ${loadingUnassignedSuggestions ? 'animate-spin' : ''}`} />
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleRunAutoAssign}
                        disabled={autoAssignRunning}
                        className="bg-[#6B5600] hover:bg-[#FFF7D1] text-[#1A1A1A]"
                        data-testid="overview-auto-assign-btn"
                      >
                        {autoAssignRunning ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Route className="w-4 h-4 mr-2" />
                        )}
                        Auto-Assign All
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {unassignedWithSuggestions.slice(0, 6).map((ticket) => {
                        const orgKey = (ticket.organization || "").toLowerCase();
                        const clientInfo = vipLookup[orgKey];
                        return (
                          <div
                            key={ticket.key}
                            className={`p-3 rounded-lg border ${
                              clientInfo?.is_vip 
                                ? 'bg-[#FFF8D6] border-[#FFF8D6]' 
                                : 'bg-[#F4F2ED] border-[#DAD5CA]'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <a
                                href={`${JIRA_URL}/${ticket.key}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#8A6D00] hover:underline font-mono text-xs"
                              >
                                {ticket.key}
                              </a>
                              {clientInfo?.is_vip && (
                                <Crown className="w-3 h-3 text-[#C9A400]" title="VIP Client" />
                              )}
                              {clientInfo?.has_sla && (
                                <FileCheck className="w-3 h-3 text-[#1D4ED8]" title="SLA Client" />
                              )}
                              <PriorityBadge priority={ticket.priority} />
                            </div>
                            <p className="text-sm text-[#1A1A1A] truncate mb-2">{ticket.summary}</p>
                            <div className="flex items-center justify-between">
                              {ticket.organization ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/runbooks?company=${encodeURIComponent(ticket.organization)}`);
                                  }}
                                  className="text-xs text-[#8A857A] hover:text-[#8A6D00] hover:underline truncate"
                                  title="Open client runbook"
                                  data-testid={`runbook-link-${ticket.key}`}
                                >
                                  {ticket.organization}
                                </button>
                              ) : (
                                <p className="text-xs text-[#8A857A]">Unknown</p>
                              )}
                              {ticket.suggested_assignee && (
                                <div className="flex items-center gap-1">
                                  <UserCheck className="w-3 h-3 text-[#0E6B3F]" />
                                  <span className="text-xs text-[#0E6B3F] font-medium">{ticket.suggested_assignee.split(' ')[0]}</span>
                                </div>
                              )}
                            </div>
                            {ticket.suggestion_reason && (
                              <p className="text-[10px] text-[#8A857A] mt-1">{ticket.suggestion_reason}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {unassignedWithSuggestions.length > 6 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setActiveTab("tickets"); setTicketFilter("unassigned"); }}
                        className="w-full mt-3 text-[#6B5600] hover:text-[#6B5600] hover:bg-[#FFF7D1]"
                      >
                        View All {unassignedWithSuggestions.length} Unassigned Tickets <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader className="pb-2">
                    <CardTitle className="heading text-lg text-[#1A1A1A]">Priority Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={priorityDist} layout="vertical">
                          <XAxis type="number" stroke="#C9C3B6" fontSize={12} />
                          <YAxis type="category" dataKey="name" stroke="#C9C3B6" fontSize={12} width={80} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#F4F2ED', border: '1px solid #DAD5CA', borderRadius: '4px' }}
                            labelStyle={{ color: '#1A1A1A' }}
                          />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {priorityDist.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader className="pb-2">
                    <CardTitle className="heading text-lg text-[#1A1A1A]">Status Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusDist}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {statusDist.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ backgroundColor: '#F4F2ED', border: '1px solid #DAD5CA', borderRadius: '4px' }}
                            labelStyle={{ color: '#1A1A1A' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 justify-center">
                      {statusDist.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 text-xs">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.fill }} />
                          <span className="text-[#59554D]">{item.name}: {item.value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Call Activity Summary */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="heading text-lg text-[#1A1A1A] flex items-center gap-2">
                    <Phone className="w-5 h-5 text-[#8A6D00]" /> Today's Calls
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveTab("calls")}
                    className="text-[#8A6D00] hover:text-[#8A6D00] hover:bg-[#F4F2ED]"
                    data-testid="view-all-calls"
                  >
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center p-3 bg-[#F4F2ED] rounded-lg">
                      <p className="text-2xl font-bold text-[#1A1A1A]">{callsData.metrics?.total || 0}</p>
                      <p className="text-xs text-[#59554D]">Total</p>
                    </div>
                    <div className="text-center p-3 bg-[#F4F2ED] rounded-lg">
                      <p className="text-2xl font-bold text-[#0E6B3F]">{callsData.metrics?.inbound || 0}</p>
                      <p className="text-xs text-[#59554D]">Inbound</p>
                    </div>
                    <div className="text-center p-3 bg-[#F4F2ED] rounded-lg">
                      <p className="text-2xl font-bold text-[#8A6D00]">{callsData.metrics?.outbound || 0}</p>
                      <p className="text-xs text-[#59554D]">Outbound</p>
                    </div>
                    <div className="text-center p-3 bg-[#F4F2ED] rounded-lg">
                      <p className={`text-2xl font-bold ${callsData.metrics?.missed > 0 ? 'text-[#C23A00]' : 'text-[#0E6B3F]'}`}>
                        {callsData.metrics?.missed || 0}
                      </p>
                      <p className="text-xs text-[#59554D]">Missed</p>
                    </div>
                    <div className="text-center p-3 bg-[#F4F2ED] rounded-lg">
                      <p className={`text-2xl font-bold ${callsData.metrics?.callback_compliance >= 100 ? 'text-[#0E6B3F]' : 'text-[#6B5600]'}`}>
                        {callsData.metrics?.callback_compliance || 100}%
                      </p>
                      <p className="text-xs text-[#59554D]">Callback SLA</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Alerts & Top Idle */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="heading text-lg text-[#1A1A1A]">Recent Alerts</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveTab("alerts")}
                      className="text-[#8A6D00] hover:text-[#8A6D00] hover:bg-[#F4F2ED]"
                      data-testid="view-all-alerts"
                    >
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[200px]">
                      {alerts.length === 0 ? (
                        <p className="text-[#59554D] text-sm text-center py-8">No alerts yet</p>
                      ) : (
                        <div className="space-y-2">
                          {alerts.slice(0, 5).map((alert, idx) => (
                            <div
                              key={idx}
                              className="flex items-start gap-3 p-2 rounded bg-[#F4F2ED] border border-[#DAD5CA]"
                            >
                              <span className={`status-dot mt-1.5 ${alert.severity === 'critical' ? 'danger' : alert.severity === 'warning' ? 'warning' : 'info'}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="mono text-xs text-[#8A6D00]">{alert.ticket_key}</span>
                                  <span className="text-xs text-[#59554D]">{alert.alert_type}</span>
                                </div>
                                <p className="text-sm text-[#1A1A1A] truncate">{alert.message}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>

                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="heading text-lg text-[#1A1A1A]">Top Idle Tickets</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setActiveTab("tickets"); setTicketFilter("idle"); }}
                      className="text-[#8A6D00] hover:text-[#8A6D00] hover:bg-[#F4F2ED]"
                      data-testid="view-idle-tickets"
                    >
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[200px]">
                      {tickets.filter(t => t.idle_hours >= (rules?.idle_warning_hours || 4)).length === 0 ? (
                        <p className="text-[#59554D] text-sm text-center py-8">No idle tickets</p>
                      ) : (
                        <div className="space-y-2">
                          {tickets
                            .filter(t => t.idle_hours >= (rules?.idle_warning_hours || 4))
                            .sort((a, b) => b.idle_hours - a.idle_hours)
                            .slice(0, 5)
                            .map((ticket, idx) => {
                              const orgKey = (ticket.organization || "").toLowerCase();
                              const clientInfo = vipLookup[orgKey];
                              return (
                              <div
                                key={idx}
                                className={`flex items-center gap-3 p-2 rounded bg-[#F4F2ED] border ${
                                  clientInfo?.is_vip ? 'border-[#FFF8D6]' : 'border-[#DAD5CA]'
                                }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <a
                                      href={`${JIRA_URL}/${ticket.key}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="mono text-xs text-[#8A6D00] hover:underline"
                                    >
                                      {ticket.key}
                                    </a>
                                    {clientInfo?.is_vip && (
                                      <Crown className="w-3 h-3 text-[#C9A400]" title="VIP Client" />
                                    )}
                                    {clientInfo?.has_sla && (
                                      <FileCheck className="w-3 h-3 text-[#1D4ED8]" title="SLA Client" />
                                    )}
                                    <PriorityBadge priority={ticket.priority} />
                                  </div>
                                  <p className="text-sm text-[#1A1A1A] truncate">{ticket.summary}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-[#6B5600]">{ticket.idle_hours.toFixed(1)}h</p>
                                  <p className="text-xs text-[#59554D]">{ticket.assignee || "Unassigned"}</p>
                                </div>
                              </div>
                            )})}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Escalations Tab */}
            <TabsContent value="escalations" className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="heading text-xl text-[#1A1A1A]">Escalation Tracking</h2>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2" data-testid="esc-active-only-toggle">
                    <Switch
                      checked={escActiveOnly}
                      onCheckedChange={setEscActiveOnly}
                      id="esc-active-only"
                    />
                    <label htmlFor="esc-active-only" className="text-xs text-[#59554D] cursor-pointer select-none">
                      Active only
                    </label>
                  </div>
                  <Select value={escalationAgentFilter || "all"} onValueChange={(val) => setEscalationAgentFilter(val === "all" ? "" : val)}>
                    <SelectTrigger className="w-[200px] bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]">
                      <SelectValue placeholder="Filter by agent" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <SelectItem value="all">All Agents</SelectItem>
                      {agents.filter(a => a.active).map(agent => (
                        <SelectItem key={agent.id} value={agent.name}>{agent.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchEscalations}
                    disabled={loadingEscalations}
                    className="border-[#DAD5CA] text-[#1A1A1A]"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingEscalations ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {/* Stats Cards */}
              {escalationStats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2">
                        <ArrowUpRight className="w-8 h-8 text-[#6B5600]" />
                        <div>
                          <p className="text-2xl font-bold text-[#1A1A1A]">{escalationStats.total_escalations}</p>
                          <p className="text-xs text-[#59554D]">Total Escalations (all time)</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  {escalationStats.by_agent?.slice(0, 3).map((stat, idx) => (
                    <Card key={idx} className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-bold text-[#1A1A1A]">{stat.count}</p>
                            <p className="text-xs text-[#59554D]">{stat.agent}</p>
                          </div>
                          <Badge className="bg-[#FFF7D1] text-[#6B5600]">
                            {stat.organizations?.length || 0} orgs
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Escalations List */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                    <ArrowUpRight className="w-5 h-5 text-[#6B5600]" />
                    {escActiveOnly ? "Active Escalations" : "All Escalations"}
                    <span className="text-sm font-normal text-[#8A857A]">({escalations.length})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingEscalations ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#6B5600]" />
                    </div>
                  ) : escalations.length === 0 ? (
                    <div className="text-center py-8">
                      <ArrowUpRight className="w-12 h-12 mx-auto mb-4 text-[#C9C3B6]" />
                      <p className="text-[#59554D]">No escalations found</p>
                      <p className="text-sm text-[#8A857A] mt-1">{escActiveOnly ? "No tickets are currently escalated. Toggle off \"Active only\" to see history." : "Escalations from agents will appear here"}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {escalations.map((esc) => (
                        <div key={esc.escalation_id} className="p-4 bg-[#F4F2ED] rounded-lg border border-[#DAD5CA]">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <a
                                href={`https://equiprsoftware.atlassian.net/browse/${esc.ticket_key}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#8A6D00] hover:underline font-mono text-sm"
                              >
                                {esc.ticket_key}
                              </a>
                              <Badge className="bg-[#DAD5CA] text-[#59554D]">{esc.priority || 'Medium'}</Badge>
                              <Badge className="bg-[#FFF7D1] text-[#6B5600]">{esc.organization || 'Unknown'}</Badge>
                              {esc.current_status && (
                                <Badge className={`${esc.is_active_escalation ? 'bg-[#FFE9E0] text-[#C23A00]' : 'bg-[#E3F3E9] text-[#0E6B3F]'}`}>
                                  {esc.current_status}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-[#8A857A]">
                                {formatDateTime(esc.escalated_at)}
                              </span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteEscalation(esc.escalation_id)}
                                className="h-7 w-7 p-0 text-[#8A857A] hover:text-[#C23A00] hover:bg-[#FFE9E0]"
                                title="Delete escalation"
                                data-testid={`delete-escalation-${esc.escalation_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-[#1A1A1A] text-sm mb-2">{esc.ticket_summary}</p>
                          <div className="p-3 bg-[#FFFFFF] rounded border border-[#DAD5CA] mb-2">
                            <p className="text-xs text-[#59554D] mb-1">Why escalated (AI Summary):</p>
                            <p className="text-sm text-[#1A1A1A] whitespace-pre-line">{esc.ai_summary}</p>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-4 flex-wrap">
                              <span className="text-[#59554D]">
                                Escalated by: <span className="text-[#6B5600]">{esc.escalated_by}</span>
                              </span>
                              <span className="text-[#59554D]">
                                To: <span className="text-[#0E6B3F]">{esc.escalated_to}</span>
                              </span>
                              {esc.last_reply_by && (
                                <span className="text-[#59554D]">
                                  Last reply: <span className="text-[#8A6D00]">{esc.last_reply_by}</span>
                                  {esc.last_reply_at ? ` · ${formatDateTime(esc.last_reply_at)}` : ''}
                                </span>
                              )}
                            </div>
                            {esc.reason && (
                              <span className="text-[#8A857A]">Reason: {esc.reason}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#DAD5CA]">
                            <Button
                              size="sm"
                              onClick={() => { setReplyEsc(esc); setReplyMessage(""); setReplyInternal(false); }}
                              className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A] h-8"
                              data-testid={`escalation-reply-${esc.ticket_key}`}
                            >
                              <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                              Reply
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setDeEsc(esc); setDeEscNote(""); setDeEscStatus("In Progress"); }}
                              className="border-[#E3F3E9] text-[#0E6B3F] hover:bg-[#E3F3E9] h-8"
                              data-testid={`escalation-resolve-${esc.ticket_key}`}
                            >
                              <ArrowDownLeft className="w-3.5 h-3.5 mr-1.5" />
                              Move out of Escalated
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Knowledge Gap Analysis */}
              {escalationStats?.by_agent?.length > 0 && (
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader>
                    <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-[#1D4ED8]" />
                      Training Insights
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#59554D] mb-4">
                      Based on escalation patterns, consider targeted training for:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {escalationStats.by_agent.map((stat, idx) => (
                        <div key={idx} className="p-3 bg-[#F4F2ED] rounded-lg border border-[#DAD5CA]">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[#1A1A1A] font-medium">{stat.agent}</span>
                            <Badge className="bg-[#FFF7D1] text-[#6B5600]">{stat.count} escalations</Badge>
                          </div>
                          <p className="text-xs text-[#8A857A]">
                            Organizations: {stat.organizations?.join(', ') || 'Various'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* #12 — Reply to Escalation Dialog */}
              <Dialog open={!!replyEsc} onOpenChange={(open) => { if (!open) { setReplyEsc(null); setReplyMessage(""); } }}>
                <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-[#8A6D00]" />
                      Reply — {replyEsc?.ticket_key}
                    </DialogTitle>
                  </DialogHeader>
                  {replyEsc && (
                    <div className="space-y-4">
                      <p className="text-sm text-[#59554D]">{replyEsc.ticket_summary}</p>
                      <Textarea
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Type your reply…"
                        rows={5}
                        className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]"
                        data-testid="escalation-reply-message"
                      />
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={replyInternal}
                          onCheckedChange={setReplyInternal}
                          id="reply-internal"
                          data-testid="escalation-reply-internal-toggle"
                        />
                        <label htmlFor="reply-internal" className="text-sm text-[#59554D] cursor-pointer select-none">
                          {replyInternal ? "Internal note (agents only)" : "Visible to customer"}
                        </label>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" className="border-[#DAD5CA] text-[#1A1A1A]" onClick={() => { setReplyEsc(null); setReplyMessage(""); }}>
                          Cancel
                        </Button>
                        <Button
                          onClick={submitEscalationReply}
                          disabled={replySending || !replyMessage.trim()}
                          className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]"
                          data-testid="escalation-reply-submit"
                        >
                          {replySending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Send Reply
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>

              {/* #12 — Move out of Escalated Dialog */}
              <Dialog open={!!deEsc} onOpenChange={(open) => { if (!open) { setDeEsc(null); setDeEscNote(""); } }}>
                <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <ArrowDownLeft className="w-5 h-5 text-[#0E6B3F]" />
                      Move out of Escalated — {deEsc?.ticket_key}
                    </DialogTitle>
                  </DialogHeader>
                  {deEsc && (
                    <div className="space-y-4">
                      <p className="text-sm text-[#59554D]">{deEsc.ticket_summary}</p>
                      <div>
                        <label className="text-xs text-[#59554D] mb-1 block">Move to status</label>
                        <Select value={deEscStatus} onValueChange={setDeEscStatus}>
                          <SelectTrigger className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]" data-testid="de-escalate-status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA]">
                            <SelectItem value="In Progress">In Progress</SelectItem>
                            <SelectItem value="Waiting for support">Waiting for support</SelectItem>
                            <SelectItem value="Waiting for customer">Waiting for customer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Textarea
                        value={deEscNote}
                        onChange={(e) => setDeEscNote(e.target.value)}
                        placeholder="Optional review note (internal)…"
                        rows={3}
                        className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]"
                        data-testid="de-escalate-note"
                      />
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" className="border-[#DAD5CA] text-[#1A1A1A]" onClick={() => { setDeEsc(null); setDeEscNote(""); }}>
                          Cancel
                        </Button>
                        <Button
                          onClick={submitDeEscalate}
                          disabled={deEscSending}
                          className="bg-[#0E6B3F] hover:bg-[#E3F3E9] text-black font-medium"
                          data-testid="de-escalate-submit"
                        >
                          {deEscSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Confirm
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Calls Tab */}
            <TabsContent value="calls" className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="heading text-xl text-[#1A1A1A]">Call Activity</h2>
                <Badge variant="outline" className="border-[#DAD5CA] text-[#59554D]">
                  <Phone className="w-3 h-3 mr-1" /> Maxotel Live
                </Badge>
              </div>

              {/* Call Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-[#1A1A1A]">{callsData.metrics?.total || 0}</p>
                    <p className="text-xs text-[#59554D] mt-1">Total Calls</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-[#0E6B3F]">{callsData.metrics?.inbound || 0}</p>
                    <p className="text-xs text-[#59554D] mt-1">Inbound</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-[#8A6D00]">{callsData.metrics?.outbound || 0}</p>
                    <p className="text-xs text-[#59554D] mt-1">Outbound</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4 text-center">
                    <p className={`text-3xl font-bold ${callsData.metrics?.missed > 0 ? 'text-[#C23A00]' : 'text-[#0E6B3F]'}`}>
                      {callsData.metrics?.missed || 0}
                    </p>
                    <p className="text-xs text-[#59554D] mt-1">Missed</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4 text-center">
                    <p className={`text-3xl font-bold ${callsData.metrics?.callback_compliance >= 100 ? 'text-[#0E6B3F]' : 'text-[#6B5600]'}`}>
                      {callsData.metrics?.callback_compliance || 100}%
                    </p>
                    <p className="text-xs text-[#59554D] mt-1">Callback SLA</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-[#1A1A1A]">
                      {Math.floor((callsData.metrics?.avg_duration || 0) / 60)}m
                    </p>
                    <p className="text-xs text-[#59554D] mt-1">Avg Duration</p>
                  </CardContent>
                </Card>
              </div>

              {/* Callback Alerts */}
              {(callsData.metrics?.callbacks_pending > 0 || callsData.metrics?.callbacks_overdue > 0) && (
                <Card className="bg-[#FFE9E0] border-[#FFE9E0]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <PhoneMissed className="w-6 h-6 text-[#C23A00]" />
                      <div>
                        <p className="font-medium text-[#C23A00]">
                          {callsData.metrics?.callbacks_overdue > 0 
                            ? `${callsData.metrics.callbacks_overdue} Callback(s) OVERDUE (>30 min)` 
                            : `${callsData.metrics.callbacks_pending} Callback(s) Pending`}
                        </p>
                        <p className="text-sm text-[#59554D]">Missed calls must be returned within 30 minutes</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Call History Table */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-lg text-[#1A1A1A]">Today&apos;s Calls</CardTitle>
                </CardHeader>
                <ScrollArea className="h-[calc(100vh-480px)]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#DAD5CA] hover:bg-transparent">
                        <TableHead className="text-[#59554D]">Time</TableHead>
                        <TableHead className="text-[#59554D]">Direction</TableHead>
                        <TableHead className="text-[#59554D]">From/To</TableHead>
                        <TableHead className="text-[#59554D]">Agent</TableHead>
                        <TableHead className="text-[#59554D]">Status</TableHead>
                        <TableHead className="text-[#59554D]">Duration</TableHead>
                        <TableHead className="text-[#59554D]">Callback</TableHead>
                        <TableHead className="text-[#59554D] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(callsData.calls || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-[#59554D] py-8">
                            No calls recorded today
                          </TableCell>
                        </TableRow>
                      ) : (
                        (callsData.calls || []).map((call, idx) => (
                          <TableRow key={idx} className="border-[#F4F2ED] hover:bg-[#F4F2ED]">
                            <TableCell className="font-mono text-sm">
                              {call.datetime?.split(' ')[1] || 'N/A'}
                            </TableCell>
                            <TableCell>
                              {call.direction === "IN" ? (
                                <span className="flex items-center gap-1 text-[#0E6B3F]">
                                  <PhoneIncoming className="w-4 h-4" /> Inbound
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-[#8A6D00]">
                                  <PhoneOutgoing className="w-4 h-4" /> Outbound
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {call.direction === "IN" ? call.origin : call.destination}
                            </TableCell>
                            <TableCell className="text-[#1A1A1A]">
                              {call.assigned_to || "Unknown"}
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                call.status === "Connected." ? "bg-[#E3F3E9] text-[#0E6B3F]" :
                                call.is_missed ? "bg-[#FFE9E0] text-[#C23A00]" :
                                "bg-[#59554D] text-[#59554D]"
                              }>
                                {call.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono">
                              {call.duration_hr || `${call.duration || 0}s`}
                            </TableCell>
                            <TableCell>
                              {call.callback_status === "overdue" ? (
                                <Badge className="bg-[#FFE9E0] text-[#C23A00] animate-pulse">OVERDUE</Badge>
                              ) : call.callback_status === "pending" ? (
                                <Badge className="bg-[#FFF7D1] text-[#6B5600]">Pending</Badge>
                              ) : (
                                <span className="text-[#59554D]">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedCall(call);
                                  setCallUploadDialogOpen(true);
                                }}
                                className="text-[#8A6D00] hover:bg-[#FFF8D6]"
                                data-testid={`upload-recording-btn-${idx}`}
                              >
                                <Upload className="w-4 h-4 mr-1" />
                                Upload
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </Card>
            </TabsContent>

            {/* Timesheets Tab */}
            <TabsContent value="timesheets" className="space-y-6" data-testid="timesheets-tab-content">
              <TimesheetsTab />
            </TabsContent>

            {/* Companies Tab */}
            <TabsContent value="companies" className="space-y-6" data-testid="companies-tab">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="heading text-xl text-[#1A1A1A]">Company Health</h2>
                  <p className="text-sm text-[#59554D]">
                    {companyHealth?.summary?.period_label || "All Open Tickets"} • {companyHealth?.summary?.total_tickets_in_period || 0} tickets
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={companyPeriod} onValueChange={setCompanyPeriod}>
                    <SelectTrigger className="w-[150px] bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]" data-testid="company-period-filter">
                      <SelectValue placeholder="Time Period" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <SelectItem value="all">All Open</SelectItem>
                      <SelectItem value="week">This Week</SelectItem>
                      <SelectItem value="last_week">Last Week</SelectItem>
                      <SelectItem value="month">This Month</SelectItem>
                      <SelectItem value="last_month">Last Month</SelectItem>
                      <SelectItem value="ytd">Year To Date</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchDashboardData}
                    className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="bg-[#FFFFFF] border-[#DAD5CA] text-center py-4">
                  <div className="text-4xl font-bold text-[#1A1A1A]">{companyHealth?.summary?.total_companies || 0}</div>
                  <div className="text-sm text-[#59554D]">Total Companies</div>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#E3F3E9] text-center py-4">
                  <div className="text-4xl font-bold text-[#0E6B3F]">{companyHealth?.summary?.healthy || 0}</div>
                  <div className="text-sm text-[#59554D]">Healthy</div>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#FFF7D1] text-center py-4">
                  <div className="text-4xl font-bold text-[#6B5600]">{companyHealth?.summary?.attention || 0}</div>
                  <div className="text-sm text-[#59554D]">Needs Attention</div>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#FFE9E0] text-center py-4">
                  <div className="text-4xl font-bold text-[#C23A00]">{companyHealth?.summary?.at_risk || 0}</div>
                  <div className="text-sm text-[#59554D]">At Risk</div>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#FFE9E0] text-center py-4">
                  <div className="text-4xl font-bold text-[#C23A00]">{companyHealth?.summary?.critical || 0}</div>
                  <div className="text-sm text-[#59554D]">Critical</div>
                </Card>
              </div>

              {/* Average Health Score */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#59554D]">Average Health Score</p>
                      <p className="text-5xl font-bold text-[#1A1A1A]">{companyHealth?.summary?.avg_health_score || 0}<span className="text-lg text-[#59554D]">/100</span></p>
                    </div>
                    <div className="w-32 h-32 relative">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle cx="64" cy="64" r="56" stroke="#DAD5CA" strokeWidth="8" fill="none" />
                        <circle 
                          cx="64" cy="64" r="56" 
                          stroke={
                            (companyHealth?.summary?.avg_health_score || 0) >= 80 ? "#0E6B3F" :
                            (companyHealth?.summary?.avg_health_score || 0) >= 60 ? "#6B5600" : "#C23A00"
                          }
                          strokeWidth="8" 
                          fill="none"
                          strokeDasharray={`${(companyHealth?.summary?.avg_health_score || 0) * 3.52} 352`}
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Company List */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-[#1A1A1A]">Companies by Health Score</CardTitle>
                  <p className="text-sm text-[#59554D]">
                    {companyHealth?.summary?.period_label || "All Open Tickets"}
                  </p>
                </CardHeader>
                <ScrollArea className="h-[calc(100vh-550px)]">
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#DAD5CA]">
                          <TableHead className="text-[#59554D]">Company</TableHead>
                          <TableHead className="text-[#59554D] text-center">Score</TableHead>
                          <TableHead className="text-[#59554D] text-center">Status</TableHead>
                          <TableHead className="text-[#59554D] text-center">Database</TableHead>
                          <TableHead className="text-[#59554D] text-center">Open Tickets</TableHead>
                          <TableHead className="text-[#59554D] text-center">SLA Breached</TableHead>
                          <TableHead className="text-[#59554D] text-center">At Risk</TableHead>
                          <TableHead className="text-[#59554D] text-center">Idle</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(companyHealth?.companies || []).map((company, idx) => (
                          <TableRow 
                            key={idx} 
                            className="border-[#F4F2ED] hover:bg-[#F4F2ED] cursor-pointer"
                            onClick={() => openCompanyDialog(company)}
                            data-testid={`company-row-${idx}`}
                          >
                            <TableCell className="font-medium text-[#1A1A1A]">
                              <div className="flex items-center gap-2 flex-wrap">
                                {company.company}
                                {company.is_vip && (
                                  <span
                                    className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#FFF8D6] text-[#FFD108] bg-[#FFF8D6] inline-flex items-center gap-1"
                                    title="VIP client"
                                    data-testid={`vip-${idx}`}
                                  >
                                    <Crown className="w-3 h-3" /> VIP
                                  </span>
                                )}
                                {company.has_sla && (
                                  <span
                                    className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#E3F3E9] text-[#0E6B3F] bg-[#E3F3E9] inline-flex items-center gap-1"
                                    title="SLA contract"
                                    data-testid={`sla-${idx}`}
                                  >
                                    <FileText className="w-3 h-3" /> SLA
                                  </span>
                                )}
                                <ChevronRight className="w-4 h-4 text-[#59554D] ml-auto" />
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={`text-xl font-bold ${
                                company.health_score >= 80 ? 'text-[#0E6B3F]' :
                                company.health_score >= 60 ? 'text-[#6B5600]' :
                                company.health_score >= 40 ? 'text-[#C23A00]' : 'text-[#C23A00]'
                              }`}>
                                {company.health_score}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={`${
                                company.status === 'healthy' ? 'bg-[#E3F3E9] text-[#0E6B3F]' :
                                company.status === 'attention' ? 'bg-[#FFF7D1] text-[#6B5600]' :
                                company.status === 'at_risk' ? 'bg-[#FFE9E0] text-[#C23A00]' : 'bg-[#FFE9E0] text-[#C23A00] animate-pulse'
                              }`}>
                                {company.status === 'at_risk' ? 'AT RISK' : company.status.toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {company.database_status ? (
                                <div className="flex items-center justify-center gap-1">
                                  {company.database_status.is_connected ? (
                                    <Wifi className={`w-4 h-4 ${
                                      company.database_status.status === 'healthy' ? 'text-[#0E6B3F]' :
                                      company.database_status.status === 'warning' ? 'text-[#6B5600]' : 'text-[#C23A00]'
                                    }`} />
                                  ) : (
                                    <WifiOff className="w-4 h-4 text-[#C23A00]" />
                                  )}
                                  <span className={`text-xs ${
                                    company.database_status.status === 'healthy' ? 'text-[#0E6B3F]' :
                                    company.database_status.status === 'warning' ? 'text-[#6B5600]' : 'text-[#C23A00]'
                                  }`}>
                                    {company.database_status.status?.toUpperCase()}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[#59554D] text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center text-[#1A1A1A]">{company.total_tickets}</TableCell>
                            <TableCell className="text-center">
                              {company.sla_breached > 0 ? (
                                <span className="text-[#C23A00] font-bold">{company.sla_breached}</span>
                              ) : (
                                <span className="text-[#0E6B3F]">0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {company.sla_at_risk > 0 ? (
                                <span className="text-[#6B5600]">{company.sla_at_risk}</span>
                              ) : (
                                <span className="text-[#59554D]">0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {company.idle_tickets > 0 ? (
                                <span className="text-[#6B5600]">{company.idle_tickets}</span>
                              ) : (
                                <span className="text-[#59554D]">0</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </ScrollArea>
              </Card>

              {/* Company Detail Dialog */}
              <Dialog open={!!selectedCompany} onOpenChange={(open) => !open && setSelectedCompany(null)}>
                <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-4xl max-h-[90vh] overflow-hidden" aria-describedby="company-detail-description">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold flex items-center gap-3 flex-wrap">
                      <Building2 className="w-6 h-6" />
                      {selectedCompany?.company}
                      {selectedCompany?.is_vip && (
                        <span
                          className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded border border-[#FFF8D6] text-[#FFD108] bg-[#FFF8D6] inline-flex items-center gap-1"
                          title="VIP client"
                          data-testid="company-vip-badge"
                        >
                          <Crown className="w-3 h-3" /> VIP
                        </span>
                      )}
                      {selectedCompany?.has_sla && (
                        <span
                          className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded border border-[#E3F3E9] text-[#0E6B3F] bg-[#E3F3E9] inline-flex items-center gap-1"
                          title="SLA contract"
                          data-testid="company-sla-badge"
                        >
                          <FileText className="w-3 h-3" /> SLA
                        </span>
                      )}
                      <Badge className={`ml-2 ${
                        selectedCompany?.status === 'healthy' ? 'bg-[#E3F3E9] text-[#0E6B3F]' :
                        selectedCompany?.status === 'attention' ? 'bg-[#FFF7D1] text-[#6B5600]' :
                        selectedCompany?.status === 'at_risk' ? 'bg-[#FFE9E0] text-[#C23A00]' : 'bg-[#FFE9E0] text-[#C23A00]'
                      }`}>
                        {selectedCompany?.status?.toUpperCase()}
                      </Badge>
                    </DialogTitle>
                    <p id="company-detail-description" className="text-[#59554D]">
                      Health Score: <span className={`text-2xl font-bold ${
                        (selectedCompany?.health_score || 0) >= 80 ? 'text-[#0E6B3F]' :
                        (selectedCompany?.health_score || 0) >= 60 ? 'text-[#6B5600]' : 'text-[#C23A00]'
                      }`}>{selectedCompany?.health_score}/100</span>
                      {companyHealth?.summary?.date_range_label && (
                        <span className="block text-xs text-[#8A857A] mt-1" data-testid="date-range-label">
                          {companyHealth.summary.date_range_label}
                        </span>
                      )}
                    </p>
                  </DialogHeader>
                  
                  <ScrollArea className="h-[calc(90vh-150px)] pr-4">
                    {/* HubSpot CRM Snapshot (manager-only — the Companies tab
                        itself is gated behind the manager role). Only renders
                        when a matching HubSpot company was found. */}
                    {selectedCompany?.hubspot ? (
                      <div className="mb-6" data-testid="hubspot-panel">
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-[#C23A00]" />
                          HubSpot CRM
                          <a
                            href={selectedCompany.hubspot.hubspot_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#C23A00] hover:underline ml-2"
                            data-testid="hubspot-open-link"
                          >
                            Open in HubSpot ↗
                          </a>
                        </h3>
                        <div className="bg-gradient-to-br from-[#FFE9E0] to-transparent border border-[#FFE9E0] rounded-lg p-4">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                            <div>
                              <div className="text-[#59554D] text-xs">Company</div>
                              <div className="text-[#1A1A1A] font-medium">{selectedCompany.hubspot.name || "—"}</div>
                            </div>
                            <div>
                              <div className="text-[#59554D] text-xs">Domain</div>
                              <div className="text-[#1A1A1A]">{selectedCompany.hubspot.domain || "—"}</div>
                            </div>
                            <div>
                              <div className="text-[#59554D] text-xs">Owner</div>
                              <div className="text-[#1A1A1A]">{selectedCompany.hubspot.owner?.full_name || "—"}</div>
                            </div>
                            <div>
                              <div className="text-[#59554D] text-xs">Lifecycle</div>
                              <div>
                                {selectedCompany.hubspot.lifecycle_stage ? (
                                  <Badge variant="outline" className="border-[#FFE9E0] text-[#C23A00] capitalize">
                                    {String(selectedCompany.hubspot.lifecycle_stage).replace(/_/g, " ")}
                                  </Badge>
                                ) : <span className="text-[#59554D]">—</span>}
                              </div>
                            </div>
                            <div>
                              <div className="text-[#59554D] text-xs">Type</div>
                              <div className="text-[#1A1A1A]">{selectedCompany.hubspot.type || "—"}</div>
                            </div>
                            <div>
                              <div className="text-[#59554D] text-xs">Industry</div>
                              <div className="text-[#1A1A1A]">{selectedCompany.hubspot.industry || "—"}</div>
                            </div>
                            <div>
                              <div className="text-[#59554D] text-xs">Open Deals</div>
                              <div className="text-[#1A1A1A] font-medium">
                                {selectedCompany.hubspot.open_deals ?? 0}
                                {" "}
                                <span className="text-[#59554D] text-xs">
                                  / {selectedCompany.hubspot.num_associated_deals ?? 0} total
                                </span>
                              </div>
                            </div>
                            <div>
                              <div className="text-[#59554D] text-xs">Annual Revenue</div>
                              <div className="text-[#1A1A1A]">
                                {selectedCompany.hubspot.annual_revenue
                                  ? `$${Number(selectedCompany.hubspot.annual_revenue).toLocaleString()}`
                                  : "—"}
                              </div>
                            </div>
                            <div>
                              <div className="text-[#59554D] text-xs">Location</div>
                              <div className="text-[#1A1A1A]">
                                {[selectedCompany.hubspot.city, selectedCompany.hubspot.country]
                                  .filter(Boolean).join(", ") || "—"}
                              </div>
                            </div>
                          </div>

                          {selectedCompany.hubspot.custom_properties &&
                           Object.keys(selectedCompany.hubspot.custom_properties).filter(k => k !== "hs_object_id").length > 0 && (
                            <div className="mt-4 pt-4 border-t border-[#FFE9E0]">
                              <div className="text-[#59554D] text-xs mb-2">Custom properties</div>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-xs">
                                {Object.entries(selectedCompany.hubspot.custom_properties)
                                  .filter(([k]) => k !== "hs_object_id")
                                  .map(([k, v]) => (
                                    <div key={k}>
                                      <div className="text-[#59554D] truncate" title={k}>{k}</div>
                                      <div className="text-[#1A1A1A] truncate" title={String(v)}>{String(v)}</div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : companyHealth?.summary?.hubspot_enriched || hubspotLoading || selectedCompany?.hubspot === null ? (
                      <div className="mb-6">
                        <div className="bg-[#F4F2ED] border border-[#DAD5CA] rounded-lg p-3 text-xs text-[#59554D] flex items-center gap-2" data-testid="hubspot-no-match">
                          <Building2 className="w-4 h-4 text-[#C23A00]" />
                          {hubspotLoading
                            ? "Looking up HubSpot CRM data…"
                            : `No matching HubSpot company found for "${selectedCompany?.company}".`}
                        </div>
                      </div>
                    ) : null}

                    {/* Score Breakdown */}
                    {selectedCompany?.score_breakdown?.length > 0 && (
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-[#6B5600]" />
                          Score Deductions
                        </h3>
                        <div className="bg-[#F4F2ED] rounded-lg p-4">
                          <div className="space-y-2">
                            {selectedCompany.score_breakdown.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between py-2 border-b border-[#DAD5CA] last:border-0">
                                <div className="flex items-center gap-3">
                                  <span className={`w-2 h-2 rounded-full ${
                                    item.severity === 'critical' ? 'bg-[#C23A00]' :
                                    item.severity === 'warning' ? 'bg-[#6B5600]' : 'bg-[#FFD108]'
                                  }`} />
                                  <span className="text-[#1A1A1A]">{item.reason}</span>
                                  <span className="text-[#59554D]">({item.count})</span>
                                </div>
                                <span className="text-[#C23A00] font-bold">-{item.deduction} pts</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Issues by Status (open tickets) */}
                    {selectedCompany?.status_breakdown && selectedCompany.status_breakdown.length > 0 && (
                      <div className="mb-6" data-testid="status-breakdown">
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                          <Activity className="w-5 h-5 text-[#1D4ED8]" />
                          Issues by Status
                          <Badge className="bg-[#DAD5CA] text-[#59554D] ml-1">
                            {selectedCompany.status_breakdown.reduce((s, r) => s + r.count, 0)} open
                          </Badge>
                          {selectedCompany.completed_count > 0 && (
                            <Badge className="bg-[#E3F3E9] text-[#0E6B3F] border border-[#E3F3E9]" data-testid="completed-badge">
                              {selectedCompany.completed_count} completed
                            </Badge>
                          )}
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {selectedCompany.status_breakdown.map((row) => {
                            const s = (row.status || "").toLowerCase();
                            const tone =
                              s.includes("waiting for customer") ? "border-[#EFF6FF] text-[#3B82F6] bg-[#EFF6FF]" :
                              s.includes("waiting for support") ? "border-[#FFF7D1] text-[#6B5600] bg-[#FFF7D1]" :
                              s.includes("pending development") ? "border-[#EFF6FF] text-[#1D4ED8] bg-[#EFF6FF]" :
                              s.includes("awaiting system") ? "border-[#FFE9E0] text-[#C23A00] bg-[#FFE9E0]" :
                              s.includes("escalat") ? "border-[#FFE9E0] text-[#C23A00] bg-[#FFE9E0]" :
                              s === "new" ? "border-[#E3F3E9] text-[#0E6B3F] bg-[#E3F3E9]" :
                              "border-[#48484A] text-[#59554D] bg-[#F4F2ED]";
                            return (
                              <div
                                key={row.status}
                                className={`flex items-center justify-between border rounded-lg px-3 py-2 ${tone}`}
                                data-testid={`status-row-${row.status}`}
                              >
                                <span className="text-xs uppercase tracking-wide truncate">{row.status}</span>
                                <span className="text-lg font-semibold">{row.count}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Tickets by Agent */}
                    {(() => {
                      const openMap = selectedCompany?.tickets_by_agent || {};
                      const closedMap = selectedCompany?.closed_tickets_by_agent || {};
                      const openCount = Object.values(openMap).reduce((s, arr) => s + (arr?.length || 0), 0);
                      const closedCount = Object.values(closedMap).reduce((s, arr) => s + (arr?.length || 0), 0);
                      if (openCount === 0 && closedCount === 0) return null;
                      const currentMap = issuesTab === "closed" ? closedMap : openMap;
                      return (
                        <div className="mb-6" data-testid="issues-by-agent">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                              <Users className="w-5 h-5 text-[#8A6D00]" />
                              Issues by Agent
                            </h3>
                            {/* Open / Closed tab toggle */}
                            <div className="flex gap-1 bg-[#F4F2ED] p-1 rounded-lg border border-[#DAD5CA]" data-testid="issues-tabs">
                              <button
                                onClick={() => setIssuesTab("open")}
                                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                                  issuesTab !== "closed"
                                    ? "bg-[#FFD108] text-[#1A1A1A]"
                                    : "text-[#59554D] hover:text-[#1A1A1A]"
                                }`}
                                data-testid="issues-tab-open"
                              >
                                Open · {openCount}
                              </button>
                              <button
                                onClick={() => setIssuesTab("closed")}
                                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                                  issuesTab === "closed"
                                    ? "bg-[#0E6B3F] text-[#1A1A1A]"
                                    : "text-[#59554D] hover:text-[#1A1A1A]"
                                }`}
                                data-testid="issues-tab-closed"
                              >
                                Closed · {closedCount}
                              </button>
                            </div>
                          </div>
                          {Object.keys(currentMap).length === 0 ? (
                            <div className="bg-[#F4F2ED] rounded-lg p-4 text-sm text-[#59554D] text-center">
                              {issuesTab === "closed"
                                ? "No closed tickets in this period."
                                : "No open issues."}
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {Object.entries(currentMap).map(([agent, agentTickets]) => (
                            <div key={agent} className="bg-[#F4F2ED] rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-[#DAD5CA] flex items-center justify-center text-xs">
                                    {agent.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                  </div>
                                  <span className="font-medium">{agent}</span>
                                </div>
                                <Badge className="bg-[#DAD5CA] text-[#59554D]">
                                  {agentTickets.length} ticket{agentTickets.length !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                              <div className="space-y-2">
                                {agentTickets.map((ticket, tIdx) => (
                                  <div key={tIdx} className="flex items-start gap-3 py-2 border-t border-[#DAD5CA]">
                                    <a 
                                      href={`${JIRA_URL}/${ticket.key}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[#8A6D00] hover:underline font-mono text-sm whitespace-nowrap"
                                    >
                                      {ticket.key}
                                    </a>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-start justify-between gap-2">
                                        <p className="text-sm text-[#1A1A1A] truncate">{ticket.summary}</p>
                                        {ticket.status && (() => {
                                          // Status pill in the top-right of each ticket row.
                                          // Colour-coded to match the "Issues by Status" panel above
                                          // so managers can scan agent workload at a glance.
                                          const s = ticket.status.toLowerCase();
                                          const tone =
                                            s.includes("waiting for customer") ? "border-[#EFF6FF] text-[#3B82F6] bg-[#EFF6FF]" :
                                            s.includes("waiting for support") ? "border-[#FFF7D1] text-[#6B5600] bg-[#FFF7D1]" :
                                            s.includes("pending development") ? "border-[#EFF6FF] text-[#1D4ED8] bg-[#EFF6FF]" :
                                            s.includes("awaiting system") ? "border-[#FFE9E0] text-[#C23A00] bg-[#FFE9E0]" :
                                            s.includes("escalat") ? "border-[#FFE9E0] text-[#C23A00] bg-[#FFE9E0]" :
                                            s === "new" ? "border-[#E3F3E9] text-[#0E6B3F] bg-[#E3F3E9]" :
                                            s.includes("resolved") || s.includes("done") || s.includes("closed") ? "border-[#E3F3E9] text-[#0E6B3F] bg-[#E3F3E9]" :
                                            "border-[#48484A] text-[#59554D] bg-[#F4F2ED]";
                                          return (
                                            <span
                                              className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border whitespace-nowrap ${tone}`}
                                              data-testid={`ticket-status-${ticket.key}`}
                                            >
                                              {ticket.status}
                                            </span>
                                          );
                                        })()}
                                      </div>
                                      {ticket.created && (
                                        <p className="text-[11px] text-[#59554D] mt-0.5">
                                          Created {formatDate(ticket.created)}
                                        </p>
                                      )}
                                      {ticket.issues && ticket.issues.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {ticket.issues.map((issue, iIdx) => (
                                            <Badge key={iIdx} className={`text-xs ${
                                              issue.includes('SLA Breached') ? 'bg-[#FFE9E0] text-[#C23A00]' :
                                              issue.includes('SLA At Risk') ? 'bg-[#FFF7D1] text-[#6B5600]' :
                                              issue.includes('Idle') ? 'bg-[#FFF7D1] text-[#6B5600]' :
                                              issue.includes('High Priority') ? 'bg-[#FFE9E0] text-[#C23A00]' :
                                              'bg-[#DAD5CA] text-[#59554D]'
                                            }`}>
                                              {issue}
                                            </Badge>
                                          ))}
                                        </div>
                                      )}
                                      {ticket.linked_items && ticket.linked_items.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid={`linked-${ticket.key}`}>
                                          <Link2 className="w-3 h-3 text-[#59554D]" />
                                          <span className="text-[10px] uppercase tracking-wide text-[#59554D] mr-1">Linked:</span>
                                          {ticket.linked_items.map((li) => (
                                            <a
                                              key={li.key}
                                              href={`${JIRA_URL}/${li.key}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              title={`${li.link_type || ''}${li.summary ? ' — ' + li.summary : ''}${li.status ? ' (' + li.status + ')' : ''}`}
                                              className="font-mono text-[11px] px-1.5 py-0.5 rounded border border-[#DAD5CA] text-[#3B82F6] hover:bg-[#DAD5CA] hover:underline"
                                            >
                                              {li.key}
                                            </a>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* No Issues */}
                    {selectedCompany?.score_breakdown?.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-12">
                        <CheckCircle className="w-16 h-16 text-[#0E6B3F] mb-4" />
                        <h3 className="text-xl font-semibold text-[#0E6B3F]">Excellent Health!</h3>
                        <p className="text-[#59554D] mt-2">No issues detected for this company.</p>
                      </div>
                    )}
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Tickets Tab */}
            <TabsContent value="tickets" className="space-y-4">
              {/* Unassigned Tickets Section at Top with Assignment Suggestions */}
              {unassignedWithSuggestions.length > 0 && (
                <Card className="bg-[#FFFFFF] border-[#FFF7D1]">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg text-[#6B5600] flex items-center gap-2">
                        <Inbox className="w-5 h-5" />
                        Unassigned Tickets
                        <Badge className="bg-[#FFF7D1] text-[#6B5600]">{unassignedWithSuggestions.length}</Badge>
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={fetchUnassignedWithSuggestions}
                          disabled={loadingUnassignedSuggestions}
                          className="text-[#59554D] hover:text-[#1A1A1A] h-7 px-2"
                        >
                          <RefreshCw className={`w-3 h-3 ${loadingUnassignedSuggestions ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleRunAutoAssign}
                          disabled={autoAssignRunning}
                          className="bg-[#6B5600] hover:bg-[#FFF7D1] text-[#1A1A1A]"
                          data-testid="auto-assign-unassigned-btn"
                        >
                          {autoAssignRunning ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Route className="w-4 h-4 mr-2" />
                          )}
                          Auto-Assign All
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {unassignedWithSuggestions.slice(0, 6).map((ticket) => {
                          const orgKey = (ticket.organization || "").toLowerCase();
                          const clientInfo = vipLookup[orgKey];
                          return (
                            <div
                              key={ticket.key}
                              className={`p-3 rounded-lg border ${
                                clientInfo?.is_vip 
                                  ? 'bg-[#FFF8D6] border-[#FFF8D6]' 
                                  : 'bg-[#F4F2ED] border-[#DAD5CA]'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <a
                                  href={`${JIRA_URL}/${ticket.key}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#8A6D00] hover:underline font-mono text-xs"
                                >
                                  {ticket.key}
                                </a>
                                {clientInfo?.is_vip && (
                                  <Crown className="w-3 h-3 text-[#C9A400]" title="VIP Client" />
                                )}
                                {clientInfo?.has_sla && (
                                  <FileCheck className="w-3 h-3 text-[#1D4ED8]" title="SLA Client" />
                                )}
                                <PriorityBadge priority={ticket.priority} />
                              </div>
                              <p className="text-sm text-[#1A1A1A] truncate mb-2">{ticket.summary}</p>
                              <div className="flex items-center justify-between">
                                {ticket.organization ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/runbooks?company=${encodeURIComponent(ticket.organization)}`);
                                    }}
                                    className="text-xs text-[#8A857A] hover:text-[#8A6D00] hover:underline truncate"
                                    title="Open client runbook"
                                    data-testid={`runbook-link-${ticket.key}`}
                                  >
                                    {ticket.organization}
                                  </button>
                                ) : (
                                  <p className="text-xs text-[#8A857A]">Unknown</p>
                                )}
                                {ticket.suggested_assignee && (
                                  <div className="flex items-center gap-1">
                                    <UserCheck className="w-3 h-3 text-[#0E6B3F]" />
                                    <span className="text-xs text-[#0E6B3F] font-medium">{ticket.suggested_assignee.split(' ')[0]}</span>
                                  </div>
                                )}
                              </div>
                              {ticket.suggestion_reason && (
                                <p className="text-[10px] text-[#8A857A] mt-1">{ticket.suggestion_reason}</p>
                              )}
                            </div>
                          );
                        })}
                    </div>
                    {unassignedWithSuggestions.length > 6 && (
                      <p className="text-sm text-[#59554D] mt-3 text-center">
                        + {unassignedWithSuggestions.length - 6} more unassigned tickets
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              <div className="flex items-center justify-between">
                <h2 className="heading text-xl text-[#1A1A1A]">Ticket List</h2>
                <div className="flex items-center gap-3">
                  <div className="flex items-center rounded-lg border border-[#DAD5CA] bg-[#FFFFFF] p-0.5" data-testid="ticket-density-toggle">
                    <button
                      type="button"
                      onClick={() => toggleTicketDensity("comfortable")}
                      title="Comfortable rows"
                      data-testid="density-comfortable-btn"
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${ticketDensity === "comfortable" ? "bg-[#FFF8D6] text-[#1A1A1A]" : "text-[#8A857A] hover:text-[#1A1A1A]"}`}
                    >
                      <Rows2 className="w-3.5 h-3.5" /> Comfortable
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleTicketDensity("compact")}
                      title="Compact rows"
                      data-testid="density-compact-btn"
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${ticketDensity === "compact" ? "bg-[#FFF8D6] text-[#1A1A1A]" : "text-[#8A857A] hover:text-[#1A1A1A]"}`}
                    >
                      <Rows3 className="w-3.5 h-3.5" /> Compact
                    </button>
                  </div>
                  <Filter className="w-4 h-4 text-[#59554D]" />
                  <Select value={ticketFilter} onValueChange={setTicketFilter}>
                    <SelectTrigger className="w-[180px] bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]" data-testid="ticket-filter">
                      <SelectValue placeholder="Filter tickets" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <SelectItem value="all">All Tickets</SelectItem>
                      <SelectItem value="idle">Idle Tickets</SelectItem>
                      <SelectItem value="sla">SLA At Risk</SelectItem>
                      <SelectItem value="high">High Priority</SelectItem>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <ScrollArea className="h-[calc(100vh-220px)]">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#DAD5CA] hover:bg-transparent">
                        <TableHead className="text-[#59554D]">Ticket</TableHead>
                        <TableHead className="text-[#59554D]">Summary</TableHead>
                        <TableHead className="text-[#59554D]">Status</TableHead>
                        <TableHead className="text-[#59554D]">Priority</TableHead>
                        <TableHead className="text-[#59554D]">Assignee</TableHead>
                        <TableHead className="text-[#59554D]">Idle</TableHead>
                        <TableHead className="text-[#59554D]">SLA</TableHead>
                        <TableHead className="text-[#59554D]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTickets.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-[#59554D] py-8">
                            No tickets found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredTickets.map((ticket, idx) => {
                          const orgKey = (ticket.organization || "").toLowerCase();
                          const clientInfo = vipLookup[orgKey];
                          return (
                          <TableRow key={idx} className={`border-[#F4F2ED] hover:bg-[#F4F2ED] ${ticketDensity === "compact" ? "[&>td]:!py-1 [&>td]:text-xs" : "[&>td]:!py-3.5"}`}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="mono text-[#8A6D00]">{ticket.key}</span>
                                {clientInfo?.is_vip && (
                                  <Crown className="w-3.5 h-3.5 text-[#C9A400]" title="VIP Client" data-testid={`vip-icon-${ticket.key}`} />
                                )}
                                {clientInfo?.has_sla && (
                                  <FileCheck className="w-3.5 h-3.5 text-[#1D4ED8]" title="SLA Client" data-testid={`sla-icon-${ticket.key}`} />
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="max-w-[300px] truncate text-[#1A1A1A]">
                              {ticket.summary}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="border-[#DAD5CA] text-[#59554D]">
                                {ticket.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <PriorityBadge priority={ticket.priority} />
                            </TableCell>
                            <TableCell className="text-[#1A1A1A]">
                              {ticket.assignee || <span className="text-[#59554D]">Unassigned</span>}
                            </TableCell>
                            <TableCell>
                              <span className={ticket.idle_hours >= (rules?.idle_warning_hours || 4) ? "text-[#6B5600] font-medium" : "text-[#59554D]"}>
                                {ticket.idle_hours.toFixed(1)}h
                              </span>
                            </TableCell>
                            <TableCell>
                              <SLABadge ticket={ticket} />
                            </TableCell>
                            <TableCell>
                              <a
                                href={`${JIRA_URL}/${ticket.key}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#8A6D00] hover:text-[#FFF8D6]"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </TableCell>
                          </TableRow>
                        )})
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </Card>
            </TabsContent>

            {/* Infrastructure Tab */}
            <TabsContent value="infrastructure" className="space-y-6" data-testid="infrastructure-tab">
              {/* Infrastructure Sub-tabs */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="heading text-xl text-[#1A1A1A]">Infrastructure Health</h2>
                <div className="flex items-center gap-2 bg-[#FFFFFF] rounded-lg p-1 border border-[#DAD5CA]">
                  <Button
                    variant={infrastructureTab === "databases" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setInfrastructureTab("databases")}
                    className={infrastructureTab === "databases" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                    data-testid="infra-tab-databases"
                  >
                    <Database className="w-4 h-4 mr-2" />
                    Databases
                    {((databaseHealth?.summary?.critical || 0) + (databaseHealth?.summary?.warning || 0)) > 0 && (
                      <Badge className="ml-2 bg-[#C23A00] text-[#1A1A1A] text-xs">{(databaseHealth?.summary?.critical || 0) + (databaseHealth?.summary?.warning || 0)}</Badge>
                    )}
                  </Button>
                  <Button
                    variant={infrastructureTab === "wcf" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => { setInfrastructureTab("wcf"); fetchWcfHealth(); }}
                    className={infrastructureTab === "wcf" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                    data-testid="infra-tab-wcf"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Web Services
                    {(wcfHealth?.summary?.critical || 0) > 0 && (
                      <Badge className="ml-2 bg-[#C23A00] text-[#1A1A1A] text-xs">{wcfHealth?.summary?.critical}</Badge>
                    )}
                  </Button>
                  <Button
                    variant={infrastructureTab === "servers" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => { setInfrastructureTab("servers"); fetchServerHealth(); }}
                    className={infrastructureTab === "servers" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                    data-testid="infra-tab-servers"
                  >
                    <Server className="w-4 h-4 mr-2" />
                    Servers
                    {(serverHealth?.summary?.critical || 0) > 0 && (
                      <Badge className="ml-2 bg-[#C23A00] text-[#1A1A1A] text-xs">{serverHealth?.summary?.critical}</Badge>
                    )}
                  </Button>
                  <div className="w-px h-6 bg-[#DAD5CA] mx-1"></div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/sql-copilot")}
                    className="text-[#59554D] hover:text-[#1A1A1A] hover:bg-[#EFF6FF] border border-[#EFF6FF]"
                    data-testid="infra-tab-sql-copilot"
                  >
                    <Sparkles className="w-4 h-4 mr-2 text-[#1D4ED8]" />
                    SQL Copilot
                  </Button>
                </div>
              </div>

              {/* DATABASE HEALTH SUB-TAB */}
              {infrastructureTab === "databases" && (
                <>
              <div className="flex items-center justify-between">
                <div />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAddDbDialogOpen(true)}
                    className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                    data-testid="add-database-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Database
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={checkAllDatabases}
                    disabled={databaseLoading}
                    className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                    data-testid="check-databases-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${databaseLoading ? 'animate-spin' : ''}`} />
                    Check All
                  </Button>
                </div>
              </div>

              {/* Database Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4 text-center">
                    <Database className="w-6 h-6 mx-auto mb-2 text-[#59554D]" />
                    <p className="text-2xl font-bold text-[#1A1A1A]">{databaseHealth?.summary?.total || 0}</p>
                    <p className="text-xs text-[#59554D]">Total Databases</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4 text-center">
                    <CheckCircle className="w-6 h-6 mx-auto mb-2 text-[#0E6B3F]" />
                    <p className="text-2xl font-bold text-[#0E6B3F]">{databaseHealth?.summary?.healthy || 0}</p>
                    <p className="text-xs text-[#59554D]">Healthy</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4 text-center">
                    <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-[#6B5600]" />
                    <p className="text-2xl font-bold text-[#6B5600]">{databaseHealth?.summary?.warning || 0}</p>
                    <p className="text-xs text-[#59554D]">Warning</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4 text-center">
                    <XCircle className="w-6 h-6 mx-auto mb-2 text-[#C23A00]" />
                    <p className="text-2xl font-bold text-[#C23A00]">{databaseHealth?.summary?.critical || 0}</p>
                    <p className="text-xs text-[#59554D]">Critical</p>
                  </CardContent>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-6 h-6 mx-auto mb-2 text-[#3B82F6]" />
                    <p className="text-2xl font-bold text-[#3B82F6]">{databaseHealth?.summary?.unknown || 0}</p>
                    <p className="text-xs text-[#59554D]">Unknown</p>
                  </CardContent>
                </Card>
              </div>

              {/* Database List */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-[#1A1A1A] flex items-center gap-2">
                    <Server className="w-5 h-5" />
                    Client Databases
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(!databaseHealth?.databases || databaseHealth.databases.length === 0) ? (
                    <div className="text-center py-8">
                      <Database className="w-12 h-12 mx-auto mb-4 text-[#59554D] opacity-50" />
                      <p className="text-[#59554D] mb-4">No databases configured yet</p>
                      <Button
                        variant="outline"
                        onClick={() => setAddDbDialogOpen(true)}
                        className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Your First Database
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {databaseHealth.databases.map(db => (
                        <Card key={db.client_id} className={`bg-[#F4F2ED] border-l-4 ${
                          db.status === 'healthy' ? 'border-l-[#0E6B3F]' :
                          db.status === 'warning' ? 'border-l-[#6B5600]' :
                          db.status === 'critical' ? 'border-l-[#C23A00]' : 'border-l-[#3B82F6]'
                        }`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  {db.is_connected ? (
                                    <Wifi className="w-5 h-5 text-[#0E6B3F]" />
                                  ) : (
                                    <WifiOff className="w-5 h-5 text-[#C23A00]" />
                                  )}
                                  <h3 className="font-semibold text-[#1A1A1A]">{db.client_name}</h3>
                                  <Badge className={`text-xs ${
                                    db.status === 'healthy' ? 'bg-[#E3F3E9] text-[#0E6B3F]' :
                                    db.status === 'warning' ? 'bg-[#FFF7D1] text-[#6B5600]' :
                                    db.status === 'critical' ? 'bg-[#FFE9E0] text-[#C23A00]' : 'bg-[#EFF6FF] text-[#3B82F6]'
                                  }`}>
                                    {db.status?.toUpperCase()}
                                  </Badge>
                                  {db.company_name && (
                                    <Badge variant="outline" className="text-xs border-[#DAD5CA] text-[#59554D]">
                                      <Building2 className="w-3 h-3 mr-1" />
                                      {db.company_name}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-[#59554D] mb-3">
                                  {db.host} • {db.database}
                                  {db.metrics?.db_status && (
                                    <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                                      db.metrics.db_status === 'ONLINE' ? 'bg-[#E3F3E9] text-[#0E6B3F]' : 'bg-[#FFE9E0] text-[#C23A00]'
                                    }`}>
                                      {db.metrics.db_status}
                                    </span>
                                  )}
                                </p>
                                
                                {/* Metrics Grid - Row 1: Core Metrics */}
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                                  <div className="bg-[#FFFFFF] rounded-lg p-2">
                                    <p className="text-[#59554D] text-xs">Response Time</p>
                                    <p className={`font-mono ${db.response_time_ms > 1000 ? 'text-[#6B5600]' : 'text-[#1A1A1A]'}`}>
                                      {db.response_time_ms?.toFixed(0) || '-'}ms
                                    </p>
                                  </div>
                                  <div className="bg-[#FFFFFF] rounded-lg p-2">
                                    <p className="text-[#59554D] text-xs">DB Size</p>
                                    <p className="text-[#1A1A1A] font-mono">
                                      {db.metrics?.db_size_mb ? `${db.metrics.db_size_mb.toFixed(0)} MB` : '-'}
                                    </p>
                                  </div>
                                  <div className="bg-[#FFFFFF] rounded-lg p-2">
                                    <p className="text-[#59554D] text-xs">Long Queries</p>
                                    <p className={`font-mono ${
                                      db.metrics?.long_running_queries > 5 ? 'text-[#C23A00]' :
                                      db.metrics?.long_running_queries > 0 ? 'text-[#6B5600]' : 'text-[#0E6B3F]'
                                    }`}>
                                      {db.metrics?.long_running_queries ?? '-'}
                                    </p>
                                  </div>
                                  <div className="bg-[#FFFFFF] rounded-lg p-2">
                                    <p className="text-[#59554D] text-xs">Blocked</p>
                                    <p className={`font-mono ${
                                      db.metrics?.blocked_sessions > 3 ? 'text-[#C23A00]' :
                                      db.metrics?.blocked_sessions > 0 ? 'text-[#6B5600]' : 'text-[#0E6B3F]'
                                    }`}>
                                      {db.metrics?.blocked_sessions ?? '-'}
                                    </p>
                                  </div>
                                  <div className="bg-[#FFFFFF] rounded-lg p-2">
                                    <p className="text-[#59554D] text-xs">Failed Jobs</p>
                                    <p className={`font-mono ${
                                      db.metrics?.failed_jobs_24h > 3 ? 'text-[#C23A00]' :
                                      db.metrics?.failed_jobs_24h > 0 ? 'text-[#6B5600]' : 'text-[#0E6B3F]'
                                    }`}>
                                      {db.metrics?.failed_jobs_24h ?? '-'}
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Metrics Grid - Row 2: Health & Maintenance */}
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm mt-2">
                                  <div className="bg-[#FFFFFF] rounded-lg p-2">
                                    <p className="text-[#59554D] text-xs">Last Backup</p>
                                    <p className={`font-mono text-xs ${
                                      db.metrics?.backup_age_hours > 48 ? 'text-[#C23A00]' :
                                      db.metrics?.backup_age_hours > 24 ? 'text-[#6B5600]' : 'text-[#1A1A1A]'
                                    }`}>
                                      {db.metrics?.backup_age_hours != null ? `${db.metrics.backup_age_hours}h ago` : '-'}
                                    </p>
                                  </div>
                                  <div className="bg-[#FFFFFF] rounded-lg p-2">
                                    <p className="text-[#59554D] text-xs">Last CHECKDB</p>
                                    <p className={`font-mono text-xs ${
                                      db.metrics?.checkdb_age_days > 30 ? 'text-[#C23A00]' :
                                      db.metrics?.checkdb_age_days > 14 ? 'text-[#6B5600]' : 'text-[#1A1A1A]'
                                    }`}>
                                      {db.metrics?.checkdb_age_days != null ? `${db.metrics.checkdb_age_days}d ago` : '-'}
                                    </p>
                                  </div>
                                  <div className="bg-[#FFFFFF] rounded-lg p-2">
                                    <p className="text-[#59554D] text-xs">TempDB</p>
                                    <p className={`font-mono ${
                                      db.metrics?.tempdb_usage_percent > 90 ? 'text-[#C23A00]' :
                                      db.metrics?.tempdb_usage_percent > 70 ? 'text-[#6B5600]' : 'text-[#1A1A1A]'
                                    }`}>
                                      {db.metrics?.tempdb_usage_percent != null ? `${db.metrics.tempdb_usage_percent.toFixed(0)}%` : '-'}
                                    </p>
                                  </div>
                                  <div className="bg-[#FFFFFF] rounded-lg p-2">
                                    <p className="text-[#59554D] text-xs">Frag. Indexes</p>
                                    <p className={`font-mono ${
                                      db.metrics?.fragmented_indexes > 25 ? 'text-[#C23A00]' :
                                      db.metrics?.fragmented_indexes > 10 ? 'text-[#6B5600]' : 'text-[#1A1A1A]'
                                    }`}>
                                      {db.metrics?.fragmented_indexes ?? '-'}
                                    </p>
                                  </div>
                                  <div className="bg-[#FFFFFF] rounded-lg p-2">
                                    <p className="text-[#59554D] text-xs">Cache Hit %</p>
                                    <p className={`font-mono ${
                                      db.metrics?.buffer_cache_hit_ratio < 90 ? 'text-[#C23A00]' :
                                      db.metrics?.buffer_cache_hit_ratio < 95 ? 'text-[#6B5600]' : 'text-[#0E6B3F]'
                                    }`}>
                                      {db.metrics?.buffer_cache_hit_ratio != null ? `${db.metrics.buffer_cache_hit_ratio.toFixed(1)}%` : '-'}
                                    </p>
                                  </div>
                                </div>
                                
                                {db.error_message && (
                                  <div className="mt-3 p-2 bg-[#FFE9E0] rounded border border-[#FFE9E0]">
                                    <p className="text-sm text-[#C23A00]">{db.error_message}</p>
                                  </div>
                                )}
                                
                                <p className="text-xs text-[#59554D] mt-3">
                                  Last checked: {db.last_checked ? formatDateTime(db.last_checked) : 'Never'}
                                </p>
                              </div>
                              
                              <div className="flex flex-col gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setEditingDb({
                                      client_id: db.client_id,
                                      client_name: db.client_name,
                                      host: db.host,
                                      port: 1433,
                                      database: db.database,
                                      username: "",
                                      password: "",
                                      company_name: db.company_name || ""
                                    });
                                    setEditDbDialogOpen(true);
                                  }}
                                  className="text-[#59554D] hover:text-[#8A6D00] hover:bg-[#FFF8D6]"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveDatabase(db.client_id)}
                                  className="text-[#59554D] hover:text-[#C23A00] hover:bg-[#FFE9E0]"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Edit Database Dialog */}
              <Dialog open={editDbDialogOpen} onOpenChange={setEditDbDialogOpen}>
                <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Edit className="w-5 h-5" />
                      Edit Database: {editingDb?.client_name}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label className="text-sm text-[#59554D]">Client Name</Label>
                      <Input
                        value={editingDb?.client_name || ""}
                        onChange={e => setEditingDb({...editingDb, client_name: e.target.value})}
                        className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-2">
                        <Label className="text-sm text-[#59554D]">Host/IP</Label>
                        <Input
                          value={editingDb?.host || ""}
                          onChange={e => setEditingDb({...editingDb, host: e.target.value})}
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-[#59554D]">Port</Label>
                        <Input
                          type="number"
                          value={editingDb?.port || 1433}
                          onChange={e => setEditingDb({...editingDb, port: parseInt(e.target.value) || 1433})}
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-[#59554D]">Database Name</Label>
                      <Input
                        value={editingDb?.database || ""}
                        onChange={e => setEditingDb({...editingDb, database: e.target.value})}
                        className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-[#59554D]">Username</Label>
                        <Input
                          value={editingDb?.username || ""}
                          onChange={e => setEditingDb({...editingDb, username: e.target.value})}
                          placeholder="Leave blank to keep current"
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-[#59554D]">Password</Label>
                        <Input
                          type="password"
                          value={editingDb?.password || ""}
                          onChange={e => setEditingDb({...editingDb, password: e.target.value})}
                          placeholder="Leave blank to keep current"
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-[#59554D]">Link to Company</Label>
                      <Input
                        value={editingDb?.company_name || ""}
                        onChange={e => setEditingDb({...editingDb, company_name: e.target.value})}
                        placeholder="e.g., ACME Corporation"
                        className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                      />
                      <p className="text-xs text-[#59554D] mt-1">Links database health to Company Health Score</p>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => { setEditDbDialogOpen(false); setEditingDb(null); }}
                        className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleEditDatabase}
                        className="bg-[#FFD108] hover:bg-[#FFD108] text-[#1A1A1A]"
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Add Database Dialog */}
              <Dialog open={addDbDialogOpen} onOpenChange={setAddDbDialogOpen}>
                <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Database className="w-5 h-5" />
                      Add Database Configuration
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-[#59554D]">Client ID *</Label>
                        <Input
                          placeholder="e.g., acme_prod"
                          value={newDbConfig.client_id}
                          onChange={e => setNewDbConfig({...newDbConfig, client_id: e.target.value})}
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-[#59554D]">Client Name *</Label>
                        <Input
                          placeholder="e.g., ACME Corp"
                          value={newDbConfig.client_name}
                          onChange={e => setNewDbConfig({...newDbConfig, client_name: e.target.value})}
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-2">
                        <Label className="text-sm text-[#59554D]">Host/IP *</Label>
                        <Input
                          placeholder="e.g., 192.168.1.100"
                          value={newDbConfig.host}
                          onChange={e => setNewDbConfig({...newDbConfig, host: e.target.value})}
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-[#59554D]">Port</Label>
                        <Input
                          type="number"
                          placeholder="1433"
                          value={newDbConfig.port}
                          onChange={e => setNewDbConfig({...newDbConfig, port: parseInt(e.target.value) || 1433})}
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-[#59554D]">Database Name</Label>
                      <Input
                        placeholder="e.g., ACME_Production"
                        value={newDbConfig.database}
                        onChange={e => setNewDbConfig({...newDbConfig, database: e.target.value})}
                        className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-[#59554D]">Username *</Label>
                        <Input
                          placeholder="sa"
                          value={newDbConfig.username}
                          onChange={e => setNewDbConfig({...newDbConfig, username: e.target.value})}
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-[#59554D]">Password *</Label>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          value={newDbConfig.password}
                          onChange={e => setNewDbConfig({...newDbConfig, password: e.target.value})}
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-[#59554D]">Link to Company (optional)</Label>
                      <Input
                        placeholder="e.g., ACME Corporation"
                        value={newDbConfig.company_name}
                        onChange={e => setNewDbConfig({...newDbConfig, company_name: e.target.value})}
                        className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                      />
                      <p className="text-xs text-[#59554D] mt-1">Links database health to Company Health Score</p>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setAddDbDialogOpen(false)}
                        className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleAddDatabase}
                        className="bg-[#FFD108] hover:bg-[#FFD108] text-[#1A1A1A]"
                      >
                        Add Database
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
                </>
              )}

              {/* WCF WEB SERVICES SUB-TAB */}
              {infrastructureTab === "wcf" && (
                <>
                  <div className="flex items-center justify-between">
                    <div />
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAddWcfDialogOpen(true)}
                        className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                        data-testid="add-wcf-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add WCF Service
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={checkAllWcfServices}
                        disabled={wcfLoading}
                        className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                        data-testid="check-wcf-btn"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${wcfLoading ? 'animate-spin' : ''}`} />
                        Check All
                      </Button>
                    </div>
                  </div>

                  {/* WCF Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <CardContent className="p-4 text-center">
                        <Globe className="w-6 h-6 mx-auto mb-2 text-[#59554D]" />
                        <p className="text-2xl font-bold text-[#1A1A1A]">{wcfHealth?.summary?.total_services || 0}</p>
                        <p className="text-xs text-[#59554D]">Total Services</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <CardContent className="p-4 text-center">
                        <CheckCircle className="w-6 h-6 mx-auto mb-2 text-[#0E6B3F]" />
                        <p className="text-2xl font-bold text-[#0E6B3F]">{wcfHealth?.summary?.healthy || 0}</p>
                        <p className="text-xs text-[#59554D]">Healthy</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <CardContent className="p-4 text-center">
                        <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-[#6B5600]" />
                        <p className="text-2xl font-bold text-[#6B5600]">{wcfHealth?.summary?.warning || 0}</p>
                        <p className="text-xs text-[#59554D]">Warning</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <CardContent className="p-4 text-center">
                        <XCircle className="w-6 h-6 mx-auto mb-2 text-[#C23A00]" />
                        <p className="text-2xl font-bold text-[#C23A00]">{wcfHealth?.summary?.critical || 0}</p>
                        <p className="text-xs text-[#59554D]">Critical</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <CardContent className="p-4 text-center">
                        <Clock className="w-6 h-6 mx-auto mb-2 text-[#3B82F6]" />
                        <p className="text-2xl font-bold text-[#3B82F6]">{Math.round(wcfHealth?.summary?.average_response_ms || 0)}ms</p>
                        <p className="text-xs text-[#59554D]">Avg Response</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* WCF Services List */}
                  <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                    <CardHeader>
                      <CardTitle className="text-[#1A1A1A] flex items-center gap-2">
                        <Globe className="w-5 h-5" />
                        WCF Web Services
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(!wcfHealth?.services || wcfHealth.services.length === 0) ? (
                        <div className="text-center py-8">
                          <Globe className="w-12 h-12 mx-auto mb-4 text-[#59554D] opacity-50" />
                          <p className="text-[#59554D] mb-4">No WCF services configured yet</p>
                          <Button
                            variant="outline"
                            onClick={() => setAddWcfDialogOpen(true)}
                            className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Your First WCF Service
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {wcfHealth.services.map(svc => (
                            <Card key={svc.service_id} className={`bg-[#F4F2ED] border-l-4 ${
                              svc.status === 'healthy' ? 'border-l-[#0E6B3F]' :
                              svc.status === 'warning' ? 'border-l-[#6B5600]' :
                              svc.status === 'critical' ? 'border-l-[#C23A00]' : 'border-l-[#3B82F6]'
                            }`}>
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {svc.status === 'healthy' ? (
                                      <CheckCircle className="w-5 h-5 text-[#0E6B3F]" />
                                    ) : svc.status === 'warning' ? (
                                      <AlertTriangle className="w-5 h-5 text-[#6B5600]" />
                                    ) : svc.status === 'critical' ? (
                                      <XCircle className="w-5 h-5 text-[#C23A00]" />
                                    ) : (
                                      <Clock className="w-5 h-5 text-[#3B82F6]" />
                                    )}
                                    <div>
                                      <h4 className="font-medium text-[#1A1A1A]">{svc.client_name}</h4>
                                      <p className="text-xs text-[#59554D]">{svc.service_url}</p>
                                      {svc.company_name && (
                                        <p className="text-xs text-[#8A6D00]">{svc.company_name}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-[#1A1A1A]">{Math.round(svc.response_time_ms)}ms</p>
                                      <div className="flex items-center gap-2 mt-1">
                                        {svc.is_reachable && <Badge className="bg-[#E3F3E9] text-[#0E6B3F] text-xs">Reachable</Badge>}
                                        {svc.wsdl_accessible && <Badge className="bg-[#FFF8D6] text-[#8A6D00] text-xs">WSDL OK</Badge>}
                                        {!svc.is_reachable && <Badge className="bg-[#FFE9E0] text-[#C23A00] text-xs">Unreachable</Badge>}
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteWcfService(svc.service_id)}
                                      className="text-[#C23A00] hover:text-[#C23A00] hover:bg-[#FFE9E0]"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                                {svc.error_message && (
                                  <p className="mt-2 text-xs text-[#C23A00]">Error: {svc.error_message}</p>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Add WCF Service Dialog */}
                  <Dialog open={addWcfDialogOpen} onOpenChange={setAddWcfDialogOpen}>
                    <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="text-[#1A1A1A] flex items-center gap-2">
                          <Globe className="w-5 h-5 text-[#8A6D00]" />
                          Add WCF Service
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm text-[#59554D]">Service ID *</label>
                            <input
                              type="text"
                              value={newWcfConfig.service_id}
                              onChange={(e) => setNewWcfConfig({...newWcfConfig, service_id: e.target.value})}
                              placeholder="e.g., abc617"
                              className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A]"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm text-[#59554D]">Client Name *</label>
                            <input
                              type="text"
                              value={newWcfConfig.client_name}
                              onChange={(e) => setNewWcfConfig({...newWcfConfig, client_name: e.target.value})}
                              placeholder="e.g., ABC Company"
                              className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A]"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm text-[#59554D]">Service URL *</label>
                          <input
                            type="text"
                            value={newWcfConfig.service_url}
                            onChange={(e) => setNewWcfConfig({...newWcfConfig, service_url: e.target.value})}
                            placeholder="http://abc617.equipr.software/data.svc"
                            className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A]"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm text-[#59554D]">Company Name</label>
                          <input
                            type="text"
                            value={newWcfConfig.company_name}
                            onChange={(e) => setNewWcfConfig({...newWcfConfig, company_name: e.target.value})}
                            placeholder="e.g., ABC"
                            className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A]"
                          />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <Button variant="outline" onClick={() => setAddWcfDialogOpen(false)} className="border-[#DAD5CA] text-[#59554D]">Cancel</Button>
                          <Button onClick={addWcfService} className="bg-[#FFD108] hover:bg-[#FFD108] text-[#1A1A1A]">Add Service</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              )}

              {/* SERVERS SUB-TAB */}
              {infrastructureTab === "servers" && (
                <>
                  <div className="flex items-center justify-between">
                    <div />
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAddServerDialogOpen(true)}
                        className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                        data-testid="add-server-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Server
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={checkAllServers}
                        disabled={serverLoading}
                        className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                        data-testid="check-servers-btn"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${serverLoading ? 'animate-spin' : ''}`} />
                        Check All
                      </Button>
                    </div>
                  </div>

                  {/* Server Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <CardContent className="p-4 text-center">
                        <Server className="w-6 h-6 mx-auto mb-2 text-[#59554D]" />
                        <p className="text-2xl font-bold text-[#1A1A1A]">{serverHealth?.summary?.total_servers || 0}</p>
                        <p className="text-xs text-[#59554D]">Total Servers</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <CardContent className="p-4 text-center">
                        <CheckCircle className="w-6 h-6 mx-auto mb-2 text-[#0E6B3F]" />
                        <p className="text-2xl font-bold text-[#0E6B3F]">{serverHealth?.summary?.healthy || 0}</p>
                        <p className="text-xs text-[#59554D]">Healthy</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <CardContent className="p-4 text-center">
                        <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-[#6B5600]" />
                        <p className="text-2xl font-bold text-[#6B5600]">{serverHealth?.summary?.warning || 0}</p>
                        <p className="text-xs text-[#59554D]">Warning</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <CardContent className="p-4 text-center">
                        <XCircle className="w-6 h-6 mx-auto mb-2 text-[#C23A00]" />
                        <p className="text-2xl font-bold text-[#C23A00]">{serverHealth?.summary?.critical || 0}</p>
                        <p className="text-xs text-[#59554D]">Critical</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Servers List */}
                  <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                    <CardHeader>
                      <CardTitle className="text-[#1A1A1A] flex items-center gap-2">
                        <Server className="w-5 h-5" />
                        Monitored Servers
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(!serverHealth?.servers || serverHealth.servers.length === 0) ? (
                        <div className="text-center py-8">
                          <Server className="w-12 h-12 mx-auto mb-4 text-[#59554D] opacity-50" />
                          <p className="text-[#59554D] mb-4">No servers configured yet</p>
                          <Button
                            variant="outline"
                            onClick={() => setAddServerDialogOpen(true)}
                            className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Your First Server
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {serverHealth.servers.map(srv => (
                            <Card key={srv.server_id} className={`bg-[#F4F2ED] border-l-4 ${
                              srv.status === 'healthy' ? 'border-l-[#0E6B3F]' :
                              srv.status === 'warning' ? 'border-l-[#6B5600]' :
                              srv.status === 'critical' ? 'border-l-[#C23A00]' : 'border-l-[#3B82F6]'
                            }`}>
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {srv.status === 'healthy' ? (
                                      <CheckCircle className="w-5 h-5 text-[#0E6B3F]" />
                                    ) : srv.status === 'warning' ? (
                                      <AlertTriangle className="w-5 h-5 text-[#6B5600]" />
                                    ) : srv.status === 'critical' ? (
                                      <XCircle className="w-5 h-5 text-[#C23A00]" />
                                    ) : (
                                      <Clock className="w-5 h-5 text-[#3B82F6]" />
                                    )}
                                    <div>
                                      <h4 className="font-medium text-[#1A1A1A]">{srv.server_name}</h4>
                                      <p className="text-xs text-[#59554D]">{srv.hostname}</p>
                                      <div className="flex items-center gap-2 mt-1">
                                        <Badge className="bg-[#DAD5CA] text-[#59554D] text-xs">{srv.server_type}</Badge>
                                        {srv.company_name && (
                                          <Badge className="bg-[#FFF8D6] text-[#8A6D00] text-xs">{srv.company_name}</Badge>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    {/* Server Metrics */}
                                    {srv.metrics && Object.keys(srv.metrics).length > 0 && (
                                      <div className="flex items-center gap-3 text-xs">
                                        {srv.metrics.cpu_usage !== undefined && (
                                          <div className="text-center">
                                            <p className={`font-medium ${srv.metrics.cpu_usage > 80 ? 'text-[#C23A00]' : srv.metrics.cpu_usage > 60 ? 'text-[#6B5600]' : 'text-[#0E6B3F]'}`}>
                                              {srv.metrics.cpu_usage}%
                                            </p>
                                            <p className="text-[#59554D]">CPU</p>
                                          </div>
                                        )}
                                        {srv.metrics.memory_usage !== undefined && (
                                          <div className="text-center">
                                            <p className={`font-medium ${srv.metrics.memory_usage > 85 ? 'text-[#C23A00]' : srv.metrics.memory_usage > 70 ? 'text-[#6B5600]' : 'text-[#0E6B3F]'}`}>
                                              {srv.metrics.memory_usage}%
                                            </p>
                                            <p className="text-[#59554D]">RAM</p>
                                          </div>
                                        )}
                                        {srv.metrics.disk_usage !== undefined && (
                                          <div className="text-center">
                                            <p className={`font-medium ${srv.metrics.disk_usage > 85 ? 'text-[#C23A00]' : srv.metrics.disk_usage > 70 ? 'text-[#6B5600]' : 'text-[#0E6B3F]'}`}>
                                              {srv.metrics.disk_usage}%
                                            </p>
                                            <p className="text-[#59554D]">Disk</p>
                                          </div>
                                        )}
                                        {srv.pending_updates > 0 && (
                                          <div className="text-center">
                                            <p className="font-medium text-[#6B5600]">{srv.pending_updates}</p>
                                            <p className="text-[#59554D]">Updates</p>
                                          </div>
                                        )}
                                        {srv.metrics.uptime_days !== undefined && (
                                          <div className="text-center">
                                            <p className="font-medium text-[#1A1A1A]">{srv.metrics.uptime_days}d</p>
                                            <p className="text-[#59554D]">Uptime</p>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    <div className="text-right">
                                      <p className="text-sm font-medium text-[#1A1A1A]">{Math.round(srv.response_time_ms)}ms</p>
                                      {srv.is_reachable ? (
                                        <Badge className="bg-[#E3F3E9] text-[#0E6B3F] text-xs">Online</Badge>
                                      ) : (
                                        <Badge className="bg-[#FFE9E0] text-[#C23A00] text-xs">Offline</Badge>
                                      )}
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteServer(srv.server_id)}
                                      className="text-[#C23A00] hover:text-[#C23A00] hover:bg-[#FFE9E0]"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                                {srv.error_message && (
                                  <p className="mt-2 text-xs text-[#C23A00]">Error: {srv.error_message}</p>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Add Server Dialog */}
                  <Dialog open={addServerDialogOpen} onOpenChange={setAddServerDialogOpen}>
                    <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="text-[#1A1A1A] flex items-center gap-2">
                          <Server className="w-5 h-5 text-[#8A6D00]" />
                          Add Server
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm text-[#59554D]">Server ID *</label>
                            <input
                              type="text"
                              value={newServerConfig.server_id}
                              onChange={(e) => setNewServerConfig({...newServerConfig, server_id: e.target.value})}
                              placeholder="e.g., db-server-01"
                              className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A]"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm text-[#59554D]">Server Name *</label>
                            <input
                              type="text"
                              value={newServerConfig.server_name}
                              onChange={(e) => setNewServerConfig({...newServerConfig, server_name: e.target.value})}
                              placeholder="e.g., Production DB"
                              className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A]"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm text-[#59554D]">Hostname / IP *</label>
                          <input
                            type="text"
                            value={newServerConfig.hostname}
                            onChange={(e) => setNewServerConfig({...newServerConfig, hostname: e.target.value})}
                            placeholder="e.g., 192.168.1.100 or server.domain.com"
                            className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A]"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm text-[#59554D]">Server Type</label>
                            <select
                              value={newServerConfig.server_type}
                              onChange={(e) => setNewServerConfig({...newServerConfig, server_type: e.target.value})}
                              className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A]"
                            >
                              <option value="database">Database</option>
                              <option value="web">Web Server</option>
                              <option value="application">Application</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm text-[#59554D]">Company Name</label>
                            <input
                              type="text"
                              value={newServerConfig.company_name}
                              onChange={(e) => setNewServerConfig({...newServerConfig, company_name: e.target.value})}
                              placeholder="e.g., ABC"
                              className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A]"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <Button variant="outline" onClick={() => setAddServerDialogOpen(false)} className="border-[#DAD5CA] text-[#59554D]">Cancel</Button>
                          <Button onClick={addServer} className="bg-[#FFD108] hover:bg-[#FFD108] text-[#1A1A1A]">Add Server</Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              )}
            </TabsContent>

            {/* Scripts Tab */}
            <TabsContent value="scripts" className="space-y-6" data-testid="scripts-tab">
              <div className="flex items-center justify-between">
                <h2 className="heading text-xl text-[#1A1A1A]">SQL Script Library</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedScript(null);
                    setNewScript({ script_id: "", name: "", description: "", sql: "", permission: "read_only", category: "General", use_custom_credentials: false, custom_username: "", custom_password: "" });
                    setScriptDialogOpen(true);
                  }}
                  className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                  data-testid="add-script-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Script
                </Button>
              </div>

              {/* Script Categories */}
              <div className="flex gap-2 flex-wrap">
                {scriptCategories.map(cat => (
                  <Badge key={cat} variant="outline" className="border-[#DAD5CA] text-[#59554D]">
                    {cat}
                  </Badge>
                ))}
              </div>

              {/* Scripts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {scripts.map(script => (
                  <Card key={script.script_id} className="bg-[#FFFFFF] border-[#DAD5CA] hover:border-[#FFF8D6] transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Code className="w-5 h-5 text-[#8A6D00]" />
                          <h3 className="font-semibold text-[#1A1A1A]">{script.name}</h3>
                        </div>
                        <div className="flex gap-1">
                          {script.has_custom_credentials && (
                            <Badge className="text-xs bg-[#EFF6FF] text-[#1D4ED8]">
                              Elevated
                            </Badge>
                          )}
                          <Badge className={`text-xs ${
                            script.permission === 'read_only' 
                              ? 'bg-[#E3F3E9] text-[#0E6B3F]' 
                              : 'bg-[#FFF7D1] text-[#6B5600]'
                          }`}>
                            {script.permission === 'read_only' ? 'Read Only' : 'Write'}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm text-[#59554D] mb-3 line-clamp-2">{script.description}</p>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="border-[#DAD5CA] text-[#59554D] text-xs">
                          {script.category}
                        </Badge>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedScript(script);
                              setNewScript({
                                script_id: script.script_id,
                                name: script.name,
                                description: script.description,
                                sql: script.sql,
                                permission: script.permission,
                                category: script.category,
                                use_custom_credentials: script.has_custom_credentials || false,
                                custom_username: "",
                                custom_password: ""
                              });
                              setScriptDialogOpen(true);
                            }}
                            className="text-[#59554D] hover:text-[#8A6D00]"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedScript(script);
                              setScriptResults(null);
                              setSelectedDbForScript("");
                              setRunScriptDialogOpen(true);
                            }}
                            className="text-[#59554D] hover:text-[#0E6B3F]"
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {scripts.length === 0 && (
                <div className="text-center py-12">
                  <FileCode className="w-12 h-12 mx-auto mb-4 text-[#59554D] opacity-50" />
                  <p className="text-[#59554D]">No scripts in library</p>
                </div>
              )}

              {/* Create/Edit Script Dialog */}
              <Dialog open={scriptDialogOpen} onOpenChange={setScriptDialogOpen}>
                <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Code className="w-5 h-5" />
                      {selectedScript ? 'Edit Script' : 'Create New Script'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-[#59554D]">Script ID *</Label>
                        <Input
                          placeholder="e.g., missing_indexes"
                          value={newScript.script_id}
                          onChange={e => setNewScript({...newScript, script_id: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                          disabled={!!selectedScript}
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-[#59554D]">Name *</Label>
                        <Input
                          placeholder="e.g., Missing Indexes Analysis"
                          value={newScript.name}
                          onChange={e => setNewScript({...newScript, name: e.target.value})}
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-[#59554D]">Description</Label>
                      <Input
                        placeholder="What does this script do?"
                        value={newScript.description}
                        onChange={e => setNewScript({...newScript, description: e.target.value})}
                        className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-[#59554D]">Category</Label>
                        <Input
                          placeholder="e.g., Performance, Backup, Storage"
                          value={newScript.category}
                          onChange={e => setNewScript({...newScript, category: e.target.value})}
                          className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-[#59554D]">Permission</Label>
                        <Select value={newScript.permission} onValueChange={val => setNewScript({...newScript, permission: val})}>
                          <SelectTrigger className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                            <SelectItem value="read_only" className="text-[#1A1A1A]">Read Only (SELECT)</SelectItem>
                            <SelectItem value="write_allowed" className="text-[#1A1A1A]">Write Allowed (INSERT/UPDATE/CREATE)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-[#59554D]">SQL Query *</Label>
                      <Textarea
                        placeholder="SELECT * FROM ..."
                        value={newScript.sql}
                        onChange={e => setNewScript({...newScript, sql: e.target.value})}
                        className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] font-mono text-sm min-h-[200px]"
                      />
                    </div>
                    
                    {/* Elevated Credentials Section */}
                    <div className="border border-[#DAD5CA] rounded-lg p-4 bg-[#FFFFFF]">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <Label className="text-sm text-[#1A1A1A] font-medium">Elevated SQL Credentials</Label>
                          <p className="text-xs text-[#59554D] mt-1">Use a different SQL account with higher permissions (e.g., DBA account)</p>
                        </div>
                        <Switch
                          checked={newScript.use_custom_credentials}
                          onCheckedChange={checked => setNewScript({...newScript, use_custom_credentials: checked})}
                        />
                      </div>
                      
                      {newScript.use_custom_credentials && (
                        <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-[#DAD5CA]">
                          <div>
                            <Label className="text-sm text-[#59554D]">SQL Username</Label>
                            <Input
                              placeholder="e.g., sa or dba_user"
                              value={newScript.custom_username}
                              onChange={e => setNewScript({...newScript, custom_username: e.target.value})}
                              className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                            />
                          </div>
                          <div>
                            <Label className="text-sm text-[#59554D]">SQL Password</Label>
                            <Input
                              type="password"
                              placeholder="••••••••"
                              value={newScript.custom_password}
                              onChange={e => setNewScript({...newScript, custom_password: e.target.value})}
                              className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                            />
                          </div>
                          <p className="col-span-2 text-xs text-[#6B5600]">
                            ⚠️ Credentials are stored securely and used only when running this script
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-between pt-4">
                      {selectedScript && selectedScript.created_by !== 'System' && (
                        <Button
                          variant="outline"
                          onClick={() => {
                            handleDeleteScript(selectedScript.script_id);
                            setScriptDialogOpen(false);
                          }}
                          className="border-[#C23A00] text-[#C23A00] hover:bg-[#FFE9E0]"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      )}
                      <div className="flex gap-2 ml-auto">
                        <Button
                          variant="outline"
                          onClick={() => setScriptDialogOpen(false)}
                          className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveScript}
                          className="bg-[#FFD108] hover:bg-[#FFD108] text-[#1A1A1A]"
                        >
                          {selectedScript ? 'Save Changes' : 'Create Script'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Run Script Dialog */}
              <Dialog open={runScriptDialogOpen} onOpenChange={setRunScriptDialogOpen}>
                <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Play className="w-5 h-5 text-[#0E6B3F]" />
                      Run Script: {selectedScript?.name}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4 flex-1 overflow-hidden flex flex-col">
                    {/* Database Selection */}
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <Label className="text-sm text-[#59554D]">Select Database</Label>
                        <Select value={selectedDbForScript} onValueChange={setSelectedDbForScript}>
                          <SelectTrigger className="mt-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]">
                            <SelectValue placeholder="Choose a database..." />
                          </SelectTrigger>
                          <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                            {databaseHealth?.databases?.map(db => (
                              <SelectItem key={db.client_id} value={db.client_id} className="text-[#1A1A1A]">
                                <span className="flex items-center gap-2">
                                  {db.is_connected ? (
                                    <Wifi className="w-3 h-3 text-[#0E6B3F]" />
                                  ) : (
                                    <WifiOff className="w-3 h-3 text-[#C23A00]" />
                                  )}
                                  {db.client_name} ({db.host})
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleRunScript}
                        disabled={!selectedDbForScript || runningScript}
                        className="bg-[#0E6B3F] hover:bg-[#28B94C] text-[#1A1A1A] mt-6"
                      >
                        {runningScript ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4 mr-2" />
                        )}
                        Execute
                      </Button>
                    </div>

                    {/* Script Preview */}
                    <div>
                      <Label className="text-sm text-[#59554D]">SQL Query</Label>
                      <pre className="mt-1 p-3 bg-[#F4F2ED] border border-[#DAD5CA] rounded text-xs text-[#59554D] font-mono overflow-x-auto max-h-[100px]">
                        {selectedScript?.sql}
                      </pre>
                    </div>

                    {/* Results */}
                    {scriptResults && (
                      <div className="flex-1 overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-sm text-[#59554D]">
                            Results {scriptResults.success && `(${scriptResults.row_count} rows in ${scriptResults.execution_time_seconds}s)`}
                          </Label>
                          {scriptResults.success && scriptResults.rows?.length > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={exportToCsv}
                              className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Export CSV
                            </Button>
                          )}
                        </div>
                        
                        {!scriptResults.success ? (
                          <div className="p-4 bg-[#FFE9E0] border border-[#FFE9E0] rounded">
                            <p className="text-[#C23A00]">{scriptResults.error}</p>
                          </div>
                        ) : scriptResults.rows?.length > 0 ? (
                          <div className="flex-1 overflow-auto border border-[#DAD5CA] rounded">
                            <Table>
                              <TableHeader>
                                <TableRow className="border-[#DAD5CA] bg-[#F4F2ED]">
                                  {scriptResults.columns.map(col => (
                                    <TableHead key={col} className="text-[#59554D] text-xs whitespace-nowrap">
                                      {col}
                                    </TableHead>
                                  ))}
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {scriptResults.rows.slice(0, 100).map((row, i) => (
                                  <TableRow key={i} className="border-[#DAD5CA]">
                                    {scriptResults.columns.map(col => (
                                      <TableCell key={col} className="text-[#1A1A1A] text-xs py-2 max-w-[300px] truncate">
                                        {row[col] !== null && row[col] !== undefined ? String(row[col]) : '-'}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {scriptResults.rows.length > 100 && (
                              <p className="text-center text-xs text-[#59554D] py-2">
                                Showing 100 of {scriptResults.rows.length} rows. Export CSV for full results.
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="p-4 bg-[#E3F3E9] border border-[#E3F3E9] rounded">
                            <p className="text-[#0E6B3F]">{scriptResults.message || 'Query executed successfully. No rows returned.'}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {!databaseHealth?.databases?.length && (
                      <div className="text-center py-8">
                        <Database className="w-12 h-12 mx-auto mb-4 text-[#59554D] opacity-50" />
                        <p className="text-[#59554D]">No databases configured. Add a database first.</p>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Compliance Tab */}
            <TabsContent value="compliance" className="space-y-6" data-testid="compliance-tab">
              <div className="flex items-center justify-between">
                <h2 className="heading text-xl text-[#1A1A1A]">Compliance Dashboard</h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/compliance/check`);
                      toast.success("Compliance checks triggered");
                      setTimeout(fetchDashboardData, 3000);
                    } catch (error) {
                      toast.error("Failed to trigger compliance checks");
                    }
                  }}
                  className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Run Checks
                </Button>
              </div>

              {/* Compliance Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Callback Compliance */}
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                      <PhoneMissed className="w-5 h-5 text-[#C23A00]" />
                      Callback SLA
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold mb-2">
                      <span className={complianceData?.callback_compliance?.overdue_count > 0 ? 'text-[#C23A00]' : 'text-[#0E6B3F]'}>
                        {complianceData?.callback_compliance?.overdue_count || 0}
                      </span>
                      <span className="text-[#59554D] text-lg ml-2">overdue</span>
                    </div>
                    <div className="text-sm text-[#59554D]">
                      {complianceData?.callback_compliance?.pending_count || 0} pending callbacks
                    </div>
                    <div className="text-xs text-[#59554D] mt-2">
                      {complianceData?.callback_compliance?.alerts_sent_today || 0} Teams alerts sent today
                    </div>
                  </CardContent>
                </Card>

                {/* Ticket Compliance */}
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                      <Ticket className="w-5 h-5 text-[#6B5600]" />
                      Call-to-Ticket
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold mb-2">
                      <span className={complianceData?.ticket_compliance?.calls_without_tickets > 0 ? 'text-[#6B5600]' : 'text-[#0E6B3F]'}>
                        {complianceData?.ticket_compliance?.calls_without_tickets || 0}
                      </span>
                      <span className="text-[#59554D] text-lg ml-2">calls</span>
                    </div>
                    <div className="text-sm text-[#59554D]">
                      without tickets after 10 min
                    </div>
                    <div className="text-xs text-[#59554D] mt-2">
                      {complianceData?.ticket_compliance?.flagged_today || 0} flagged today
                    </div>
                  </CardContent>
                </Card>

                {/* Quality Compliance */}
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-[#8A6D00]" />
                      Ticket Quality
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold mb-2">
                      <span className={complianceData?.quality_compliance?.tickets_with_issues > 0 ? 'text-[#6B5600]' : 'text-[#0E6B3F]'}>
                        {complianceData?.quality_compliance?.tickets_with_issues || 0}
                      </span>
                      <span className="text-[#59554D] text-lg ml-2">tickets</span>
                    </div>
                    <div className="text-sm text-[#59554D]">
                      with quality issues
                    </div>
                    <div className="text-xs text-[#59554D] mt-2">
                      {complianceData?.quality_compliance?.comments_added_today || 0} internal notes added today
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Details Tables */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Calls Without Tickets */}
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader>
                    <CardTitle className="text-[#1A1A1A]">Calls Without Tickets</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[300px]">
                      {(complianceData?.ticket_compliance?.calls || []).length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8">
                          <CheckCircle className="w-12 h-12 text-[#0E6B3F] mb-2" />
                          <p className="text-[#59554D]">All calls have tickets!</p>
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow className="border-[#DAD5CA]">
                              <TableHead className="text-[#59554D]">Phone</TableHead>
                              <TableHead className="text-[#59554D]">Agent</TableHead>
                              <TableHead className="text-[#59554D]">Duration</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(complianceData?.ticket_compliance?.calls || []).map((call, idx) => (
                              <TableRow key={idx} className="border-[#DAD5CA]">
                                <TableCell className="text-[#1A1A1A] font-mono">{call.phone}</TableCell>
                                <TableCell className="text-[#59554D]">{call.agent}</TableCell>
                                <TableCell className="text-[#59554D]">{call.duration}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Tickets with Quality Issues */}
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader>
                    <CardTitle className="text-[#1A1A1A]">Tickets with Quality Issues</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[300px]">
                      {(complianceData?.quality_compliance?.tickets || []).length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8">
                          <CheckCircle className="w-12 h-12 text-[#0E6B3F] mb-2" />
                          <p className="text-[#59554D]">No quality issues found!</p>
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow className="border-[#DAD5CA]">
                              <TableHead className="text-[#59554D]">Ticket</TableHead>
                              <TableHead className="text-[#59554D]">Issues</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(complianceData?.quality_compliance?.tickets || []).map((ticket, idx) => (
                              <TableRow key={idx} className="border-[#DAD5CA]">
                                <TableCell>
                                  <a
                                    href={`${JIRA_URL}/${ticket.key}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#8A6D00] hover:underline font-mono"
                                  >
                                    {ticket.key}
                                  </a>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {ticket.issues.map((issue, i) => (
                                      <Badge key={i} className="bg-[#FFF7D1] text-[#6B5600] text-xs">
                                        {issue}
                                      </Badge>
                                    ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Recordings Tab */}
            <TabsContent value="recordings" className="space-y-6" data-testid="recordings-tab">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="heading text-xl text-[#1A1A1A]">Call Recordings</h2>
                  <p className="text-sm text-[#59554D]">Upload call recordings for transcription & quality review</p>
                </div>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        setUploadingRecording(true);
                        const formData = new FormData();
                        formData.append("file", file);
                        formData.append("agent_name", "");
                        formData.append("notes", "");
                        
                        try {
                          await axios.post(`${API}/recordings/upload`, formData, {
                            headers: { "Content-Type": "multipart/form-data" }
                          });
                          toast.success("Recording uploaded! Transcription in progress...");
                          fetchDashboardData();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Failed to upload recording");
                        } finally {
                          setUploadingRecording(false);
                          e.target.value = "";
                        }
                      }}
                      disabled={uploadingRecording}
                      data-testid="recording-upload-input"
                    />
                    <Button
                      variant="outline"
                      className="border-[#DAD5CA] bg-transparent text-[#1A1A1A] hover:bg-[#F4F2ED]"
                      disabled={uploadingRecording}
                      asChild
                    >
                      <span>
                        {uploadingRecording ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {uploadingRecording ? "Uploading..." : "Upload Recording"}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>

              {/* Recordings List */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <ScrollArea className="h-[calc(100vh-280px)]">
                  {recordings.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <FileAudio className="w-16 h-16 text-[#DAD5CA] mb-4" />
                      <p className="text-[#59554D]">No recordings uploaded yet</p>
                      <p className="text-xs text-[#59554D] mt-1">Upload MP3, WAV, or M4A files for transcription</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-[#DAD5CA]">
                      {recordings.map((recording, idx) => (
                        <div key={idx} className="p-4 hover:bg-[#F4F2ED]">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-4 min-w-0">
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                recording.status === 'completed' && recording.analysis ? 'bg-gradient-to-br from-[#FFF8D6] to-[#E3F3E9]' :
                                recording.status === 'completed' ? 'bg-[#E3F3E9]' :
                                recording.status === 'transcribed' ? 'bg-[#FFF7D1]' :
                                recording.status === 'processing' ? 'bg-[#FFF8D6]' :
                                'bg-[#FFE9E0]'
                              }`}>
                                {recording.status === 'processing' ? (
                                  <Loader2 className="w-6 h-6 text-[#8A6D00] animate-spin" />
                                ) : recording.status === 'transcribed' ? (
                                  <Loader2 className="w-6 h-6 text-[#6B5600] animate-spin" />
                                ) : recording.status === 'completed' && recording.analysis ? (
                                  <Brain className="w-6 h-6 text-[#8A6D00]" />
                                ) : recording.status === 'completed' ? (
                                  <FileAudio className="w-6 h-6 text-[#0E6B3F]" />
                                ) : (
                                  <AlertTriangle className="w-6 h-6 text-[#C23A00]" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-[#1A1A1A] break-all">{recording.original_filename}</p>
                                <p className="text-sm text-[#59554D]">
                                  Uploaded {formatDateTime(recording.uploaded_at)}
                                  {recording.duration && ` • ${Math.round(recording.duration / 60)}m ${Math.round(recording.duration % 60)}s`}
                                  {recording.speakers_detected > 0 && ` • ${recording.speakers_detected} speakers detected`}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge className={`${
                                    recording.status === 'completed' ? 'bg-[#E3F3E9] text-[#0E6B3F]' :
                                    recording.status === 'transcribed' ? 'bg-[#FFF7D1] text-[#6B5600]' :
                                    recording.status === 'processing' ? 'bg-[#FFF8D6] text-[#8A6D00]' :
                                    'bg-[#FFE9E0] text-[#C23A00]'
                                  }`}>
                                    {recording.status === 'completed' ? (recording.analysis ? 'Analyzed' : 'Transcribed') :
                                     recording.status === 'transcribed' ? 'Analyzing...' :
                                     recording.status === 'processing' ? 'Transcribing...' : 'Error'}
                                  </Badge>
                                  {recording.analysis && (
                                    <Badge className="bg-[#FFF8D6] text-[#8A6D00]">
                                      <Brain className="w-3 h-3 mr-1" /> AI Analyzed
                                    </Badge>
                                  )}
                                  {recording.call_id && (
                                    <Badge className="bg-[#DAD5CA] text-[#59554D]">
                                      <Phone className="w-3 h-3 mr-1" /> Linked to Call
                                    </Badge>
                                  )}
                                </div>
                                {/* Call metadata if available */}
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  {recording.agent_name ? (
                                    <span className="text-xs text-[#59554D]">
                                      Agent: <span className="text-[#1A1A1A]">{recording.agent_name}</span>
                                    </span>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-6 text-xs border-[#6B5600] text-[#6B5600] hover:bg-[#FFF7D1]"
                                      onClick={() => {
                                        setEditingRecording(recording);
                                        setEditRecordingDialogOpen(true);
                                      }}
                                      data-testid={`assign-agent-${recording.id}`}
                                    >
                                      <UserPlus className="w-3 h-3 mr-1" /> Assign Agent
                                    </Button>
                                  )}
                                  {recording.customer_name && (
                                    <span className="text-xs text-[#59554D]">
                                      • Customer: <span className="text-[#1A1A1A]">{recording.customer_name}</span>
                                    </span>
                                  )}
                                  {recording.agent_name && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 px-1 text-xs text-[#59554D] hover:text-[#1A1A1A]"
                                      onClick={() => {
                                        setEditingRecording(recording);
                                        setEditRecordingDialogOpen(true);
                                      }}
                                      data-testid={`edit-recording-${recording.id}`}
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                                <CallInsights recording={recording} onChanged={fetchDashboardData} />
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (confirm("Delete this recording?")) {
                                  try {
                                    await axios.delete(`${API}/recordings/${recording.id}`);
                                    toast.success("Recording deleted");
                                    fetchDashboardData();
                                  } catch (error) {
                                    toast.error("Failed to delete recording");
                                  }
                                }
                              }}
                              className="text-[#C23A00] hover:bg-[#FFE9E0]"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Collapsible Transcription & Analysis Section */}
                          {recording.status === 'completed' && recording.transcription && (
                            <div className="mt-4">
                              {/* Collapse/Expand Header */}
                              <button
                                onClick={() => setExpandedRecordings(prev => ({
                                  ...prev,
                                  [recording.id]: !prev[recording.id]
                                }))}
                                className="w-full flex items-center justify-between p-3 bg-[#FFFFFF] rounded-lg hover:bg-[#141414] transition-colors"
                                data-testid={`toggle-transcript-${recording.id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <FileAudio className="w-4 h-4 text-[#8A6D00]" />
                                  <span className="text-sm text-[#1A1A1A] font-medium">
                                    Transcription
                                    {recording.speakers_detected > 0 && (
                                      <span className="text-[#59554D] font-normal ml-2">
                                        ({recording.speakers_detected} speakers)
                                      </span>
                                    )}
                                  </span>
                                  {recording.analysis && (
                                    <Badge className="bg-[#FFF8D6] text-[#8A6D00] text-xs">
                                      <Brain className="w-3 h-3 mr-1" /> AI Analysis
                                    </Badge>
                                  )}
                                  {recording.analysis?.sentiment && (
                                    <Badge className={`text-xs ${
                                      recording.analysis.sentiment === 'positive' ? 'bg-[#E3F3E9] text-[#0E6B3F]' :
                                      recording.analysis.sentiment === 'negative' ? 'bg-[#FFE9E0] text-[#C23A00]' :
                                      'bg-[#FFF7D1] text-[#6B5600]'
                                    }`}>
                                      {recording.analysis.sentiment === 'positive' && <ThumbsUp className="w-3 h-3 mr-1" />}
                                      {recording.analysis.sentiment === 'negative' && <ThumbsDown className="w-3 h-3 mr-1" />}
                                      {recording.analysis.sentiment === 'neutral' && <Minus className="w-3 h-3 mr-1" />}
                                      {recording.analysis.sentiment.charAt(0).toUpperCase() + recording.analysis.sentiment.slice(1)}
                                    </Badge>
                                  )}
                                  {recording.analysis?.agent_rating && (
                                    <div className="flex items-center gap-0.5">
                                      {[...Array(5)].map((_, i) => (
                                        <Star
                                          key={i}
                                          className={`w-3 h-3 ${i < recording.analysis.agent_rating ? 'text-[#C9A400] fill-[#C9A400]' : 'text-[#DAD5CA]'}`}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {expandedRecordings[recording.id] ? (
                                  <ChevronUp className="w-4 h-4 text-[#59554D]" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-[#59554D]" />
                                )}
                              </button>

                              {/* Expanded Content */}
                              {expandedRecordings[recording.id] && (
                                <div className="mt-2 space-y-4">
                                  {/* Transcription Content */}
                                  <div className="p-4 bg-[#FFFFFF] rounded-lg">
                                    <p className="text-xs text-[#59554D] font-medium mb-3">TRANSCRIPTION</p>
                                    <div className="text-sm text-[#1A1A1A] space-y-3">
                                      {recording.transcription.split('\n\n').map((segment, segIdx) => {
                                        const speakerMatch = segment.match(/^(Speaker [A-Z0-9]+):\s*/i);
                                        if (speakerMatch) {
                                          const speaker = speakerMatch[1];
                                          const text = segment.replace(speakerMatch[0], '');
                                          const isEven = segIdx % 2 === 0;
                                          return (
                                            <div key={segIdx} className={`p-3 rounded-lg ${isEven ? 'bg-[#F4F2ED]' : 'bg-[#141414]'}`}>
                                              <p className={`text-xs font-medium mb-1 ${isEven ? 'text-[#8A6D00]' : 'text-[#0E6B3F]'}`}>
                                                {speaker}
                                              </p>
                                              <p className="text-[#1A1A1A] leading-relaxed">{text}</p>
                                            </div>
                                          );
                                        }
                                        return (
                                          <p key={segIdx} className="leading-relaxed">{segment}</p>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  {/* AI Analysis Content */}
                                  {recording.analysis && (
                                    <div className="p-4 bg-gradient-to-br from-[#FFF8D6] to-[#E3F3E9] rounded-lg border border-[#FFF8D6]">
                                      <div className="flex items-center gap-2 mb-3">
                                        <Brain className="w-4 h-4 text-[#8A6D00]" />
                                        <p className="text-xs text-[#59554D] font-medium">AI ANALYSIS</p>
                                      </div>
                                      
                                      <div className="text-sm text-[#1A1A1A] space-y-2 whitespace-pre-wrap leading-relaxed">
                                        {recording.analysis.raw_analysis?.split('\n').map((line, lineIdx) => {
                                          if (line.match(/^\*\*.*\*\*:?$/)) {
                                            return (
                                              <p key={lineIdx} className="font-semibold text-[#8A6D00] mt-3 first:mt-0">
                                                {line.replace(/\*\*/g, '')}
                                              </p>
                                            );
                                          }
                                          if (line.match(/^[\d]+\.\s/) || line.match(/^[-•]\s/)) {
                                            return (
                                              <p key={lineIdx} className="pl-4 text-[#59554D]">
                                                {line}
                                              </p>
                                            );
                                          }
                                          if (line.trim()) {
                                            return <p key={lineIdx}>{line}</p>;
                                          }
                                          return null;
                                        })}
                                      </div>
                                      
                                      <p className="text-xs text-[#59554D] mt-3 flex items-center gap-1">
                                        <Sparkles className="w-3 h-3" />
                                        Analyzed by {recording.analysis.model_used || 'AI'} at {formatDateTime(recording.analysis.analyzed_at)}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Analysis in progress */}
                          {recording.analysis_status === 'analyzing' && (
                            <div className="mt-4 p-4 bg-[#FFF8D6] rounded-lg flex items-center gap-3">
                              <Loader2 className="w-4 h-4 text-[#8A6D00] animate-spin" />
                              <p className="text-sm text-[#59554D]">AI is analyzing this transcript...</p>
                            </div>
                          )}
                          
                          {recording.status === 'error' && (
                            <div className="mt-4 p-4 bg-[#FFE9E0] rounded-lg">
                              <p className="text-sm text-[#C23A00]">
                                Error: {recording.error || "Transcription failed"}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </Card>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="space-y-6" data-testid="analytics-tab">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="heading text-xl text-[#1A1A1A]">
                    {analyticsSubtab === "tickets" ? "Ticket Reports" : "Call Analytics"}
                  </h2>
                  <p className="text-sm text-[#59554D]">
                    {analyticsSubtab === "tickets"
                      ? "Ticket volume, throughput, SLA and agent breakdown over any timeframe"
                      : "Analyze call patterns, agent performance, and AI-powered insights"}
                  </p>
                </div>
                {analyticsSubtab === "calls" && (
                  <div className="flex items-center gap-3">
                    {/* Period Selector (calls) */}
                    <Select value={analyticsPeriod} onValueChange={handlePeriodChange}>
                      <SelectTrigger className="w-[180px] bg-[#F4F2ED] border-[#DAD5CA]" data-testid="analytics-period-select">
                        <SelectValue placeholder="Select period" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="this_week">This Week</SelectItem>
                        <SelectItem value="last_week">Last Week</SelectItem>
                        <SelectItem value="this_month">This Month</SelectItem>
                        <SelectItem value="last_month">Last Month</SelectItem>
                        <SelectItem value="last_90_days">Last 90 Days</SelectItem>
                        <SelectItem value="last_180_days">Last 180 Days</SelectItem>
                        <SelectItem value="all">All Time</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={() => { fetchAnalytics(); toast.success("Analytics refreshed"); }}
                      variant="outline"
                      className="border-[#DAD5CA]"
                      data-testid="refresh-analytics-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                )}
              </div>

              {/* Analytics sub-tabs: Calls / Tickets */}
              <div className="inline-flex items-center gap-1 bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg p-1">
                <button
                  onClick={() => setAnalyticsSubtab("calls")}
                  data-testid="analytics-subtab-calls"
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    analyticsSubtab === "calls"
                      ? "bg-[#FFD108] text-[#1A1A1A]"
                      : "text-[#59554D] hover:text-[#1A1A1A]"
                  }`}
                >
                  Calls
                </button>
                <button
                  onClick={() => setAnalyticsSubtab("tickets")}
                  data-testid="analytics-subtab-tickets"
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    analyticsSubtab === "tickets"
                      ? "bg-[#FFD108] text-[#1A1A1A]"
                      : "text-[#59554D] hover:text-[#1A1A1A]"
                  }`}
                >
                  Tickets
                </button>
              </div>

              {analyticsSubtab === "tickets" && <TicketReportsView />}

              {analyticsSubtab === "calls" && (
              <>
              {/* Period Info Badge */}
              {callVolumeData?.date_range?.start && (
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#FFF8D6] text-[#8A6D00]">
                    {analyticsPeriod === "today" ? "Today" :
                     analyticsPeriod === "this_week" ? "This Week" :
                     analyticsPeriod === "last_week" ? "Last Week" :
                     analyticsPeriod === "this_month" ? "This Month" :
                     analyticsPeriod === "last_month" ? "Last Month" :
                     analyticsPeriod === "last_90_days" ? "Last 90 Days" :
                     analyticsPeriod === "last_180_days" ? "Last 180 Days" : "All Time"}
                  </Badge>
                  <span className="text-xs text-[#59554D]">
                    {formatDate(callVolumeData.date_range.start)} - {formatDate(callVolumeData.date_range.end)}
                  </span>
                </div>
              )}

              {/* Call Volume Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#FFF8D6] flex items-center justify-center">
                        <Phone className="w-5 h-5 text-[#8A6D00]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-[#1A1A1A]">{callVolumeData?.metrics?.total_calls || 0}</p>
                        <p className="text-xs text-[#59554D]">Total Calls</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#FFE9E0] flex items-center justify-center">
                        <PhoneMissed className="w-5 h-5 text-[#C23A00]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-[#1A1A1A]">{callVolumeData?.metrics?.missed_rate || 0}%</p>
                        <p className="text-xs text-[#59554D]">Missed Call Rate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#E3F3E9] flex items-center justify-center">
                        <Timer className="w-5 h-5 text-[#0E6B3F]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-[#1A1A1A]">{callVolumeData?.metrics?.avg_duration_formatted || "0m 0s"}</p>
                        <p className="text-xs text-[#59554D]">Avg Call Duration</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#FFF7D1] flex items-center justify-center">
                        <Zap className="w-5 h-5 text-[#6B5600]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-[#1A1A1A]">
                          {callVolumeData?.peak_hours?.[0]?.hour !== undefined 
                            ? `${callVolumeData.peak_hours[0].hour}:00` 
                            : "N/A"}
                        </p>
                        <p className="text-xs text-[#59554D]">Peak Hour</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Hourly Call Volume Heatmap */}
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader>
                    <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#8A6D00]" />
                      Hourly Call Volume
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {callVolumeData?.hourly_volume ? (
                      <div className="space-y-2">
                        {/* Business hours (7am - 7pm) */}
                        <div className="grid grid-cols-12 gap-1">
                          {[7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(hour => {
                            const count = callVolumeData.hourly_volume[hour] || 0;
                            const maxCount = Math.max(...Object.values(callVolumeData.hourly_volume), 1);
                            const intensity = count / maxCount;
                            return (
                              <div key={hour} className="text-center">
                                <div
                                  className="h-16 rounded flex items-end justify-center transition-all"
                                  style={{
                                    background: count > 0 
                                      ? `rgba(0, 122, 255, ${0.2 + intensity * 0.8})`
                                      : 'rgba(39, 39, 42, 0.5)'
                                  }}
                                  title={`${hour}:00 - ${count} calls`}
                                >
                                  <span className="text-xs text-[#1A1A1A] font-medium pb-1">{count || ''}</span>
                                </div>
                                <p className="text-[10px] text-[#59554D] mt-1">{hour}</p>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-[#59554D] text-center mt-2">Hour of Day (AEST)</p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-32 text-[#59554D]">
                        <Loader2 className="w-6 h-6 animate-spin mr-2" />
                        Loading...
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Daily Distribution */}
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader>
                    <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                      <CircleDot className="w-5 h-5 text-[#0E6B3F]" />
                      Call Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-[#FFFFFF] rounded-lg">
                        <div className="flex items-center gap-2">
                          <PhoneIncoming className="w-4 h-4 text-[#0E6B3F]" />
                          <span className="text-sm text-[#1A1A1A]">Inbound</span>
                        </div>
                        <span className="text-lg font-bold text-[#1A1A1A]">{callVolumeData?.metrics?.inbound || 0}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-[#FFFFFF] rounded-lg">
                        <div className="flex items-center gap-2">
                          <PhoneOutgoing className="w-4 h-4 text-[#8A6D00]" />
                          <span className="text-sm text-[#1A1A1A]">Outbound</span>
                        </div>
                        <span className="text-lg font-bold text-[#1A1A1A]">{callVolumeData?.metrics?.outbound || 0}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-[#FFFFFF] rounded-lg">
                        <div className="flex items-center gap-2">
                          <PhoneMissed className="w-4 h-4 text-[#C23A00]" />
                          <span className="text-sm text-[#1A1A1A]">Missed</span>
                        </div>
                        <span className="text-lg font-bold text-[#1A1A1A]">{callVolumeData?.metrics?.missed || 0}</span>
                      </div>
                      
                      {/* Peak Hours */}
                      <div className="mt-4 pt-4 border-t border-[#DAD5CA]">
                        <p className="text-xs text-[#59554D] mb-2">PEAK HOURS</p>
                        <div className="flex gap-2">
                          {(callVolumeData?.peak_hours || []).slice(0, 3).map((peak, idx) => (
                            <Badge 
                              key={idx} 
                              className={idx === 0 ? "bg-[#FFF7D1] text-[#6B5600]" : "bg-[#DAD5CA] text-[#59554D]"}
                            >
                              {peak.hour}:00 ({peak.calls} calls)
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Agent Performance Comparison */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-[#8A6D00]" />
                    Agent Performance Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {agentPerformance?.agents?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-xs text-[#59554D] border-b border-[#DAD5CA]">
                            <th className="pb-3">Agent</th>
                            <th className="pb-3 text-center">Tickets</th>
                            <th className="pb-3 text-center">Resolved</th>
                            <th className="pb-3 text-center">SLA %</th>
                            <th className="pb-3 text-center">Calls</th>
                            <th className="pb-3 text-center">Missed %</th>
                            <th className="pb-3 text-center">Avg Duration</th>
                            <th className="pb-3 text-center">Sentiment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentPerformance.agents.map((agent, idx) => (
                            <tr key={idx} className="border-b border-[#DAD5CA] hover:bg-[#F4F2ED]">
                              <td className="py-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-[#FFF8D6] flex items-center justify-center text-xs text-[#8A6D00] font-medium">
                                    {agent.name.split(' ').map(n => n[0]).join('')}
                                  </div>
                                  <span className="text-[#1A1A1A] font-medium">{agent.name}</span>
                                </div>
                              </td>
                              <td className="py-3 text-center text-[#1A1A1A]">{agent.tickets_total}</td>
                              <td className="py-3 text-center">
                                <span className="text-[#0E6B3F]">{agent.tickets_resolved}</span>
                              </td>
                              <td className="py-3 text-center">
                                <Badge className={agent.sla_compliance >= 80 ? "bg-[#E3F3E9] text-[#0E6B3F]" : agent.sla_compliance >= 60 ? "bg-[#FFF7D1] text-[#6B5600]" : "bg-[#FFE9E0] text-[#C23A00]"}>
                                  {agent.sla_compliance}%
                                </Badge>
                              </td>
                              <td className="py-3 text-center text-[#1A1A1A]">{agent.calls_total}</td>
                              <td className="py-3 text-center">
                                <span className={agent.missed_call_rate > 20 ? "text-[#C23A00]" : "text-[#59554D]"}>
                                  {agent.missed_call_rate}%
                                </span>
                              </td>
                              <td className="py-3 text-center text-[#59554D]">
                                {Math.floor(agent.avg_call_duration / 60)}m {Math.round(agent.avg_call_duration % 60)}s
                              </td>
                              <td className="py-3 text-center">
                                {agent.transcripts_analyzed > 0 ? (
                                  <div className="flex items-center justify-center gap-1">
                                    {agent.avg_sentiment_score >= 0.7 ? (
                                      <ThumbsUp className="w-4 h-4 text-[#0E6B3F]" />
                                    ) : agent.avg_sentiment_score >= 0.4 ? (
                                      <Minus className="w-4 h-4 text-[#6B5600]" />
                                    ) : (
                                      <ThumbsDown className="w-4 h-4 text-[#C23A00]" />
                                    )}
                                    <span className="text-xs text-[#59554D]">({agent.transcripts_analyzed})</span>
                                  </div>
                                ) : (
                                  <span className="text-[#59554D]">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-32 text-[#59554D]">
                      No agent data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* AI Insights */}
              <Card className="bg-gradient-to-br from-[#FFF8D6] to-[#EFF6FF] border-[#FFF8D6]">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                      <Brain className="w-5 h-5 text-[#8A6D00]" />
                      AI-Powered Insights
                    </CardTitle>
                    <Button
                      onClick={generateAiInsights}
                      disabled={loadingInsights}
                      className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]"
                      data-testid="generate-insights-btn"
                    >
                      {loadingInsights ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate Insights
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {aiInsights ? (
                    <div className="space-y-4">
                      <div className="text-sm text-[#1A1A1A] whitespace-pre-wrap leading-relaxed">
                        {aiInsights.insights.split('\n').map((line, idx) => {
                          if (line.startsWith('**') && line.endsWith('**')) {
                            return (
                              <p key={idx} className="font-bold text-[#8A6D00] mt-4 first:mt-0 text-base">
                                {line.replace(/\*\*/g, '')}
                              </p>
                            );
                          }
                          if (line.startsWith('- ')) {
                            return (
                              <p key={idx} className="pl-4 text-[#59554D] py-0.5">
                                • {line.substring(2)}
                              </p>
                            );
                          }
                          if (line.trim()) {
                            return <p key={idx}>{line}</p>;
                          }
                          return null;
                        })}
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-[#DAD5CA]">
                        <p className="text-xs text-[#59554D] flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Generated at {formatDateTime(aiInsights.generated_at)}
                        </p>
                        <div className="flex gap-2 text-xs text-[#59554D]">
                          <span>{aiInsights.data_summary.total_calls} calls</span>
                          <span>•</span>
                          <span>{aiInsights.data_summary.open_tickets} tickets</span>
                          <span>•</span>
                          <span>{aiInsights.data_summary.transcripts_analyzed} transcripts</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <Brain className="w-12 h-12 text-[#DAD5CA] mb-4" />
                      <p className="text-[#59554D]">Click &quot;Generate Insights&quot; to analyze your call data</p>
                      <p className="text-xs text-[#59554D] mt-1">AI will identify busy periods, agent performance, and recommendations</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Frequent Callers */}
              {customerIntel?.frequent_callers?.length > 0 && (
                <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                  <CardHeader>
                    <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#6B5600]" />
                      Frequent Callers
                      <Badge className="ml-2 bg-[#DAD5CA] text-[#59554D]">
                        {customerIntel.customers_with_multiple_calls} customers
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {customerIntel.frequent_callers.slice(0, 6).map((caller, idx) => (
                        <div key={idx} className="p-3 bg-[#FFFFFF] rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-[#1A1A1A]">{caller.phone}</span>
                            <Badge className="bg-[#FFF7D1] text-[#6B5600]">
                              {caller.call_count} calls
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs text-[#59554D]">
                            <span>Avg: {Math.floor(caller.avg_duration / 60)}m {Math.round(caller.avg_duration % 60)}s</span>
                            {caller.missed_calls > 0 && (
                              <span className="text-[#C23A00]">{caller.missed_calls} missed</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* AI Suggestion Feedback Analytics */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-[#1D4ED8]" />
                      AI Suggestion Feedback
                      {aiFeedbackData?.total > 0 && (
                        <Badge className="ml-2 bg-[#EFF6FF] text-[#1D4ED8]">
                          {aiFeedbackData.total} ignores
                        </Badge>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Select value={String(aiFeedbackDays)} onValueChange={(v) => { setAiFeedbackDays(Number(v)); fetchAiFeedback(Number(v)); }}>
                        <SelectTrigger className="w-[120px] bg-[#F4F2ED] border-[#DAD5CA]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                          <SelectItem value="7">Last 7 days</SelectItem>
                          <SelectItem value="14">Last 14 days</SelectItem>
                          <SelectItem value="30">Last 30 days</SelectItem>
                          <SelectItem value="90">Last 90 days</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchAiFeedback()}
                        disabled={loadingAiFeedback}
                        className="border-[#DAD5CA]"
                      >
                        {loadingAiFeedback ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {!aiFeedbackData ? (
                    <div className="text-center py-8">
                      <Sparkles className="w-8 h-8 mx-auto mb-3 text-[#1D4ED8] opacity-50" />
                      <p className="text-[#59554D]">Click refresh to load AI feedback data</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchAiFeedback()}
                        className="mt-3 border-[#DAD5CA]"
                      >
                        Load Feedback Data
                      </Button>
                    </div>
                  ) : aiFeedbackData.total === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-8 h-8 mx-auto mb-3 text-[#0E6B3F]" />
                      <p className="text-[#59554D]">No ignored suggestions in the last {aiFeedbackDays} days</p>
                      <p className="text-xs text-[#8A857A] mt-1">AI suggestions are being accepted by agents</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Summary Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-[#FFFFFF] rounded-lg text-center">
                          <p className="text-2xl font-bold text-[#1A1A1A]">{aiFeedbackData.total}</p>
                          <p className="text-xs text-[#59554D]">Total Ignores</p>
                        </div>
                        <div className="p-4 bg-[#FFFFFF] rounded-lg text-center">
                          <p className="text-2xl font-bold text-[#0E6B3F]">
                            {aiFeedbackData.by_category?.already_handled || 0}
                          </p>
                          <p className="text-xs text-[#59554D]">Already Handled</p>
                        </div>
                        <div className="p-4 bg-[#FFFFFF] rounded-lg text-center">
                          <p className="text-2xl font-bold text-[#6B5600]">
                            {aiFeedbackData.by_category?.incorrect || 0}
                          </p>
                          <p className="text-xs text-[#59554D]">Incorrect</p>
                        </div>
                        <div className="p-4 bg-[#FFFFFF] rounded-lg text-center">
                          <p className="text-2xl font-bold text-[#8A6D00]">
                            {aiFeedbackData.by_category?.will_action_later || 0}
                          </p>
                          <p className="text-xs text-[#59554D]">Will Action Later</p>
                        </div>
                      </div>

                      {/* By Category Breakdown */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-[#FFFFFF] rounded-lg">
                          <h4 className="text-sm font-medium text-[#1A1A1A] mb-3">By Ignore Reason</h4>
                          <div className="space-y-2">
                            {Object.entries(aiFeedbackData.by_category || {}).map(([category, count]) => (
                              <div key={category} className="flex items-center justify-between">
                                <span className="text-sm text-[#59554D] capitalize">
                                  {category.replace(/_/g, ' ')}
                                </span>
                                <div className="flex items-center gap-2">
                                  <div className="w-24 h-2 bg-[#DAD5CA] rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-[#1D4ED8] rounded-full"
                                      style={{ width: `${(count / aiFeedbackData.total) * 100}%` }}
                                    />
                                  </div>
                                  <span className="text-sm text-[#1A1A1A] w-8 text-right">{count}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="p-4 bg-[#FFFFFF] rounded-lg">
                          <h4 className="text-sm font-medium text-[#1A1A1A] mb-3">By Suggestion Type</h4>
                          <div className="space-y-2">
                            {Object.entries(aiFeedbackData.by_action || {}).map(([action, count]) => (
                              <div key={action} className="flex items-center justify-between">
                                <span className="text-sm text-[#59554D] capitalize">
                                  {action.replace(/_/g, ' ')}
                                </span>
                                <div className="flex items-center gap-2">
                                  <div className="w-24 h-2 bg-[#DAD5CA] rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-[#6B5600] rounded-full"
                                      style={{ width: `${(count / aiFeedbackData.total) * 100}%` }}
                                    />
                                  </div>
                                  <span className="text-sm text-[#1A1A1A] w-8 text-right">{count}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Recent Feedback List */}
                      <div>
                        <h4 className="text-sm font-medium text-[#1A1A1A] mb-3">Recent Feedback</h4>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {aiFeedbackData.feedback?.slice(0, 10).map((item, idx) => (
                            <div key={idx} className="p-3 bg-[#FFFFFF] rounded-lg">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <a 
                                    href={`https://equiprsoftware.atlassian.net/browse/${item.ticket_key}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#8A6D00] hover:underline font-medium"
                                  >
                                    {item.ticket_key}
                                  </a>
                                  <Badge className={`text-[10px] ${
                                    item.ignore_category === 'incorrect' ? 'bg-[#FFE9E0] text-[#C23A00]' :
                                    item.ignore_category === 'already_handled' ? 'bg-[#E3F3E9] text-[#0E6B3F]' :
                                    'bg-[#DAD5CA] text-[#59554D]'
                                  }`}>
                                    {item.ignore_category?.replace(/_/g, ' ')}
                                  </Badge>
                                </div>
                                <span className="text-xs text-[#8A857A]">
                                  {item.ignored_at ? formatDate(item.ignored_at) : ''}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-[#59554D]">
                                  Suggestion: <span className="text-[#6B5600]">{item.suggestion_action?.replace(/_/g, ' ')}</span>
                                </span>
                                <span className="text-xs text-[#8A857A]">by {item.ignored_by}</span>
                              </div>
                              {item.ignore_notes && (
                                <p className="text-xs text-[#59554D] mt-2 italic">&quot;{item.ignore_notes}&quot;</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Learning Insights */}
                      {aiFeedbackData.total >= 5 && (
                        <div className="p-4 bg-gradient-to-r from-[#EFF6FF] to-[#FFF8D6] rounded-lg border border-[#EFF6FF]">
                          <div className="flex items-center gap-2 mb-2">
                            <Lightbulb className="w-4 h-4 text-[#C9A400]" />
                            <h4 className="text-sm font-medium text-[#1A1A1A]">AI Learning Insights</h4>
                          </div>
                          <ul className="text-sm text-[#59554D] space-y-1">
                            {aiFeedbackData.by_category?.incorrect > 2 && (
                              <li>• {aiFeedbackData.by_category.incorrect} suggestions marked as &quot;incorrect&quot; - review AI logic for these patterns</li>
                            )}
                            {aiFeedbackData.by_category?.already_handled > 3 && (
                              <li>• {aiFeedbackData.by_category.already_handled} tickets were already handled - AI may be analyzing stale data</li>
                            )}
                            {Object.keys(aiFeedbackData.by_action || {}).length > 0 && (
                              <li>• Most ignored suggestion type: <span className="text-[#6B5600]">{
                                Object.entries(aiFeedbackData.by_action).sort((a, b) => b[1] - a[1])[0]?.[0]?.replace(/_/g, ' ')
                              }</span></li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              </>
              )}
            </TabsContent>

            {/* Alerts Tab */}
            <TabsContent value="alerts" className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="heading text-xl text-[#1A1A1A]">Alert History</h2>
                <Badge variant="outline" className="border-[#DAD5CA] text-[#59554D]">
                  {alerts.length} alerts
                </Badge>
              </div>

              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <ScrollArea className="h-[600px]">
                  {alerts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <Bell className="w-12 h-12 text-[#DAD5CA] mb-4" />
                      <p className="text-[#59554D]">No alerts generated yet</p>
                      <p className="text-xs text-[#59554D] mt-1">Alerts will appear here after the next sync</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-[#DAD5CA]">
                      {alerts.map((alert, idx) => (
                        <div key={idx} className="p-4 hover:bg-[#F4F2ED] transition-colors">
                          <div className="flex items-start gap-4">
                            <span className={`status-dot mt-1 ${alert.severity === 'critical' ? 'danger' : alert.severity === 'warning' ? 'warning' : 'info'}`} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <a
                                  href={`${JIRA_URL}/${alert.ticket_key}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mono text-sm text-[#8A6D00] hover:underline"
                                >
                                  {alert.ticket_key}
                                </a>
                                <Badge className={`text-xs ${
                                  alert.severity === 'critical' ? 'bg-[#FFE9E0] text-[#C23A00]' :
                                  alert.severity === 'warning' ? 'bg-[#FFF7D1] text-[#6B5600]' :
                                  'bg-[#FFF8D6] text-[#8A6D00]'
                                }`}>
                                  {alert.alert_type}
                                </Badge>
                              </div>
                              <p className="text-sm text-[#1A1A1A]">{alert.message}</p>
                              <p className="text-xs text-[#59554D] mt-1">{formatTimeAgo(alert.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </Card>
            </TabsContent>

            {/* Agents Tab */}
            <TabsContent value="agents" className="space-y-4">
              <h2 className="heading text-xl text-[#1A1A1A]">Agent Workload</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {agents.length === 0 ? (
                  <Card className="bg-[#FFFFFF] border-[#DAD5CA] col-span-full">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                      <Users className="w-12 h-12 text-[#DAD5CA] mb-4" />
                      <p className="text-[#59554D]">No agent data available</p>
                    </CardContent>
                  </Card>
                ) : (
                  agents.map((agent, idx) => (
                    <Card key={idx} className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#F4F2ED] flex items-center justify-center text-[#1A1A1A] font-medium">
                              {agent.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </div>
                            <div>
                              <p className="font-medium text-[#1A1A1A]">{agent.name}</p>
                              <p className="text-xs text-[#59554D]">{agent.email || "No email"}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {agent.queue_health && (
                              <button
                                type="button"
                                onClick={() => setQhAgent(agent)}
                                className="flex flex-col items-center hover:opacity-80 transition-opacity"
                                title="Queue Health — click for stale ticket breakdown"
                                data-testid={`agent-queue-health-${agent.id || idx}`}
                              >
                                <QueueHealthGauge score={agent.queue_health.score} color={agent.queue_health.color} size={42} stroke={4} />
                                <span className="text-[10px] mt-0.5 font-medium" style={{ color: agent.queue_health.color }}>{agent.queue_health.label}</span>
                              </button>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                impersonateAgent(agent);
                              }}
                              className="text-[#8A6D00] hover:bg-[#FFF8D6] h-8 px-2"
                              title={`View as ${agent.name}`}
                              data-testid={`impersonate-${agent.id || idx}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View As
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="metric-label">Open</p>
                            <p className="text-2xl font-bold text-[#1A1A1A]">{agent.open_tickets}</p>
                          </div>
                          <div>
                            <p className="metric-label">High Priority</p>
                            <p className="text-2xl font-bold text-[#C23A00]">{agent.high_priority}</p>
                          </div>
                          <div>
                            <p className="metric-label">Avg Idle</p>
                            <p className="text-2xl font-bold text-[#3B82F6]">{agent.avg_idle_hours}h</p>
                          </div>
                        </div>

                        {/* By Status breakdown */}
                        {agent.status_breakdown && Object.keys(agent.status_breakdown).length > 0 && (
                          <div className="mt-4 pt-3 border-t border-[#DAD5CA]">
                            <p className="metric-label mb-2">By Status</p>
                            <div className="flex flex-wrap gap-1.5" data-testid={`agent-status-breakdown-${agent.id || idx}`}>
                              {Object.entries(agent.status_breakdown)
                                .sort((a, b) => b[1] - a[1])
                                .map(([status, count]) => {
                                  const sl = status.toLowerCase();
                                  const dot = sl.includes("escalat") ? "#C23A00"
                                    : sl.includes("customer") ? "#6B5600"
                                    : sl.includes("support") ? "#8A6D00"
                                    : sl.includes("system") ? "#1D4ED8"
                                    : sl.includes("development") ? "#1D4ED8"
                                    : sl === "new" ? "#0E6B3F"
                                    : "#59554D";
                                  return (
                                    <span
                                      key={status}
                                      className="inline-flex items-center gap-1.5 text-xs bg-[#F4F2ED] border border-[#DAD5CA] rounded-full px-2 py-0.5"
                                      title={status}
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dot }} />
                                      <span className="text-[#59554D] truncate max-w-[120px]">{status}</span>
                                      <span className="text-[#1A1A1A] font-semibold">{count}</span>
                                    </span>
                                  );
                                })}
                            </div>
                          </div>
                        )}

                        {/* Open tickets by client */}
                        {agent.clients && agent.clients.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-[#DAD5CA]">
                            <p className="metric-label mb-2">Open by Client ({agent.client_count})</p>
                            <div className="space-y-1 max-h-36 overflow-y-auto pr-1" data-testid={`agent-clients-${agent.id || idx}`}>
                              {agent.clients.map((c) => (
                                <div key={c.name} className="flex items-center justify-between text-xs gap-2">
                                  <span className="text-[#59554D] truncate flex items-center gap-1.5 min-w-0">
                                    <Building2 className="w-3 h-3 text-[#8A857A] shrink-0" />
                                    <span className="truncate">{c.name}</span>
                                  </span>
                                  <span className="text-[#1A1A1A] font-semibold bg-[#F4F2ED] rounded px-1.5 min-w-[22px] text-center shrink-0">{c.count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Nudge agent when their waiting queue is decaying */}
                        {agent.queue_health && agent.queue_health.needs_action > 0 && (
                          <div className="mt-3 pt-3 border-t border-[#DAD5CA]">
                            <Button
                              type="button"
                              size="sm"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); nudgeAgent(agent); }}
                              disabled={nudgingAgent === agent.name}
                              className="w-full bg-[#FFF7D1] text-[#6B5600] hover:bg-[#FFF7D1] border border-[#FFF7D1]"
                              data-testid={`nudge-agent-${agent.id || idx}`}
                            >
                              {nudgingAgent === agent.name ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Bell className="w-4 h-4 mr-2" />
                              )}
                              Nudge — {agent.queue_health.needs_action} need{agent.queue_health.needs_action > 1 ? '' : 's'} action
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

              {/* Agent Queue Health breakdown dialog */}
              <Dialog open={!!qhAgent} onOpenChange={(open) => { if (!open) setQhAgent(null); }}>
                <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg" data-testid="agent-queue-health-dialog">
                  {qhAgent && qhAgent.queue_health && (
                    <>
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <HeartPulse className="w-5 h-5" style={{ color: qhAgent.queue_health.color }} />
                          {qhAgent.name} — Queue Health {qhAgent.queue_health.label} ({qhAgent.queue_health.score})
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <p className="text-sm text-[#59554D]">
                          Driven by how long this agent's <span className="text-[#1A1A1A]">Waiting for customer</span> /{' '}
                          <span className="text-[#1A1A1A]">Waiting for support</span> tickets have been sitting.
                        </p>
                        <div className="grid grid-cols-3 gap-3 text-center">
                          <div className="p-3 bg-[#F4F2ED] rounded-lg">
                            <p className="text-xl font-bold text-[#1A1A1A]">{qhAgent.queue_health.total_waiting}</p>
                            <p className="text-xs text-[#59554D]">Waiting total</p>
                          </div>
                          <div className="p-3 bg-[#F4F2ED] rounded-lg">
                            <p className="text-xl font-bold text-[#1A1A1A]">{qhAgent.queue_health.waiting_for_customer}</p>
                            <p className="text-xs text-[#59554D]">On customer</p>
                          </div>
                          <div className="p-3 bg-[#F4F2ED] rounded-lg">
                            <p className="text-xl font-bold text-[#1A1A1A]">{qhAgent.queue_health.waiting_for_support}</p>
                            <p className="text-xs text-[#59554D]">On support</p>
                          </div>
                        </div>
                        {qhAgent.queue_health.breakdown.length === 0 ? (
                          <div className="text-center py-6">
                            <CheckCircle className="w-10 h-10 mx-auto mb-2 text-[#0E6B3F]" />
                            <p className="text-sm text-[#59554D]">This agent's waiting queue is clean — nothing stale.</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                            {qhAgent.queue_health.breakdown.map((item) => (
                              <div key={item.key} className="p-3 bg-[#F4F2ED] rounded-lg border border-[#DAD5CA]" data-testid={`agent-qh-item-${item.key}`}>
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <a href={`https://equiprsoftware.atlassian.net/browse/${item.key}`} target="_blank" rel="noopener noreferrer" className="text-[#8A6D00] hover:underline font-mono text-sm">{item.key}</a>
                                  <Badge className={item.severity === 'critical' ? 'bg-[#FFE9E0] text-[#C23A00]' : 'bg-[#FFF7D1] text-[#6B5600]'}>
                                    {item.status} · {item.business_days}d
                                  </Badge>
                                </div>
                                <p className="text-sm text-[#1A1A1A] truncate">{item.summary}</p>
                                <p className="text-xs text-[#8A857A] mt-1">{item.recommendation}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" className="border-[#DAD5CA] text-[#1A1A1A]" onClick={() => setQhAgent(null)}>Close</Button>
                          {qhAgent.queue_health.needs_action > 0 && (
                            <Button
                              onClick={() => { nudgeAgent(qhAgent); setQhAgent(null); }}
                              disabled={nudgingAgent === qhAgent.name}
                              className="bg-[#6B5600] hover:bg-[#FFF7D1] text-black font-medium"
                              data-testid="agent-qh-nudge"
                            >
                              <Bell className="w-4 h-4 mr-2" />
                              Nudge {qhAgent.name.split(' ')[0]}
                            </Button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-6">
              {/* Settings Sub-tabs */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="heading text-xl text-[#1A1A1A]">Settings</h2>
                <div className="flex items-center gap-2 bg-[#FFFFFF] rounded-lg p-1 border border-[#DAD5CA]">
                  <Button
                    variant={settingsTab === "assignment" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSettingsTab("assignment")}
                    className={settingsTab === "assignment" ? "bg-[#6B5600] text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                    data-testid="settings-assignment-tab"
                  >
                    <Route className="w-4 h-4 mr-2" />
                    Auto-Assignment
                  </Button>
                  <Button
                    variant={settingsTab === "rules" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSettingsTab("rules")}
                    className={settingsTab === "rules" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Rules
                  </Button>
                  <Button
                    variant={settingsTab === "agents" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSettingsTab("agents")}
                    className={settingsTab === "agents" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Agent Mapping
                  </Button>
                  <Button
                    variant={settingsTab === "workhours" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSettingsTab("workhours")}
                    className={settingsTab === "workhours" ? "bg-purple-600 text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                    data-testid="settings-workhours-tab"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Work Hours
                  </Button>
                  <Button
                    variant={settingsTab === "reports" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSettingsTab("reports")}
                    className={settingsTab === "reports" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                    data-testid="settings-reports-tab"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Reports
                  </Button>
                  <Button
                    variant={settingsTab === "ai_model" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSettingsTab("ai_model")}
                    className={settingsTab === "ai_model" ? "bg-[#1D4ED8] text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                    data-testid="settings-ai-model-tab"
                  >
                    <Brain className="w-4 h-4 mr-2" />
                    AI Model
                  </Button>
                  <Button
                    variant={settingsTab === "heartbeat" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSettingsTab("heartbeat")}
                    className={settingsTab === "heartbeat" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                    data-testid="settings-heartbeat-tab"
                  >
                    <Activity className="w-4 h-4 mr-2" />
                    Heartbeat
                  </Button>
                  <Button
                    variant={settingsTab === "digest_log" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSettingsTab("digest_log")}
                    className={settingsTab === "digest_log" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                    data-testid="settings-digest-log-tab"
                  >
                    <History className="w-4 h-4 mr-2" />
                    Digest Log
                  </Button>
                  <Button
                    variant={settingsTab === "integrations" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSettingsTab("integrations")}
                    className={settingsTab === "integrations" ? "bg-[#FFD108] text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                    data-testid="settings-integrations-tab"
                  >
                    <KeyRound className="w-4 h-4 mr-2" />
                    Integrations &amp; Keys
                  </Button>
                  <Button
                    variant={settingsTab === "hubspot_export" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSettingsTab("hubspot_export")}
                    className={settingsTab === "hubspot_export" ? "bg-[#C23A00] text-[#1A1A1A]" : "text-[#59554D] hover:text-[#1A1A1A]"}
                    data-testid="settings-hubspot-export-tab"
                  >
                    <Database className="w-4 h-4 mr-2" />
                    HubSpot Backup
                  </Button>
                </div>
              </div>

              {/* HUBSPOT BACKUP TAB */}
              {settingsTab === "hubspot_export" && (
                <HubSpotExport />
              )}

              {/* HEARTBEAT TAB */}
              {settingsTab === "heartbeat" && (
                <HeartbeatSettings />
              )}

              {/* DIGEST LOG TAB */}
              {settingsTab === "digest_log" && (
                <DigestSendLog />
              )}

              {/* AI MODEL TAB */}
              {settingsTab === "ai_model" && (
                <AIModelSettings />
              )}

              {/* INTEGRATIONS & KEYS TAB */}
              {settingsTab === "integrations" && (
                <IntegrationsKeys />
              )}

              {/* AUTO-ASSIGNMENT TAB */}
              {settingsTab === "assignment" && (
                <>
                  {/* Agent Support Capacity — drives KPI calculations + workload weighting */}
                  <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                    <CardHeader>
                      <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#8A6D00]" />
                        Agent Support Capacity
                      </CardTitle>
                      <p className="text-sm text-[#59554D] mt-1">
                        Tune each agent&apos;s support dedication %. Used by KPI dashboards and the auto-assignment workload balancer — 100% = full-time support, 40% = part-time. Coloured badges flag agents whose current open-ticket load is materially off from their fair share at the configured %. Press Enter or click Save to persist.
                      </p>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const supportAgents = (agentMappingsForCapacity || []).filter(
                          a => a.role !== "manager" && a.id !== "emergency" && a.active !== false
                        );

                        // ─── Compute workload imbalance (fed by /agents/workload) ───
                        // Expected share per agent = (their weight / Σ weights) × Σ open tickets.
                        // We then flag rows whose actual current load is materially off from
                        // their dedication %. Use generous thresholds + a small floor to
                        // avoid noise on tiny queues.
                        const workloadByName = {};
                        (agents || []).forEach(w => { workloadByName[w.name] = w; });
                        const totalWeight = supportAgents.reduce(
                          (sum, a) => sum + (a.support_weight || 0), 0
                        );
                        const totalOpen = supportAgents.reduce(
                          (sum, a) => sum + (workloadByName[a.name]?.open_tickets || 0), 0
                        );

                        const balanceFor = (agent) => {
                          const weight = agent.support_weight || 0;
                          const actual = workloadByName[agent.name]?.open_tickets || 0;
                          if (!totalWeight || !totalOpen || weight === 0) {
                            return { status: "ok", actual, expected: 0, label: "" };
                          }
                          const expected = (weight / totalWeight) * totalOpen;
                          const ratio = expected > 0 ? actual / expected : 1;
                          // Small floor: don't yell about a 1-ticket "imbalance"
                          const FLOOR = 3;
                          if (actual >= FLOOR && ratio > 1.3) {
                            return { status: "overloaded", actual, expected, ratio, label: "Overloaded" };
                          }
                          if (expected >= FLOOR && ratio < 0.7) {
                            return { status: "underloaded", actual, expected, ratio, label: "Underloaded" };
                          }
                          return { status: "balanced", actual, expected, ratio, label: "Balanced" };
                        };

                        if (supportAgents.length === 0) {
                          return (
                            <p className="text-sm text-[#8A857A] text-center py-4">
                              No support agents found. Add agents in Settings → Agent Mapping.
                            </p>
                          );
                        }
                        return (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="agent-capacity-grid">
                            {supportAgents.map(agent => {
                              const persistedPct = Math.round((agent.support_weight || 0) * 100);
                              const draft = capacityDrafts[agent.id];
                              const currentPct = draft !== undefined ? draft : String(persistedPct);
                              const dirty = draft !== undefined && parseInt(draft, 10) !== persistedPct;
                              const isSaving = savingAgentId === agent.id;
                              const bal = balanceFor(agent);
                              const balStyles = {
                                overloaded: "bg-[#FFE9E0] text-[#C23A00] border-[#FFE9E0]",
                                underloaded: "bg-[#FFF7D1] text-[#6B5600] border-[#FFF7D1]",
                                balanced: "bg-[#E3F3E9] text-[#0E6B3F] border-[#E3F3E9]",
                                ok: "",
                              }[bal.status];
                              const balTooltip =
                                bal.status === "ok"
                                  ? ""
                                  : `Carrying ${bal.actual} ticket${bal.actual === 1 ? "" : "s"}, expected ~${Math.round(bal.expected)} at ${persistedPct}% dedication (${(bal.ratio || 1).toFixed(2)}× fair share)`;
                              return (
                                <div
                                  key={agent.id}
                                  className="flex items-center gap-3 bg-[#F4F2ED] rounded-lg p-3"
                                  data-testid={`capacity-row-${agent.id}`}
                                >
                                  <div className="w-9 h-9 rounded-full bg-[#FFF8D6] flex items-center justify-center text-[#8A6D00] font-medium text-sm shrink-0">
                                    {agent.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm font-medium text-[#1A1A1A] truncate">{agent.name}</p>
                                      {bal.status !== "ok" && bal.label && (
                                        <span
                                          className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${balStyles}`}
                                          title={balTooltip}
                                          data-testid={`balance-badge-${agent.id}`}
                                        >
                                          {bal.label === "Balanced"
                                            ? `${bal.actual}/${Math.round(bal.expected)}`
                                            : `${bal.label} · ${bal.actual}/${Math.round(bal.expected)}`}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-[#8A857A] truncate">
                                      {agent.extension ? `Ext ${agent.extension} · ` : ''}{agent.email}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <Input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="10"
                                      value={currentPct}
                                      onChange={(e) =>
                                        setCapacityDrafts(prev => ({
                                          ...prev,
                                          [agent.id]: e.target.value,
                                        }))
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" && dirty) {
                                          saveAgentSupportWeight(agent, currentPct);
                                        } else if (e.key === "Escape") {
                                          setCapacityDrafts(prev => {
                                            const next = { ...prev };
                                            delete next[agent.id];
                                            return next;
                                          });
                                        }
                                      }}
                                      className="w-16 h-8 bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] text-sm text-center"
                                      data-testid={`capacity-input-${agent.id}`}
                                    />
                                    <span className="text-xs text-[#59554D]">%</span>
                                    {dirty ? (
                                      <Button
                                        size="sm"
                                        onClick={() => saveAgentSupportWeight(agent, currentPct)}
                                        disabled={isSaving}
                                        className="h-8 px-2 bg-[#0E6B3F] hover:bg-[#E3F3E9] text-[#1A1A1A] text-xs"
                                        data-testid={`capacity-save-${agent.id}`}
                                      >
                                        {isSaving ? (
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                          "Save"
                                        )}
                                      </Button>
                                    ) : (
                                      <span className="text-[10px] text-[#8A857A] w-10 text-center">
                                        saved
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                          <Route className="w-5 h-5 text-[#6B5600]" />
                          Auto-Assignment Rules
                        </CardTitle>
                        <p className="text-sm text-[#59554D] mt-1">
                          Configure rules to automatically assign tickets based on organization, keywords, or priority
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handlePreviewAutoAssign}
                          disabled={previewLoading || autoAssignRunning}
                          variant="outline"
                          className="border-[#6B5600] text-[#6B5600] hover:bg-[#FFF7D1]"
                          data-testid="preview-auto-assign-btn"
                        >
                          {previewLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Eye className="w-4 h-4 mr-2" />
                          )}
                          Preview
                        </Button>
                        <Button
                          onClick={handleRunAutoAssign}
                          disabled={autoAssignRunning}
                          className="bg-[#0E6B3F] hover:bg-[#E3F3E9] text-[#1A1A1A]"
                          data-testid="run-auto-assign-btn"
                        >
                          {autoAssignRunning ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4 mr-2" />
                          )}
                          Run Auto-Assign
                        </Button>
                        <Button
                          onClick={() => {
                            setEditingRule(null);
                            setNewRule({
                              rule_id: "",
                              name: "",
                              description: "",
                              conditions: [{ field: "organization", operator: "equals", value: "" }],
                              assign_to: "",
                              priority: 50,
                              enabled: true
                            });
                            setEditRuleDialogOpen(true);
                          }}
                          className="bg-[#6B5600] hover:bg-[#FFF7D1] text-[#1A1A1A]"
                          data-testid="add-rule-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Rule
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {loadingRules ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-[#6B5600]" />
                        </div>
                      ) : assignmentRules.length === 0 ? (
                        <div className="text-center py-8">
                          <Route className="w-12 h-12 mx-auto mb-4 text-[#C9C3B6]" />
                          <p className="text-[#59554D]">No assignment rules configured</p>
                          <p className="text-sm text-[#8A857A] mt-1">Add rules to automatically route tickets to the right agents</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {assignmentRules.map((rule) => {
                            const assignedAgent = agents.find(a => a.id === rule.assign_to) || 
                              { name: rule.assign_to };
                            return (
                              <div
                                key={rule.rule_id}
                                className={`p-4 rounded-lg border ${
                                  rule.enabled 
                                    ? 'bg-[#F4F2ED] border-[#DAD5CA]' 
                                    : 'bg-[#F4F2ED] border-[#DAD5CA] opacity-60'
                                }`}
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-medium text-[#1A1A1A]">{rule.name}</h4>
                                    <Badge className={rule.enabled ? "bg-[#E3F3E9] text-[#0E6B3F]" : "bg-[#C9C3B6] text-[#8A857A]"}>
                                      {rule.enabled ? "Active" : "Disabled"}
                                    </Badge>
                                    <Badge className="bg-[#DAD5CA] text-[#59554D]">
                                      Priority: {rule.priority}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setEditingRule(rule);
                                        setEditRuleDialogOpen(true);
                                      }}
                                      className="text-[#59554D] hover:text-[#1A1A1A] h-8 px-2"
                                      data-testid={`edit-rule-${rule.rule_id}`}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteRule(rule.rule_id)}
                                      className="text-[#C23A00] hover:text-[#C23A00] hover:bg-[#FFE9E0] h-8 px-2"
                                      data-testid={`delete-rule-${rule.rule_id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                                {rule.description && (
                                  <p className="text-sm text-[#59554D] mb-2">{rule.description}</p>
                                )}
                                <div className="flex flex-wrap gap-2 mb-2">
                                  {(rule.conditions || []).map((cond, idx) => (
                                    <Badge key={idx} className="bg-[#DAD5CA] text-[#59554D]">
                                      {cond.field} {cond.operator} &quot;{cond.value}&quot;
                                    </Badge>
                                  ))}
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="text-[#8A857A]">Assigns to:</span>
                                  <span className="text-[#6B5600] font-medium">{assignedAgent.name || rule.assign_to}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Round-Robin Info */}
                  <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                    <CardHeader>
                      <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#8A6D00]" />
                        Round-Robin Fallback
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-[#59554D] mb-4">
                        When no rules match, tickets are assigned using weighted round-robin based on agent support dedication %.
                        Configure agent weights inline above, or in the Agent Mapping tab.
                      </p>
                      <div className="flex gap-2">
                        {agents.filter(a => a.active && !["anthony", "emergency", "support_queue"].includes(a.id)).map(agent => (
                          <Badge key={agent.id} className="bg-[#DAD5CA] text-[#1A1A1A]">
                            {agent.name} ({Math.round((agent.support_weight || 1) * 100)}%)
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Rule Edit/Create Dialog */}
                  <Dialog open={editRuleDialogOpen} onOpenChange={setEditRuleDialogOpen}>
                    <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-xl">
                          {editingRule ? "Edit Assignment Rule" : "Create Assignment Rule"}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-[#59554D]">Rule ID</Label>
                            <Input
                              value={editingRule?.rule_id || newRule.rule_id}
                              onChange={(e) => editingRule 
                                ? setEditingRule({...editingRule, rule_id: e.target.value})
                                : setNewRule({...newRule, rule_id: e.target.value})
                              }
                              disabled={!!editingRule}
                              placeholder="e.g., borger_kronos"
                              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#59554D]">Name</Label>
                            <Input
                              value={editingRule?.name || newRule.name}
                              onChange={(e) => editingRule 
                                ? setEditingRule({...editingRule, name: e.target.value})
                                : setNewRule({...newRule, name: e.target.value})
                              }
                              placeholder="e.g., Borger Cranes - Kronos"
                              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-[#59554D]">Description (optional)</Label>
                          <Input
                            value={editingRule?.description || newRule.description}
                            onChange={(e) => editingRule 
                              ? setEditingRule({...editingRule, description: e.target.value})
                              : setNewRule({...newRule, description: e.target.value})
                            }
                            placeholder="Brief description of what this rule does"
                            className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-[#59554D]">Conditions (all must match)</Label>
                          <div className="space-y-2">
                            {(editingRule?.conditions || newRule.conditions).map((cond, idx) => (
                              <div key={idx} className="flex gap-2 items-center">
                                <Select
                                  value={cond.field}
                                  onValueChange={(val) => {
                                    const conditions = [...(editingRule?.conditions || newRule.conditions)];
                                    conditions[idx] = {...conditions[idx], field: val};
                                    editingRule 
                                      ? setEditingRule({...editingRule, conditions})
                                      : setNewRule({...newRule, conditions});
                                  }}
                                >
                                  <SelectTrigger className="w-[150px] bg-[#F4F2ED] border-[#DAD5CA]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                                    <SelectItem value="organization">Organization</SelectItem>
                                    <SelectItem value="keywords">Keywords</SelectItem>
                                    <SelectItem value="priority">Priority</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Select
                                  value={cond.operator}
                                  onValueChange={(val) => {
                                    const conditions = [...(editingRule?.conditions || newRule.conditions)];
                                    conditions[idx] = {...conditions[idx], operator: val};
                                    editingRule 
                                      ? setEditingRule({...editingRule, conditions})
                                      : setNewRule({...newRule, conditions});
                                  }}
                                >
                                  <SelectTrigger className="w-[130px] bg-[#F4F2ED] border-[#DAD5CA]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                                    <SelectItem value="equals">equals</SelectItem>
                                    <SelectItem value="contains">contains</SelectItem>
                                    <SelectItem value="in">in (comma-separated)</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Input
                                  value={cond.value}
                                  onChange={(e) => {
                                    const conditions = [...(editingRule?.conditions || newRule.conditions)];
                                    conditions[idx] = {...conditions[idx], value: e.target.value};
                                    editingRule 
                                      ? setEditingRule({...editingRule, conditions})
                                      : setNewRule({...newRule, conditions});
                                  }}
                                  placeholder={cond.field === "keywords" ? "KRONOS,INVOICE" : "BORGERCRANES"}
                                  className="flex-1 bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                                />
                                {(editingRule?.conditions || newRule.conditions).length > 1 && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      const conditions = [...(editingRule?.conditions || newRule.conditions)];
                                      conditions.splice(idx, 1);
                                      editingRule 
                                        ? setEditingRule({...editingRule, conditions})
                                        : setNewRule({...newRule, conditions});
                                    }}
                                    className="text-[#C23A00] hover:bg-[#FFE9E0] h-8 px-2"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const conditions = [...(editingRule?.conditions || newRule.conditions)];
                                conditions.push({ field: "keywords", operator: "contains", value: "" });
                                editingRule 
                                  ? setEditingRule({...editingRule, conditions})
                                  : setNewRule({...newRule, conditions});
                              }}
                              className="border-[#DAD5CA] text-[#59554D] hover:text-[#1A1A1A]"
                            >
                              <Plus className="w-4 h-4 mr-1" /> Add Condition
                            </Button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-[#59554D]">Assign To</Label>
                            <Select
                              value={editingRule?.assign_to || newRule.assign_to}
                              onValueChange={(val) => editingRule 
                                ? setEditingRule({...editingRule, assign_to: val})
                                : setNewRule({...newRule, assign_to: val})
                              }
                            >
                              <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA]">
                                <SelectValue placeholder="Select agent" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                                {agents.filter(a => a.active).map(agent => (
                                  <SelectItem key={agent.id} value={agent.id}>
                                    {agent.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-[#59554D]">Priority (higher = evaluated first)</Label>
                            <Input
                              type="number"
                              value={editingRule?.priority || newRule.priority}
                              onChange={(e) => editingRule 
                                ? setEditingRule({...editingRule, priority: parseInt(e.target.value)})
                                : setNewRule({...newRule, priority: parseInt(e.target.value)})
                              }
                              className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editingRule?.enabled ?? newRule.enabled}
                            onCheckedChange={(checked) => editingRule 
                              ? setEditingRule({...editingRule, enabled: checked})
                              : setNewRule({...newRule, enabled: checked})
                            }
                          />
                          <Label className="text-[#1A1A1A]">Rule Enabled</Label>
                        </div>
                      </div>
                      <DialogFooter className="mt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditRuleDialogOpen(false);
                            setEditingRule(null);
                          }}
                          className="border-[#DAD5CA] text-[#1A1A1A]"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={editingRule ? handleUpdateRule : handleCreateRule}
                          className="bg-[#6B5600] hover:bg-[#FFF7D1] text-[#1A1A1A]"
                        >
                          {editingRule ? "Update Rule" : "Create Rule"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </>
              )}

              {/* RULES TAB */}
              {settingsTab === "rules" && (
                <>
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-lg text-[#1A1A1A]">Alert Rules</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#59554D]">Idle Warning (hours)</Label>
                      <Input
                        type="number"
                        value={rules?.idle_warning_hours || 4}
                        onChange={(e) => setRules({ ...rules, idle_warning_hours: parseInt(e.target.value) })}
                        className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        data-testid="idle-warning-input"
                      />
                      <p className="text-xs text-[#59554D]">Alert when ticket is idle for this many hours</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#59554D]">Idle Escalation (hours)</Label>
                      <Input
                        type="number"
                        value={rules?.idle_escalation_hours || 24}
                        onChange={(e) => setRules({ ...rules, idle_escalation_hours: parseInt(e.target.value) })}
                        className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        data-testid="idle-escalation-input"
                      />
                      <p className="text-xs text-[#59554D]">Escalate when idle beyond this threshold</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#59554D]">Staff Response (hours)</Label>
                      <Input
                        type="number"
                        value={rules?.staff_response_hours || 4}
                        onChange={(e) => setRules({ ...rules, staff_response_hours: parseInt(e.target.value) })}
                        className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        data-testid="staff-response-input"
                      />
                      <p className="text-xs text-[#59554D]">Alert if no staff response after customer comment</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#59554D]">SLA Warning (hours)</Label>
                      <Input
                        type="number"
                        value={rules?.sla_warning_hours || 1}
                        onChange={(e) => setRules({ ...rules, sla_warning_hours: parseInt(e.target.value) })}
                        className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        data-testid="sla-warning-input"
                      />
                      <p className="text-xs text-[#59554D]">Warn when due date is within this many hours</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quality Validation Rules */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-[#0E6B3F]" />
                    Quality Validation Rules
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-[#59554D]">
                    Configure what quality checks are performed on tickets before resolution.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-[#F4F2ED] rounded-lg">
                      <div>
                        <p className="text-sm text-[#1A1A1A]">Require Due Date</p>
                        <p className="text-xs text-[#59554D]">Flag tickets missing due dates</p>
                      </div>
                      <Switch
                        checked={rules?.require_due_date ?? true}
                        onCheckedChange={(checked) => setRules({ ...rules, require_due_date: checked })}
                        data-testid="require-due-date-switch"
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-[#F4F2ED] rounded-lg">
                      <div>
                        <p className="text-sm text-[#1A1A1A]">Require Priority</p>
                        <p className="text-xs text-[#59554D]">Flag tickets without priority set</p>
                      </div>
                      <Switch
                        checked={rules?.require_priority ?? true}
                        onCheckedChange={(checked) => setRules({ ...rules, require_priority: checked })}
                        data-testid="require-priority-switch"
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-[#F4F2ED] rounded-lg">
                      <div>
                        <p className="text-sm text-[#1A1A1A]">Require Description</p>
                        <p className="text-xs text-[#59554D]">Flag tickets with insufficient description</p>
                      </div>
                      <Switch
                        checked={rules?.require_description ?? true}
                        onCheckedChange={(checked) => setRules({ ...rules, require_description: checked })}
                        data-testid="require-description-switch"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveRules}
                  className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]"
                  data-testid="save-rules-btn"
                >
                  Save Rules
                </Button>
              </div>
                </>
              )}

              {/* AGENT MAPPING TAB */}
              {settingsTab === "agents" && (
                <AgentMappingCard />
              )}

              {/* WORK HOURS TAB */}
              {settingsTab === "workhours" && (
                <AgentWorkHoursConfig />
              )}

              {/* INTEGRATIONS TAB */}
              {settingsTab === "integrations" && (
                <>
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-lg text-[#1A1A1A]">Teams Integration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-[#59554D]">
                    Teams webhook is configured. Critical alerts and daily summaries will be sent automatically.
                  </p>
                  <Button
                    onClick={handleTestTeams}
                    variant="outline"
                    className="border-[#DAD5CA] bg-transparent hover:bg-[#F4F2ED] text-[#1A1A1A]"
                    data-testid="test-teams-btn"
                  >
                    Send Test Notification
                  </Button>
                </CardContent>
              </Card>

              {/* Global Elevated SQL Credentials for Database Health Checks */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                    <Database className="w-5 h-5 text-[#8A6D00]" />
                    Global Elevated SQL Account
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-[#59554D]">
                    Configure a SQL Server account with elevated permissions (e.g., SA or sysadmin) to be used for database health checks. 
                    This enables the collection of detailed metrics like DBCC CHECKDB status, failed jobs, and index fragmentation.
                  </p>
                  
                  {elevatedCredentials.has_elevated_credentials && (
                    <div className="p-3 bg-[#E3F3E9] border border-[#E3F3E9] rounded-lg flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#0E6B3F]" />
                      <span className="text-sm text-[#0E6B3F]">
                        Elevated credentials configured (Username: {elevatedCredentials.elevated_sql_username})
                      </span>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#59554D]">SQL Username</Label>
                      <Input
                        placeholder="e.g., sa"
                        value={elevatedCredentials.elevated_sql_username || ""}
                        onChange={(e) => setElevatedCredentials({...elevatedCredentials, elevated_sql_username: e.target.value})}
                        className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        data-testid="elevated-sql-username-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#59554D]">SQL Password</Label>
                      <Input
                        type="password"
                        placeholder={elevatedCredentials.has_elevated_credentials ? "••••••••" : "Enter password"}
                        value={elevatedPassword}
                        onChange={(e) => setElevatedPassword(e.target.value)}
                        className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        data-testid="elevated-sql-password-input"
                      />
                      {elevatedCredentials.has_elevated_credentials && (
                        <p className="text-xs text-[#59554D]">Leave blank to keep existing password</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2">
                    <div>
                      <Label className="text-[#1A1A1A]">Use for Database Health Checks</Label>
                      <p className="text-xs text-[#59554D]">Enable to use these credentials for all database health monitoring</p>
                    </div>
                    <Switch
                      checked={elevatedCredentials.use_elevated_for_health_checks}
                      onCheckedChange={(checked) => setElevatedCredentials({...elevatedCredentials, use_elevated_for_health_checks: checked})}
                      data-testid="use-elevated-for-health-switch"
                    />
                  </div>
                  
                  <Separator className="bg-[#DAD5CA]" />
                  
                  <div className="flex gap-2 justify-end">
                    {elevatedCredentials.has_elevated_credentials && (
                      <Button
                        variant="outline"
                        onClick={clearElevatedCredentials}
                        className="border-[#C23A00] text-[#C23A00] hover:bg-[#FFE9E0]"
                        data-testid="clear-elevated-credentials-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Clear Credentials
                      </Button>
                    )}
                    <Button
                      onClick={saveElevatedCredentials}
                      disabled={savingCredentials || !elevatedCredentials.elevated_sql_username}
                      className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]"
                      data-testid="save-elevated-credentials-btn"
                    >
                      {savingCredentials ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Save Credentials"
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
                </>
              )}

              {/* REPORTS TAB */}
              {settingsTab === "reports" && (
                <>
              {/* Weekly Reports Settings */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#1D4ED8]" />
                    Weekly Reports
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-[#59554D]">
                    PDF reports are automatically sent every Monday at 7 AM to the managers below.
                    Each report leads with the team's workload &amp; queue health. Add or remove recipients here.
                  </p>
                  
                  <div className="space-y-3">
                    <Label className="text-[#59554D]">Manager Email Recipients</Label>
                    <div className="space-y-2" data-testid="report-recipients-list">
                      {reportRecipients.length === 0 ? (
                        <p className="text-xs text-[#8A857A]">No recipients yet — add one below.</p>
                      ) : (
                        reportRecipients.map((email) => (
                          <div key={email} className="flex items-center justify-between bg-[#F4F2ED] border border-[#DAD5CA] rounded-lg px-3 py-2" data-testid={`recipient-${email}`}>
                            <span className="text-sm text-[#1A1A1A] flex items-center gap-2">
                              <Mail className="w-4 h-4 text-[#1D4ED8]" />
                              {email}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeRecipient(email)}
                              disabled={savingReportSettings}
                              className="h-7 w-7 p-0 text-[#8A857A] hover:text-[#C23A00] hover:bg-[#FFE9E0]"
                              title="Remove recipient"
                              data-testid={`remove-recipient-${email}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="email"
                        placeholder="add.manager@equipr.software"
                        value={newRecipient}
                        onChange={(e) => setNewRecipient(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                        className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        data-testid="report-emails-input"
                      />
                      <Button
                        onClick={addRecipient}
                        disabled={savingReportSettings || !newRecipient.trim()}
                        className="bg-[#1D4ED8] hover:bg-[#EFF6FF] text-[#1A1A1A] shrink-0"
                        data-testid="add-recipient-btn"
                      >
                        {savingReportSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        <span className="ml-1.5">Add</span>
                      </Button>
                    </div>
                    <p className="text-xs text-[#8A857A]">Recipients are saved automatically and persist across restarts.</p>

                    <div className="pt-2 mt-1 border-t border-[#DAD5CA] space-y-2">
                      <Label className="text-[#59554D]">SLA Clients (fallback allowlist)</Label>
                      <p className="text-xs text-[#8A857A]">Optional. Only used when the Non-SLA list below is empty. When set, only these clients count as SLA and everyone else is Non-SLA. Match the client/organisation name as it appears in Jira.</p>
                      <div className="space-y-2" data-testid="sla-clients-list">
                        {slaClients.length === 0 ? (
                          <p className="text-xs text-[#8A857A]">No SLA clients configured yet — the report shows all clients as Non-SLA until you add some.</p>
                        ) : (
                          slaClients.map((name) => (
                            <div key={name} className="flex items-center justify-between bg-[#F4F2ED] border border-[#DAD5CA] rounded-lg px-3 py-2" data-testid={`sla-client-${name}`}>
                              <span className="text-sm text-[#1A1A1A] flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-[#0E6B3F]" />
                                {name}
                              </span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeSlaClient(name)}
                                disabled={savingReportSettings}
                                className="h-7 w-7 p-0 text-[#8A857A] hover:text-[#C23A00] hover:bg-[#FFE9E0]"
                                title="Remove SLA client"
                                data-testid={`remove-sla-client-${name}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          placeholder="e.g. Borger Cranes"
                          value={newSlaClient}
                          onChange={(e) => setNewSlaClient(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSlaClient(); } }}
                          className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                          data-testid="sla-client-input"
                        />
                        <Button
                          onClick={addSlaClient}
                          disabled={savingReportSettings || !newSlaClient.trim()}
                          className="bg-[#0E6B3F] hover:bg-[#E3F3E9] text-black shrink-0"
                          data-testid="add-sla-client-btn"
                        >
                          {savingReportSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                          <span className="ml-1.5">Add</span>
                        </Button>
                      </div>
                    </div>

                    <div className="pt-2 mt-1 border-t border-[#DAD5CA] space-y-2">
                      <Label className="text-[#59554D]">Non-SLA Clients</Label>
                      <p className="text-xs text-[#8A857A]">Clients <span className="font-semibold text-[#1A1A1A]">not</span> on an SLA. When this list has entries, everyone <span className="font-semibold text-[#1A1A1A]">else</span> is treated as an SLA client in the weekly report's "SLA Summary" split. Names are matched loosely against Jira org names (legal suffixes like "Pty Ltd" are ignored).</p>
                      <div className="space-y-2" data-testid="non-sla-clients-list">
                        {nonSlaClients.length === 0 ? (
                          <p className="text-xs text-[#8A857A]">No Non-SLA clients configured — the report falls back to the SLA-clients allowlist above.</p>
                        ) : (
                          nonSlaClients.map((name) => (
                            <div key={name} className="flex items-center justify-between bg-[#F4F2ED] border border-[#DAD5CA] rounded-lg px-3 py-2" data-testid={`non-sla-client-${name}`}>
                              <span className="text-sm text-[#1A1A1A] flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-[#6B5600]" />
                                {name}
                              </span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeNonSlaClient(name)}
                                disabled={savingReportSettings}
                                className="h-7 w-7 p-0 text-[#8A857A] hover:text-[#C23A00] hover:bg-[#FFE9E0]"
                                title="Remove Non-SLA client"
                                data-testid={`remove-non-sla-client-${name}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          placeholder="e.g. GWT Earthmoving"
                          value={newNonSlaClient}
                          onChange={(e) => setNewNonSlaClient(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNonSlaClient(); } }}
                          className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                          data-testid="non-sla-client-input"
                        />
                        <Button
                          onClick={addNonSlaClient}
                          disabled={savingReportSettings || !newNonSlaClient.trim()}
                          className="bg-[#6B5600] hover:bg-[#FFF7D1] text-black shrink-0"
                          data-testid="add-non-sla-client-btn"
                        >
                          {savingReportSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                          <span className="ml-1.5">Add</span>
                        </Button>
                      </div>
                    </div>
                    <Button
                      onClick={sendTestReport}
                      disabled={sendingTestReport || reportRecipients.length === 0}
                      className="w-full bg-[#0E6B3F] hover:bg-[#E3F3E9] text-black font-medium"
                      data-testid="send-test-report-btn"
                    >
                      {sendingTestReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send weekly report now (to {reportRecipients.length} recipient{reportRecipients.length === 1 ? '' : 's'})
                    </Button>
                  </div>

                  <Separator className="bg-[#DAD5CA]" />
                  
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await axios.post(`${API}/reports/manager-weekly`, 
                            { send_email: false }, 
                            { responseType: 'blob' }
                          );
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `Manager_Weekly_Report.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          toast.success("Manager report downloaded!");
                        } catch (error) {
                          toast.error("Failed to generate report");
                        }
                      }}
                      className="border-[#1D4ED8] text-[#1D4ED8] hover:bg-[#EFF6FF]"
                      data-testid="download-manager-report-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Manager Report
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        const agentName = prompt("Enter agent name for recap:", "Simon Brennan");
                        if (!agentName) return;
                        try {
                          const response = await axios.post(`${API}/reports/agent-recap`, 
                            { agent_name: agentName, send_email: false }, 
                            { responseType: 'blob' }
                          );
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `Agent_Recap_${agentName.replace(' ', '_')}.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          toast.success(`Agent recap for ${agentName} downloaded!`);
                        } catch (error) {
                          toast.error("Failed to generate report");
                        }
                      }}
                      className="border-[#0E6B3F] text-[#0E6B3F] hover:bg-[#E3F3E9]"
                      data-testid="download-agent-recap-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Agent Recap
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await axios.post(`${API}/reports/monthly-summary`, 
                            { send_email: false }, 
                            { responseType: 'blob' }
                          );
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `Monthly_Summary_Report.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          toast.success("Monthly summary downloaded!");
                        } catch (error) {
                          toast.error("Failed to generate report");
                        }
                      }}
                      className="border-[#6B5600] text-[#6B5600] hover:bg-[#FFF7D1]"
                      data-testid="download-monthly-report-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Monthly Summary
                    </Button>
                  </div>
                  
                  <Separator className="bg-[#DAD5CA]" />

                  {/* Development Team Report (A365 backlog) */}
                  <div className="rounded-lg border border-[#FFF8D6] bg-[#FFF8D6] p-4" data-testid="dev-team-report-section">
                    <div className="flex items-center gap-2 mb-1">
                      <Code className="w-4 h-4 text-[#8A6D00]" />
                      <h4 className="text-sm font-semibold text-[#1A1A1A]">Development Team Report</h4>
                    </div>
                    <p className="text-xs text-[#59554D] mb-3">
                      Analyses all open Jira <span className="font-mono text-[#8A6D00]">A365</span> tickets — backlog by status, type, priority, age,
                      release-ready queue, and breakdown by customer &amp; industry. Generation takes a few seconds.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={devReportLoading}
                        onClick={async () => {
                          setDevReportLoading(true);
                          try {
                            const response = await axios.post(`${API}/reports/dev-team`,
                              { send_email: false },
                              { responseType: 'blob', timeout: 120000 }
                            );
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `Dev_Team_Report.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            toast.success("Development Team Report downloaded!");
                          } catch (error) {
                            toast.error("Failed to generate Dev Team Report");
                          } finally {
                            setDevReportLoading(false);
                          }
                        }}
                        className="border-[#8A6D00] text-[#8A6D00] hover:bg-[#FFF8D6]"
                        data-testid="download-dev-report-btn"
                      >
                        {devReportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Download Dev Team Report
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={devReportSending}
                        onClick={async () => {
                          setDevReportSending(true);
                          try {
                            const res = await axios.post(`${API}/reports/dev-team`,
                              { send_email: true },
                              { timeout: 120000 }
                            );
                            const sent = (res.data?.results || []).filter(r => r.status === 'success').length;
                            if (sent > 0) toast.success(`Dev Team Report emailed to kevin.russell@equipr.software`);
                            else toast.error("Email not sent — check Resend configuration.");
                          } catch (error) {
                            toast.error("Failed to send Dev Team Report");
                          } finally {
                            setDevReportSending(false);
                          }
                        }}
                        className="border-[#0E6B3F] text-[#0E6B3F] hover:bg-[#E3F3E9]"
                        data-testid="send-dev-report-btn"
                      >
                        {devReportSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        Email to kevin.russell@equipr.software
                      </Button>
                    </div>
                  </div>

                  <p className="text-xs text-[#8A857A]">
                    Note: Email delivery requires Resend API key to be configured in the backend.
                  </p>
                </CardContent>
              </Card>
                </>
              )}

            </TabsContent>

            <TabsContent value="cs_portal" className="space-y-6" data-testid="cs-portal-content">
                <>
              {/* Client Management Settings */}
              <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg text-[#1A1A1A] flex items-center gap-2">
                      <Building className="w-5 h-5 text-[#8A6D00]" />
                      Client Management
                    </CardTitle>
                    <p className="text-sm text-[#59554D] mt-1">
                      Manage clients who can access the <a href="/client" target="_blank" className="text-[#8A6D00] hover:underline">Client Portal</a>
                    </p>
                  </div>
                  <Button
                    onClick={() => setAddClientDialogOpen(true)}
                    className="bg-[#0E6B3F] hover:bg-[#E3F3E9] text-[#1A1A1A]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Client
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingClients ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#8A6D00]" />
                    </div>
                  ) : clients.length === 0 ? (
                    <div className="text-center py-8">
                      <Building className="w-12 h-12 text-[#59554D] mx-auto mb-4" />
                      <p className="text-[#59554D]">No clients configured yet</p>
                      <p className="text-sm text-[#8A857A]">Add your first client to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {clients.map((client) => (
                        <div key={client.client_id} className={`bg-[#F4F2ED] rounded-lg p-4 border ${client.is_vip ? 'border-[#6B5600]' : 'border-[#DAD5CA]'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className={`w-10 h-10 rounded-full ${client.is_vip ? 'bg-[#FFF7D1]' : 'bg-[#FFF8D6]'} flex items-center justify-center`}>
                                  {client.is_vip ? (
                                    <Crown className="w-5 h-5 text-[#6B5600]" />
                                  ) : (
                                    <Building className="w-5 h-5 text-[#8A6D00]" />
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-[#1A1A1A] font-medium">{client.company_name}</h4>
                                    {client.is_vip && (
                                      <Badge className="bg-[#FFF7D1] text-[#6B5600] text-xs">
                                        <Crown className="w-3 h-3 mr-1" />
                                        VIP
                                      </Badge>
                                    )}
                                    {client.has_sla && (
                                      <Badge className="bg-[#E3F3E9] text-[#0E6B3F] text-xs">
                                        <FileCheck className="w-3 h-3 mr-1" />
                                        SLA
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-[#59554D]">ID: {client.client_id}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                <div>
                                  <span className="text-[#59554D]">Contact:</span>
                                  <p className="text-[#1A1A1A]">{client.contact_name || "Not set"}</p>
                                </div>
                                <div>
                                  <span className="text-[#59554D]">Email:</span>
                                  <p className="text-[#1A1A1A]">{client.email}</p>
                                </div>
                                <div>
                                  <span className="text-[#59554D]">Phone:</span>
                                  <p className="text-[#1A1A1A]">{client.phone || "Not set"}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                {client.jira_org_name && (
                                  <span className="text-xs bg-[#FFF8D6] px-2 py-1 rounded text-[#8A6D00]">
                                    JIRA: {client.jira_org_name}
                                  </span>
                                )}
                                {client.sla_notes && (
                                  <span className="text-xs bg-[#E3F3E9] px-2 py-1 rounded text-[#0E6B3F]">
                                    {client.sla_notes}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleGenerateQuickLogin(client)}
                                disabled={generatingQuickLogin}
                                className="border-[#8A6D00] text-[#8A6D00] hover:bg-[#FFF8D6]"
                                title="Generate Quick Login Link"
                              >
                                <Link2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingClient(client);
                                  setEditClientDialogOpen(true);
                                }}
                                className="border-[#DAD5CA] bg-transparent hover:bg-[#DAD5CA] text-[#1A1A1A]"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteClient(client.client_id)}
                                className="border-[#C23A00] text-[#C23A00] hover:bg-[#FFE9E0]"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Add Client Dialog */}
              <Dialog open={addClientDialogOpen} onOpenChange={setAddClientDialogOpen}>
                <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-xl text-[#1A1A1A] flex items-center gap-2">
                      <Plus className="w-5 h-5 text-[#0E6B3F]" />
                      Add New Client
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#59554D]">Client ID *</Label>
                        <Input
                          type="text"
                          placeholder="e.g., EAG123"
                          value={newClient.client_id}
                          onChange={(e) => setNewClient({...newClient, client_id: e.target.value.toUpperCase()})}
                          className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#59554D]">Company Name *</Label>
                        <Input
                          type="text"
                          placeholder="Company Name"
                          value={newClient.company_name}
                          onChange={(e) => setNewClient({...newClient, company_name: e.target.value})}
                          className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#59554D]">Email *</Label>
                        <Input
                          type="email"
                          placeholder="contact@company.com"
                          value={newClient.email}
                          onChange={(e) => setNewClient({...newClient, email: e.target.value.toLowerCase()})}
                          className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#59554D]">Contact Name</Label>
                        <Input
                          type="text"
                          placeholder="John Smith"
                          value={newClient.contact_name}
                          onChange={(e) => setNewClient({...newClient, contact_name: e.target.value})}
                          className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#59554D]">Phone</Label>
                        <Input
                          type="tel"
                          placeholder="+61 400 000 000"
                          value={newClient.phone}
                          onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                          className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#59554D]">JIRA Organization</Label>
                        <Select value={newClient.jira_org_name} onValueChange={(value) => setNewClient({...newClient, jira_org_name: value})}>
                          <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]">
                            <SelectValue placeholder="Select organization..." />
                          </SelectTrigger>
                          <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA] max-h-60">
                            {jiraOrganizations.map((org) => (
                              <SelectItem key={org} value={org} className="text-[#1A1A1A]">{org}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddClientDialogOpen(false)} className="border-[#DAD5CA] text-[#1A1A1A]">Cancel</Button>
                    <Button onClick={handleCreateClient} className="bg-[#0E6B3F] hover:bg-[#E3F3E9] text-[#1A1A1A]">
                      <Plus className="w-4 h-4 mr-2" />Create Client
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Edit Client Dialog */}
              <Dialog open={editClientDialogOpen} onOpenChange={setEditClientDialogOpen}>
                <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl text-[#1A1A1A] flex items-center gap-2">
                      <Edit className="w-5 h-5 text-[#8A6D00]" />
                      Edit Client: {editingClient?.client_id}
                    </DialogTitle>
                  </DialogHeader>
                  {editingClient && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-[#59554D]">Company Name</Label>
                        <Input type="text" value={editingClient.company_name} onChange={(e) => setEditingClient({...editingClient, company_name: e.target.value})} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#59554D]">Email</Label>
                          <Input type="email" value={editingClient.email} onChange={(e) => setEditingClient({...editingClient, email: e.target.value})} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#59554D]">Contact Name</Label>
                          <Input type="text" value={editingClient.contact_name || ""} onChange={(e) => setEditingClient({...editingClient, contact_name: e.target.value})} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-[#59554D]">Phone</Label>
                          <Input type="tel" value={editingClient.phone || ""} onChange={(e) => setEditingClient({...editingClient, phone: e.target.value})} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-[#59554D]">JIRA Organization</Label>
                          <Select value={editingClient.jira_org_name || ""} onValueChange={(value) => setEditingClient({...editingClient, jira_org_name: value})}>
                            <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]">
                              <SelectValue placeholder="Select organization..." />
                            </SelectTrigger>
                            <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA] max-h-60">
                              {jiraOrganizations.map((org) => (
                                <SelectItem key={org} value={org} className="text-[#1A1A1A]">{org}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="pt-4 border-t border-[#DAD5CA]">
                        <h4 className="text-sm font-medium text-[#6B5600] mb-3 flex items-center gap-2">
                          <Star className="w-4 h-4" /> Priority Settings
                        </h4>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between bg-[#F4F2ED] p-3 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#FFF7D1] flex items-center justify-center">
                                <Crown className="w-4 h-4 text-[#6B5600]" />
                              </div>
                              <div>
                                <p className="text-[#1A1A1A] text-sm font-medium">VIP Client</p>
                                <p className="text-xs text-[#8A857A]">Higher priority for all tickets</p>
                              </div>
                            </div>
                            <button onClick={() => setEditingClient({...editingClient, is_vip: !editingClient.is_vip})} className={`w-12 h-6 rounded-full transition-colors ${editingClient.is_vip ? 'bg-[#6B5600]' : 'bg-[#DAD5CA]'}`}>
                              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${editingClient.is_vip ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                          </div>
                          <div className="flex items-center justify-between bg-[#F4F2ED] p-3 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-[#E3F3E9] flex items-center justify-center">
                                <FileCheck className="w-4 h-4 text-[#0E6B3F]" />
                              </div>
                              <div>
                                <p className="text-[#1A1A1A] text-sm font-medium">Has SLA Contract</p>
                                <p className="text-xs text-[#8A857A]">Contractual service agreement</p>
                              </div>
                            </div>
                            <button onClick={() => setEditingClient({...editingClient, has_sla: !editingClient.has_sla})} className={`w-12 h-6 rounded-full transition-colors ${editingClient.has_sla ? 'bg-[#0E6B3F]' : 'bg-[#DAD5CA]'}`}>
                              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${editingClient.has_sla ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                          </div>
                          {editingClient.has_sla && (
                            <div className="space-y-2">
                              <Label className="text-[#59554D]">SLA Notes</Label>
                              <Input type="text" placeholder="e.g., 4hr response, 24hr resolution" value={editingClient.sla_notes || ""} onChange={(e) => setEditingClient({...editingClient, sla_notes: e.target.value})} className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <DialogFooter>
                    <Button variant="outline" onClick={() => { setEditClientDialogOpen(false); setEditingClient(null); }} className="border-[#DAD5CA] text-[#1A1A1A]">Cancel</Button>
                    <Button onClick={handleUpdateClient} className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]">Save Changes</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Quick Login Link Dialog */}
              <Dialog open={quickLoginDialogOpen} onOpenChange={setQuickLoginDialogOpen}>
                <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-xl text-[#1A1A1A] flex items-center gap-2">
                      <Link2 className="w-5 h-5 text-[#8A6D00]" />
                      Quick Login Link Generated
                    </DialogTitle>
                  </DialogHeader>
                  
                  {quickLoginData && (
                    <div className="space-y-4">
                      <div className="p-4 bg-[#FFFFFF] rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[#59554D] text-sm">Client</span>
                          <span className="text-[#1A1A1A] font-medium">{quickLoginData.company_name}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[#59554D] text-sm">Client ID</span>
                          <span className="text-[#1A1A1A] font-mono">{quickLoginData.client_id}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[#59554D] text-sm">Expires</span>
                          <span className="text-[#1A1A1A]">{formatDateTime(quickLoginData.expires_at)}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-[#59554D]">Shareable Link</Label>
                        <div className="flex gap-2">
                          <Input 
                            type="text" 
                            value={quickLoginData.url} 
                            readOnly 
                            className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] text-sm flex-1" 
                          />
                          <Button
                            onClick={copyQuickLoginLink}
                            className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-[#8A857A]">
                          This link pre-fills the client ID. The client will still need to enter their email and verify with OTP.
                        </p>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => window.open(quickLoginData.url, '_blank')}
                          className="flex-1 border-[#DAD5CA] text-[#1A1A1A] hover:bg-[#DAD5CA]"
                        >
                          <ExternalLinkIcon className="w-4 h-4 mr-2" />
                          Test Link
                        </Button>
                        <Button
                          onClick={copyQuickLoginLink}
                          className="flex-1 bg-[#0E6B3F] hover:bg-[#E3F3E9] text-[#1A1A1A]"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Link
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setQuickLoginDialogOpen(false)} className="border-[#DAD5CA] text-[#1A1A1A]">Close</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
                </>
            </TabsContent>
          </Tabs>
          )}
        </main>
      </div>

      {/* Call Recording Upload Dialog */}
      <Dialog open={callUploadDialogOpen} onOpenChange={setCallUploadDialogOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#1A1A1A] flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#8A6D00]" />
              Upload Call Recording
            </DialogTitle>
          </DialogHeader>
          
          {selectedCall && (
            <div className="space-y-4">
              {/* Call Details Summary */}
              <div className="p-4 bg-[#FFFFFF] rounded-lg space-y-2">
                <p className="text-sm text-[#59554D] font-medium">CALL DETAILS</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-[#59554D]">Time: </span>
                    <span className="text-[#1A1A1A]">{selectedCall.datetime?.split(' ')[1] || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-[#59554D]">Direction: </span>
                    <span className={selectedCall.direction === "IN" ? "text-[#0E6B3F]" : "text-[#8A6D00]"}>
                      {selectedCall.direction === "IN" ? "Inbound" : "Outbound"}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#59554D]">{selectedCall.direction === "IN" ? "From: " : "To: "}</span>
                    <span className="text-[#1A1A1A] font-mono">{selectedCall.direction === "IN" ? selectedCall.origin : selectedCall.destination}</span>
                  </div>
                  <div>
                    <span className="text-[#59554D]">Agent: </span>
                    <span className="text-[#1A1A1A]">{selectedCall.assigned_to || "Unknown"}</span>
                  </div>
                  <div>
                    <span className="text-[#59554D]">Duration: </span>
                    <span className="text-[#1A1A1A] font-mono">{selectedCall.duration_hr || `${selectedCall.duration || 0}s`}</span>
                  </div>
                  <div>
                    <span className="text-[#59554D]">Status: </span>
                    <span className="text-[#1A1A1A]">{selectedCall.status}</span>
                  </div>
                </div>
              </div>

              {/* File Upload Area */}
              <div className="border-2 border-dashed border-[#DAD5CA] rounded-lg p-8 text-center hover:border-[#FFF8D6] transition-colors">
                <input
                  type="file"
                  accept=".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm"
                  className="hidden"
                  id="call-recording-upload"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    
                    setUploadingCallRecording(true);
                    const formData = new FormData();
                    formData.append("file", file);
                    formData.append("agent_name", selectedCall.assigned_to || "");
                    formData.append("call_date", selectedCall.datetime?.split(' ')[0] || "");
                    formData.append("customer_name", selectedCall.direction === "IN" ? selectedCall.origin : selectedCall.destination);
                    formData.append("notes", `Call ID: ${selectedCall.id || 'N/A'}, Direction: ${selectedCall.direction}, Duration: ${selectedCall.duration_hr || selectedCall.duration + 's'}`);
                    formData.append("call_id", selectedCall.id || `${selectedCall.datetime}_${selectedCall.origin}_${selectedCall.destination}`);
                    
                    try {
                      await axios.post(`${API}/recordings/upload`, formData, {
                        headers: { "Content-Type": "multipart/form-data" }
                      });
                      toast.success("Recording uploaded! Transcription with speaker diarization in progress...");
                      setCallUploadDialogOpen(false);
                      setSelectedCall(null);
                      fetchDashboardData();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Failed to upload recording");
                    } finally {
                      setUploadingCallRecording(false);
                      e.target.value = "";
                    }
                  }}
                  disabled={uploadingCallRecording}
                  data-testid="call-recording-upload-input"
                />
                <label htmlFor="call-recording-upload" className="cursor-pointer">
                  {uploadingCallRecording ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-12 h-12 text-[#8A6D00] animate-spin" />
                      <p className="text-[#59554D]">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <FileAudio className="w-12 h-12 text-[#DAD5CA]" />
                      <div>
                        <p className="text-[#1A1A1A] font-medium">Click to select audio file</p>
                        <p className="text-sm text-[#59554D] mt-1">MP3, WAV, M4A, WEBM (max 25MB)</p>
                      </div>
                    </div>
                  )}
                </label>
              </div>

              {/* Speaker Diarization Note */}
              <div className="flex items-start gap-2 p-3 bg-[#FFF8D6] rounded-lg">
                <Mic className="w-4 h-4 text-[#8A6D00] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-[#59554D]">
                  <span className="text-[#8A6D00] font-medium">Speaker Diarization Enabled:</span> The transcription will automatically identify and label different speakers (e.g., &quot;Speaker A&quot;, &quot;Speaker B&quot;) in the conversation.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Recording Dialog */}
      <Dialog open={editRecordingDialogOpen} onOpenChange={setEditRecordingDialogOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1A1A1A] flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#8A6D00]" />
              {editingRecording?.agent_name ? "Edit Recording Details" : "Assign Agent to Recording"}
            </DialogTitle>
          </DialogHeader>
          {editingRecording && (
            <div className="space-y-4">
              {/* Recording Info */}
              <div className="p-3 bg-[#FFFFFF] rounded-lg">
                <p className="text-sm text-[#59554D]">Recording</p>
                <p className="text-[#1A1A1A] font-mono text-sm truncate">{editingRecording.original_filename || editingRecording.filename}</p>
                {editingRecording.call_id && (
                  <p className="text-xs text-[#59554D] mt-1">Call ID: {editingRecording.call_id}</p>
                )}
              </div>

              {/* Agent Selection */}
              <div className="space-y-2">
                <label className="text-sm text-[#59554D]">Support Agent *</label>
                <select
                  value={editingRecording.agent_name || ""}
                  onChange={(e) => setEditingRecording({...editingRecording, agent_name: e.target.value})}
                  className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#8A6D00]"
                  data-testid="select-agent"
                >
                  <option value="">-- Select Agent --</option>
                  <option value="Simon Brennan">Simon Brennan</option>
                  <option value="Kevin Carbe">Kevin Carbe</option>
                  <option value="Kevin Russell">Kevin Russell</option>
                  <option value="Jason Hooper">Jason Hooper</option>
                  <option value="Valentino Grosso">Valentino Grosso</option>
                  <option value="Anthony Roche">Anthony Roche</option>
                </select>
              </div>

              {/* Customer Name */}
              <div className="space-y-2">
                <label className="text-sm text-[#59554D]">Customer Name/Number</label>
                <input
                  type="text"
                  value={editingRecording.customer_name || ""}
                  onChange={(e) => setEditingRecording({...editingRecording, customer_name: e.target.value})}
                  placeholder="e.g., 0437764590 or John Smith"
                  className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A] placeholder-[#8A857A] focus:outline-none focus:ring-2 focus:ring-[#8A6D00]"
                  data-testid="input-customer"
                />
              </div>

              {/* Call Date */}
              <div className="space-y-2">
                <label className="text-sm text-[#59554D]">Call Date</label>
                <input
                  type="date"
                  value={editingRecording.call_date || ""}
                  onChange={(e) => setEditingRecording({...editingRecording, call_date: e.target.value})}
                  className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#8A6D00]"
                  data-testid="input-call-date"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <label className="text-sm text-[#59554D]">Notes</label>
                <textarea
                  value={editingRecording.notes || ""}
                  onChange={(e) => setEditingRecording({...editingRecording, notes: e.target.value})}
                  placeholder="Additional notes about this recording..."
                  rows={2}
                  className="w-full bg-[#FFFFFF] border border-[#DAD5CA] rounded-lg px-3 py-2 text-[#1A1A1A] placeholder-[#8A857A] focus:outline-none focus:ring-2 focus:ring-[#8A6D00] resize-none"
                  data-testid="input-notes"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditRecordingDialogOpen(false);
                    setEditingRecording(null);
                  }}
                  className="border-[#DAD5CA] text-[#59554D] hover:bg-[#DAD5CA]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!editingRecording.agent_name) {
                      toast.error("Please select an agent");
                      return;
                    }
                    setSavingRecording(true);
                    try {
                      const params = new URLSearchParams();
                      if (editingRecording.agent_name) params.append("agent_name", editingRecording.agent_name);
                      if (editingRecording.customer_name) params.append("customer_name", editingRecording.customer_name);
                      if (editingRecording.call_date) params.append("call_date", editingRecording.call_date);
                      if (editingRecording.notes !== undefined) params.append("notes", editingRecording.notes);
                      
                      await axios.patch(`${API}/recordings/${editingRecording.id}?${params.toString()}`);
                      toast.success("Recording updated successfully");
                      setEditRecordingDialogOpen(false);
                      setEditingRecording(null);
                      fetchDashboardData();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || "Failed to update recording");
                    } finally {
                      setSavingRecording(false);
                    }
                  }}
                  disabled={savingRecording || !editingRecording.agent_name}
                  className="bg-[#FFD108] hover:bg-[#FFD108] text-[#1A1A1A]"
                  data-testid="save-recording-btn"
                >
                  {savingRecording ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Auto-Assign Preview (Dry-Run) Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#1C1C1E] border-[#2C2C2E] text-[#1A1A1A] max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="auto-assign-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#6B5600]" />
              Auto-Assign Preview (Dry Run)
            </DialogTitle>
            <p className="text-sm text-[#59554D] mt-1">
              These are the assignments that would be made if you click &quot;Apply&quot;.
              No tickets have been updated in JIRA yet.
            </p>
          </DialogHeader>

          {previewLoading ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-[#6B5600]" />
            </div>
          ) : previewResults.length === 0 ? (
            <div className="text-center py-12 text-[#59554D]">
              No unassigned tickets to process.
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              <div className="text-sm text-[#59554D] mb-3">
                {previewResults.filter(r => r.status === "preview").length} ticket(s) would be assigned ·
                {" "}{previewResults.filter(r => r.status === "skipped").length} skipped
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2C2C2E]">
                    <TableHead className="text-[#59554D]">Ticket</TableHead>
                    <TableHead className="text-[#59554D]">Organization</TableHead>
                    <TableHead className="text-[#59554D]">Would Assign To</TableHead>
                    <TableHead className="text-[#59554D]">Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewResults.map((r) => (
                    <TableRow key={r.ticket} className="border-[#2C2C2E]" data-testid={`preview-row-${r.ticket}`}>
                      <TableCell className="font-mono text-[#1A1A1A]">{r.ticket}</TableCell>
                      <TableCell className="text-[#59554D]">{r.organization || "—"}</TableCell>
                      <TableCell className="text-[#1A1A1A]">
                        {r.assigned_to || <span className="text-[#59554D]">—</span>}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            r.status === "skipped"
                              ? "border-[#48484A] text-[#59554D]"
                              : r.reason?.startsWith("Rule:")
                              ? "border-[#0E6B3F] text-[#0E6B3F]"
                              : "border-[#6B5600] text-[#6B5600]"
                          }
                        >
                          {r.reason}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter className="border-t border-[#2C2C2E] pt-4">
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
              className="border-[#2C2C2E] text-[#1A1A1A] hover:bg-[#2C2C2E]"
              data-testid="preview-close-btn"
            >
              Close
            </Button>
            <Button
              onClick={handleApplyPreview}
              disabled={applyingPreview || previewLoading || previewResults.filter(r => r.status === "preview").length === 0}
              className="bg-[#0E6B3F] hover:bg-[#E3F3E9] text-[#1A1A1A]"
              data-testid="preview-apply-btn"
            >
              {applyingPreview ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Apply &amp; Assign in JIRA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
